import i18next, { type i18n } from 'i18next';
import { initReactI18next } from 'react-i18next';

/** The UI languages (docs/01 §6): English is the default, Estonian beside it. */
export const languages = [
  { code: 'en', name: 'English' },
  { code: 'et', name: 'Eesti' },
] as const;

export type Language = (typeof languages)[number]['code'];

export const defaultLanguage: Language = 'en';

const en = {
  brand: 'TripPath',
  skip: 'Skip to content',
  language: { label: 'Language' },
  status: {
    checking: 'Checking the server…',
    ok: 'Server OK · schema {{schema}}',
    down: 'Server not reachable',
  },
  empty: {
    title: 'No trips yet',
    tagline: 'Plan the route. Plan the time. Plan the money.',
    body: 'For long trips of three to six months: will the money last, and what if you stay longer in Japan? Your first trip starts here.',
    start: 'Plan a new trip',
    routeTitle: 'Route and map',
    routeBody:
      'Type HEL-BKK or a city, add stops by country and see the way drawn, with the usual stopovers.',
    moneyTitle: 'Trip against budget',
    moneyBody:
      'Min, expected and max for every stop: paid, booked and still estimated, and what each change saves.',
    roadTitle: 'On the road',
    roadBody:
      'What is left for today in local money, bills photographed, documents and entry rules at hand, offline too.',
  },
  pick: {
    placeholder: 'Choose…',
    noOptions: 'Nothing matches',
    count_one: '{{count}} option',
    count_other: '{{count}} options',
    focused: '{{label}}, press Enter to choose',
    chosen: '{{label}} chosen',
    guidance: 'Arrow keys move, Enter chooses, Escape closes',
    filtered: '{{results}} for “{{text}}”',
  },
  date: {
    placeholder: 'Pick a date',
    open: 'Open calendar',
    before: 'The day before',
    after: 'The day after',
  },
  range: {
    placeholder: 'Pick the dates',
    days_one: '{{count}} day',
    days_other: '{{count}} days',
    since: 'from {{day}}',
    until: 'to {{day}}',
    from: 'Start',
    to: 'End',
    typeHint: 'dd.mm.yyyy',
    clear: 'Clear',
  },
  dialog: {
    close: 'Close',
    cancel: 'Cancel',
  },
  field: {
    price: 'Price',
    range: 'Range',
    amount: 'Type an amount like 12.50',
    currency: '{{field}}: currency',
    number: 'Type a whole number',
    more: '{{label}}: more',
    less: '{{label}}: less',
    lowest: 'Lowest',
    highest: 'Highest',
  },
  problem: {
    check: 'Some fields need fixing: they are marked below.',
    required: 'Fill this in',
    too_long: 'Too long: 100 characters at most',
    format: 'Not in the expected form',
    range: 'Out of the allowed range',
    reversed: 'The lowest is above the highest',
    negative: 'Cannot be below zero',
    mismatch: 'The stops changed meanwhile; reload the trip',
    taken: 'Already used',
    no_rate: 'No exchange rate for this money: set one',
    gone: 'The trip is gone, or no longer yours to change',
    booked: 'A ticket is booked for this way: remove it to change how it goes',
    leaked: 'This password is in known lists of leaked passwords: choose another',
    archived: 'The trip is archived and read only',
  },
  trips: {
    menu: 'More for {{name}}',
    edit: 'Edit',
    title: 'Trips',
    new: 'New trip',
    days_one: '{{count}} day',
    days_other: '{{count}} days',
    daysShort: '{{count}}d',
    stops_one: '{{count}} stop',
    stops_other: '{{count}} stops',
    dates: '{{from}} – {{to}}',
    loading: 'Loading…',
    failed: 'Could not reach the server.',
    notFound: 'This trip does not exist.',
    back: 'All trips',
    copy: 'Make a copy…',
    copyHint: 'The same route on other dates',
    archive: 'Archive',
    archiveHint: 'Hides it from the list; nothing is deleted',
    restore: 'Unarchive',
    restoreHint: 'Back in the list, editable again',
    archived_one: 'Archived · {{count}}',
    archived_other: 'Archived · {{count}}',
    archivedOn: 'archived {{day}}',
    readOnly: 'read only',
    archivedNote: 'This trip is archived and read only.',
    readOnlyNote: 'Read only: nothing of this trip can be changed.',
    actionFailed: 'That did not work. Try again.',
  },
  live: {
    here: 'On the trip now: {{names}}',
    state: { live: 'Live', connecting: 'Connecting…', offline: 'Offline' },
    see: 'See',
    close: 'Close',
    undo: 'Undo',
    undoFailed: 'Could not undo it.',
    changedMeanwhile: '{{name}} changed this stop while you were editing it.',
    theirDays_one: '{{name}} made it {{count}} day.',
    theirDays_other: '{{name}} made it {{count}} days.',
    keepTheirs: 'Keep {{name}}’s',
    keepMine: 'Keep mine',
  },
  dayOrder: {
    ask: 'Order by the shortest way',
    title: 'Shortest order',
    pace: 'How you go',
    by: { walk: 'On foot', transit: 'Public transport', car: 'By car' },
    now: 'Now',
    new: 'New order',
    km: '{{km}} km',
    time: '{{h}} h {{m}} min',
    was: 'was {{n}}.',
    fixed: 'time fixed',
    already: 'The day is already in its shortest order.',
    note: 'About: the straight line a third longer. The room stays at the start and the end; a thing with a time keeps its place.',
    cancel: 'Cancel',
    apply: 'Apply order',
  },
  weather: {
    title: 'Weather',
    days: 'Day',
    loading: 'Loading the weather…',
    failed: 'The weather could not be loaded.',
    partly: 'Some places’ weather could not be had; try again later.',
    unknown: 'No weather is known for this day.',
    forecastTag: 'Forecast',
    wasTag: 'As it was',
    rainFell: 'Rain',
    usualTag: 'Average of {{from}}–{{to}}, not a forecast',
    rainChance: 'Chance of rain',
    rainyDays: 'Rainy days',
    highUsually: 'By day, usually',
    lowUsually: 'At night, usually',
    wind: 'Wind',
    sun: 'Sunrise and sunset',
    sunTimes: '{{from}}–{{to}} local time',
    mm: '{{mm}} mm',
    kmh: '{{kmh}} km/h',
    band: 'The day’s temperature over {{years}} years: {{from}} to {{to}}',
    bandLabel: 'Usually {{from}} to {{to}} by day',
    forecastNote: 'Forecast by Open-Meteo, updated every three hours.',
    wasNote: 'The weather as it was that day, from Open-Meteo’s archive.',
    usualNote:
      'An average, not a forecast. The forecast comes on {{day}}, {{days}} days ahead.',
    lane: 'Weather',
    partsTitle: 'Through the day',
    part: {
      night: 'Night',
      early: 'Early morning',
      morning: 'Morning',
      midday: 'Midday',
      afternoon: 'Afternoon',
      evening: 'Evening',
      late: 'Late evening',
    },
    partSaid: '{{part}} {{from}}–{{to}} · {{sky}} · {{temp}}',
    average: '{{from}}–{{to}} · {{sky}} · {{temp}} on average',
    onWay: 'Where the way is, {{from}}–{{to}} · {{sky}} · {{temp}}',
    clear: 'clear',
    sky: {
      sun: 'sunny',
      part: 'partly cloudy',
      cloud: 'cloudy',
      fog: 'fog',
      rain: 'rain',
      snow: 'snow',
      storm: 'thunder',
    },
  },
  tripCopy: {
    title: 'Make a copy: {{name}}',
    name: 'Name of the new trip',
    nameOf: '{{name}} (copy)',
    start: 'Starts',
    shift_one: 'Dates: {{sign}}{{count}} day · {{from}} – {{to}}',
    shift_other: 'Dates: {{sign}}{{count}} days · {{from}} – {{to}}',
    clocks: 'Times stay the same local time, each in its place’s time zone.',
    holds: 'What the copy holds',
    route: 'Route',
    routeHint: 'Stops, nights, ways, day plans and activities',
    prices: 'Prices',
    pricesHint: 'Ranges and estimates as they are now',
    bookings: 'Bookings as estimates',
    bookingsHint:
      'Each booking becomes an estimate at its price; no references, payments or files are copied',
    branches_one: 'Branch trips · {{count}}',
    branches_other: 'Branch trips · {{count}}',
    branchesHint: 'Moved by the same number of days',
    travellers: 'Fellow travellers',
    travellersHint:
      'Their days moved with the trip; shared costs, debts and documents always stay with the old trip',
    submit: 'Make a copy',
    cancel: 'Cancel',
  },
  tripForm: {
    newTitle: 'New trip',
    editTitle: 'Edit trip',
    name: 'Name',
    start: 'Start date',
    budget: 'Budget',
    currency: 'Currency',
    past: 'The trip has already happened (an earlier start date)',
    purpose: 'Trip type',
    purposeNone: 'Not chosen',
    create: 'Create trip',
    save: 'Save',
  },
  purpose: {
    holiday: 'Holiday',
    work: 'Business trip',
    study: 'Study trip',
    visit: 'Visiting family or friends',
    culture: 'Culture trip',
    adventure: 'Adventure trip',
    sport: 'Sports trip',
    hint: {
      holiday: 'Relaxing and sightseeing',
      work: 'Work tasks and meetings',
      study: 'Training or studying',
      visit: 'Seeing family or friends',
      culture: 'Museums, concerts and cultural events',
      adventure: 'Hiking and outdoor activities',
      sport: 'Competing in or watching sports events',
    },
  },
  today: {
    open: 'Today',
    back: 'Back to the trip',
    before: 'The day before',
    after: 'The day after',
    tripDay: 'day {{day}} of {{of}}',
    here: '{{city}} · night {{night}} of {{nights}}',
    ended: 'The trip is over',
    startsIn_one: 'The trip starts tomorrow',
    startsIn_other: 'The trip starts in {{count}} days',
    left: 'Left for today',
    spentOf: 'spent {{spent}} of {{allowance}}',
    underPlan: '{{amount}} under plan so far',
    overPlan: '{{amount}} over plan so far',
    next: 'Next',
    by: {
      flight: 'Flight to {{to}}',
      train: 'Train to {{to}}',
      bus: 'Bus to {{to}}',
      ferry: 'Ferry to {{to}}',
      car: 'Drive to {{to}}',
      foot: 'Walk to {{to}}',
      none: 'On to {{to}}',
    },
    inDays_zero: 'today',
    inDays_one: 'tomorrow',
    inDays_other: 'in {{count}} days',
    booked: 'booked · {{amount}}',
    notBooked: 'not booked · {{range}}',
    add: 'Add a spend',
    amount: 'Amount',
    ownRate: 'your rate',
    ecbRate: 'ECB, {{day}}',
    marketRate: 'market rate, {{day}}',
    noRate: 'No exchange rate for this money yet.',
    setRate: 'Set the rate',
    category: 'Category',
    categories: {
      food: 'Food',
      transport: 'Transport',
      stay: 'Stay',
      fun: 'Fun',
      other: 'Other',
    },
    note: 'Note',
    photo: 'Photo of the bill',
    photoAgain: 'Another photo',
    photoAttached: 'Bill photo attached.',
    save: 'Add',
    failed: 'Saving failed. Try again.',
    rateTitle: 'Rate for the {{currency}}',
    rateLabel: '{{currency}} per 1 {{trip}}',
    rateEcb: 'Use the ECB rate',
    rateSave: 'Save',
    rateWrong: 'Not a rate: type a number such as 30500.',
    spends: 'Spent this day',
    none: 'Nothing spent yet.',
    bill: 'Bill',
    remove: 'Remove {{what}}',
    offline: 'Offline: spends wait on this phone',
    waiting_one: '{{count}} spend waiting to send',
    waiting_other: '{{count}} spends waiting to send',
    stale: 'Offline: showing what was last seen, for {{day}}',
    notSent: 'waiting to send',
    refused: 'not kept: {{why}}',
    forget: 'Forget {{what}}',
  },
  countries: {
    title: 'Countries',
    expected: 'Expected',
    of: 'of {{budget}}',
    perDay: 'Cost per day by country',
    legendExpected: 'expected a day',
    legendRange: 'lowest to highest',
    legendDear: 'a stop over {{over}} a day',
    dearAt: 'Over {{over}} a day: {{stops}}',
    nights: 'Days',
    aDay: '€ / day',
    between: 'Flights & trains between countries',
    span: '{{range}} · {{days}} days',
    byCountry: 'By country',
    entryBooked: 'Entry · booked',
    open: 'Show {{country}}',
    close: 'Hide {{country}}',
    tiles: {
      countries: 'Countries',
      countriesSub: '{{days}} days · {{cities}} cities',
      entry: 'Entry',
      visas_one: '{{count}} visa to get',
      visas_other: '{{count}} visas to get',
      noVisas: 'No visa to get',
      visaNext: '{{country}} {{kind}} · apply by {{day}}',
      forms: 'Arrival forms',
      formsToFill: '{{count}} to fill',
      noForms: 'None to fill',
      formNext: 'next: {{form}}, {{range}}',
      noPassport: 'Set your passport',
      noPassportSub: 'in Docs, to know what each border asks',
      nights: 'Nights booked',
    },
    rule: {
      free: 'Visa-free {{days}} d',
      card: 'Arrival card {{days}} d',
      eta: 'Travel authorisation {{days}} d',
      evisa: 'E-visa',
      arrival: 'Visa on arrival',
      visa: 'Visa',
      by: '{{what}} by {{day}}',
    },
    ruleLong: {
      free: 'Visa-free, {{days}} days',
      card: 'With an arrival card, {{days}} days',
      eta: 'With a travel authorisation, {{days}} days',
      evisa: 'E-visa, {{days}} days',
      arrival: 'Visa on arrival, {{days}} days',
      visa: 'Visa, {{days}} days',
    },
    unknownRule: 'Not known',
    ownCountry: 'Your own country',
    inMoney: 'Amounts in',
    plugType: 'Type {{letter}}',
    freeMovement: 'Free movement (EU)',
    bookedOf: '{{booked}} / {{nights}} nights',
    entry: 'Entry and stay',
    passport: 'Passport · {{country}}',
    daysUsed: 'Days used',
    daysOf: '{{days}} of {{max}}',
    toFill: 'To fill',
    toApply: 'To apply',
    filled: 'Done',
    formWindow: '{{form}} · {{range}}',
    applyBy: '{{form}} · by {{day}}',
    official: 'Official page',
    setPassport: 'Set your passport in Docs',
    money: 'Money',
    currency: 'Currency',
    rate: 'Rate',
    notes: 'Banknotes',
    coins: 'Coins',
    seldom: 'Seldom used',
    noRate: 'No rate yet',
    stays: 'Stays',
    food: 'Food',
    local: 'Getting around',
    activities: 'Activities',
    paidToPay: 'Paid · to pay',
    nightsTitle: 'Stays',
    booked: 'Booked',
    notBooked: 'No bed booked',
    partly: '{{booked}} of {{nights}} nights booked',
    whileThere: 'Holidays while you are there',
    publicHoliday: 'public holiday',
    observance: 'observance',
    time: 'Time zone',
    homeAhead_one: '{{count}} hour ahead of home',
    homeAhead_other: '{{count}} hours ahead of home',
    homeBehind_one: '{{count}} hour behind home',
    homeBehind_other: '{{count}} hours behind home',
    plugs: 'Plugs and voltage',
    unknown: 'Not known',
    crossings: 'Border crossings',
    arrive: '→ {{country}}',
    need: {
      free: 'No visa or permit needed',
      card: '{{form}}, {{lead}} days before',
      eta: '{{form}}, {{lead}} days before',
      evisa: 'E-visa, apply {{lead}} days before',
      arrival: 'Visa on arrival',
      visa: 'Visa from an embassy, {{lead}} days before',
    },
    fill: 'Fill {{range}}',
    apply: 'Apply by {{day}}',
    done: 'Done',
    passportValid: 'Your passport: {{country}} · valid to {{day}}',
    passportLong: '(more than 6 months after you return)',
    passportShort: '(less than 6 months after you return — check what each border asks)',
    passportNone: 'Your passport: {{country}}',
    indicative: 'Indicative only — check official sources before travel.',
  },
  theme: { dark: 'Dark mode' },
  settings: {
    title: 'Trip settings',
    trip: 'The trip',
    danger: 'Delete the trip',
    ownerOnly: 'Only the owner of the trip can delete it.',
    archive: 'Archive',
    archiveHow:
      'The owner archives a trip from its menu on the trips list: it is then read only, nothing deleted.',
  },
  widget: {
    move: 'Move {{name}}',
    close: 'Close {{name}}',
    dock: 'Widgets',
  },
  ticket: {
    part: {
      ticket: 'Ticket',
      legs: 'Segments',
      flights: 'Flights',
      people: 'Passengers',
      rules: 'Baggage, check-in',
    },
    departure: 'Departure',
    arrival: 'Arrival',
    airport: 'Airport',
    placeBy: {
      flight: 'Airport',
      train: 'Station',
      bus: 'Stop',
      ferry: 'Port',
      car: 'Place',
      foot: 'Place',
      none: 'Station or stop',
    },
    setOff: 'Setting off',
    arrive: 'Arriving',
    date: 'Date',
    terminal: 'Terminal',
    actual: 'Actual time',
    airline: 'Airline',
    airlineHint: 'Type a name or code',
    aircraftHint: 'Type or code',
    clockHint: 'hh:mm',
    cabin: 'Cabin bag',
    pieces: 'Pieces',
    kg: 'Kilograms',
    size: 'Size, cm',
    personalItem: 'A personal item besides',
    checked: 'Checked baggage',
    bag: 'Bag {{number}}, kg',
    addBag: 'Add bag',
    removeBag: 'Remove bag {{number}}',
    lookup: 'Fill in from the flight number',
    bags: 'Bags',
    ticketBags: 'Same as the ticket',
    ownBags: 'Different',
    cabinKg: 'Cabin bag, kg',
    after: 'after {{time}}',
    boarding: 'Boarding',
    deplaning: 'Leaving the plane',
    apron: { bridge: 'Air bridge', stairs: 'Stairs', bus: 'Bus' },
    lookingUp: 'Looking up…',
    lookupNeeds: 'Needs the flight number and its day.',
    lookupFound: 'Filled in from {{service}}: check what it says.',
    lookupNone: 'No service knows this flight on this day.',
    lookupNoService: 'No flight data service is set up: see Data sources.',
    lookupFailed: 'The flight could not be looked up. Try again.',
    checkIn: 'Check-in',
    onlineOpens: 'Online opens, h before',
    onlineCloses: 'Online closes, min before',
    deskOpens: 'Desk opens, h before',
    deskCloses: 'Desk closes, min before',
    notes: 'Notes',
    leg: 'Segment {{number}}',
    flight: 'Flight {{number}}',
    time: 'Time',
    numberBy: {
      flight: 'Flight number',
      train: 'Train number',
      bus: 'Line number',
      ferry: 'Sailing number',
      car: 'Booking number',
      foot: 'Booking number',
      none: 'Number',
    },
    operatorBy: {
      flight: 'Operator',
      train: 'Operator',
      bus: 'Operator',
      ferry: 'Shipping line',
      car: 'Rental company',
      foot: 'Guide or company',
      none: 'Operator',
    },
    vehicleBy: {
      flight: 'Aircraft',
      train: 'Train type',
      bus: 'Bus model',
      ferry: 'Ship',
      car: 'Make and model',
      foot: 'Trail',
      none: 'Vehicle',
    },
    registrationBy: {
      flight: 'Registration',
      train: 'Registration',
      bus: 'Registration plate',
      ferry: 'IMO number',
      car: 'Registration plate',
      foot: 'Permit number',
      none: 'Registration',
    },
    gateBy: {
      flight: 'Gate',
      train: 'Platform',
      bus: 'Platform',
      ferry: 'Berth',
      car: 'Gate',
      foot: 'Start',
      none: 'Gate',
    },
    travelClass: 'Class',
    classes: {
      train: {
        first: '1st class',
        second: '2nd class',
        sleeper: 'Sleeper',
        couchette: 'Couchette',
      },
      ferry: {
        deck: 'Seat',
        inside: 'Inside cabin',
        outside: 'Sea-view cabin',
        suite: 'Suite',
      },
    },
    carried: 'Vehicle aboard',
    carriedNone: 'None',
    carriedKind: {
      car: 'Car',
      van: 'Van',
      motorcycle: 'Motorcycle',
      bicycle: 'Bicycle',
      camper: 'Camper',
    },
    carriedPlate: 'Its registration plate',
    removeLeg: 'Remove {{name}}',
    addLeg: 'Add a segment',
    addFlight: 'Add a flight',
    legsMenu: 'More on its legs',
    bagsMenu: 'More on its bags',
    people: 'People on the ticket',
    person: 'Person {{number}}',
    addPerson: '+ Person',
    name: 'Name',
    price: 'Price for this person',
    ticketNumber: 'Ticket number',
    share: 'Shared equally: {{amount}} each',
    seatBy: {
      flight: 'Seat {{leg}}',
      train: 'Seat {{leg}}',
      bus: 'Seat {{leg}}',
      ferry: 'Cabin or seat {{leg}}',
      car: 'Seat {{leg}}',
      foot: 'Place {{leg}}',
      none: 'Seat {{leg}}',
    },
    coach: 'Coach {{leg}}',
    removePerson: 'Take {{name}} off the ticket',
    nameHint: 'Choose or type a name',
    newPerson: '+ {{name}}, new on the ticket',
  },
  car: {
    title: 'Car',
    whose: 'Car',
    hired: 'Rental car',
    own: 'Own car',
    kind: 'Runs on',
    petrol: 'Petrol',
    electric: 'Electricity',
    use: 'Fuel use, l/100 km',
    useElectric: 'Uses, kWh/100 km',
    useWrong: '1 to 50 per 100 km, like 6.5',
    price: 'Price a litre in {{country}}',
    priceElectric: 'A kWh in {{country}}',
    save: 'Save',
  },
  proposal: {
    title: 'Variant',
    close: 'Close',
    fold: 'Fold the proposal',
    change: 'Change',
    total: 'Expected',
    was: 'now {{amount}}',
    length: 'Length',
    budget: 'Budget',
    within: 'within {{amount}}',
  },
  variants: {
    title: 'Variants',
    widget: 'Variants',
    none: 'No variants yet. Draft a route in the search and keep it as a variant to compare it here.',
    tree: 'The plan and its variants, as branches',
    branches: 'Variants from here',
    addLabel: 'A Google Flights link or a route to keep as a variant',
    addHint: 'Paste a Google Flights link, or type KUL-SIN-SYD',
    add: 'Add variant',
    unread: 'Not every place was found.',
    fix: 'Complete it in the search',
    precise: 'Compare flight tickets in detail',
    asIs: 'The plan as it is',
    skips: 'Skips {{cities}}',
    otherWay: 'Another way to {{city}}',
    instead: 'Instead of {{cities}}',
    goesOn: 'Goes on from {{city}}',
    elsewhere: 'From elsewhere, after the end of the trip',
    unfit: 'No longer fits the plan',
    days_one: '{{count}} day · to {{end}}',
    days_other: '{{count}} days · to {{end}}',
    over: '{{amount}} over budget',
    use: 'Use this',
    forget: 'Forget {{name}}',
    forgetShort: 'Forget',
    useQuestion: 'Put {{name}} into the plan?',
    useBody: 'Its places become stops at their typical prices.',
    useBodyDrops_one:
      'Its places become stops at their typical prices. Out of the plan: {{cities}}, with {{count}} booking.',
    useBodyDrops_other:
      'Its places become stops at their typical prices. Out of the plan: {{cities}}, with {{count}} bookings.',
    keep: 'Keep as a variant',
    kept: '{{name}} kept as a variant',
  },
  mapPick: {
    inPlan_one: 'Already in your plan',
    inPlan_other: 'In your plan {{count}} times',
    open: 'Open the stop',
    stopOf_one: 'Stop {{number}} of {{of}} · {{count}} night',
    stopOf_other: 'Stop {{number}} of {{of}} · {{count}} nights',
    wayHere: 'Draft a way here',
    wayHereFrom: 'From {{from}}: compare the ways, then add it',
    wayHereFirst: 'Start a route here: compare the ways, then add it',
    asStop: 'Add a stop here',
    again: 'Visit again',
    asStopDetail: 'A new stop: set its nights and costs',
    close: 'Close',
    perDay: 'Usually {{range}} a day',
  },
  track: {
    title: 'Track',
    from: 'From the file {{name}} ({{format}})',
    import: 'Import a track',
    importHint:
      'A GPX, KML, KMZ, GeoJSON, CSV, FIT or TCX file: the way is drawn on the map as it goes.',
    replace: 'Replace the file',
    remove: 'Remove the track',
    reading: 'Reading the file…',
    failed: 'That file could not be read as a track: it needs two points at least.',
    saveFailed: 'Saving failed. Try again.',
    length: 'Length',
    climb: 'Up · down',
    height: 'Height',
    time: 'Time in the file',
    profile: 'Height along the way: {{low}}–{{high}} m',
  },
  tabs: {
    sections: 'Sections',
    trips: 'Trips',
    allTrips: 'All trips',
    settings: 'Trip settings',
    offline: 'Maps offline',
    actual: 'Plan and what happened',
    stats: 'Statistics',
    lists: 'Tasks and files',
    plan: 'Plan',
    bookings: 'Bookings',
    calendar: 'Calendar',
    variants: 'Variants',
    countries: 'Countries',
    docs: 'Docs',
    journal: 'Journal',
    group: 'Group',
    today: 'Daily spending',
    search: 'Search the trip: a city, a booking…',
    searchOpen: 'Search',
    searchMap: 'Search the map: “{{text}}”',
    searchStop: 'Stop · {{from}} – {{to}}',
    searchBooking: 'Booking · {{city}}',
    searchThing: 'To do · {{city}}',
    budget: 'Budget',
    budgetTitle: 'Budget and costs',
    expected: 'Expected cost',
    range: 'Lowest – highest',
    paid: 'Paid',
    documents: 'of it documents and insurance',
    toPay: 'Still to pay',
    left: 'Left in the budget',
    over: 'Over the budget',
    share: 'Share',
    history: 'Changes',
    export: 'Export',
    import: 'Import places',
    menu: 'Menu',
  },
  rides: {
    ways: 'Ways to get there',
    loading: 'Looking for ways…',
    walk: 'On foot',
    walkHow: 'Along streets and paths · OSRM',
    transit: 'Public transport',
    transitLine: '{{mode}} · line {{line}}',
    transitHow: 'By the timetables · Transitous',
    bike: 'Shared bike · {{system}}',
    scooter: 'E-scooter · {{system}}',
    station: 'Station {{metres}} away',
    free_one: '{{count}} bike free',
    free_other: '{{count}} bikes free',
    near_one: '{{count}} nearby',
    near_other: '{{count}} nearby',
    anywhere: 'Pick up and leave in the operator’s zone',
    taxi: 'Taxi',
    taxiHow: 'By road, with traffic',
    stations: 'Bike stations nearby · GBFS',
    atEnd: 'at the destination',
    bikes_one: '{{count}} bike',
    bikes_other: '{{count}} bikes',
    docks_one: '{{count}} space',
    docks_other: '{{count}} spaces',
    stationsNote:
      'From the operator’s public GBFS feed, updated every minute while this page is open. For another day the stations and the price are shown, not how many bikes there are.',
    location: 'Location and battery',
    locationFrom:
      'Distances are measured from the plan’s place, here {{place}}, not from your phone’s GPS: planning needs no GPS.',
    locationOnce:
      '“Show me on the map” asks for your location once and stops. Following you runs only from “Guide me”, and turns off after 30 minutes or when the page is hidden.',
    locationNever: 'TripPath never asks for your location in the background.',
    showMe: 'Show me on the map',
    guide: 'Guide me',
    stopGuide: 'Stop guiding',
    you: 'You',
    noLocation: 'Your location could not be found.',
    parking: 'Arriving by car · park and ride',
    parkingBy: {
      train: 'then by train',
      bus: 'then by bus',
      other: 'then by public transport',
    },
    parkingFree: 'free',
    parkingNone: 'No park-and-ride car park within 15 km.',
    parkingNote:
      'You arrive by car: car parks tagged park_ride in OpenStreetMap, with their price and the ride on.',
  },
  mail: {
    title: 'Bookings from mail',
    lead: 'Forward a confirmation to the trip’s address, or drop it here: TripPath reads it and shows what it found and where before anything is added.',
    forward: 'Forward here',
    copy: 'Copy',
    copied: 'Copied',
    onlyYours:
      'Only mail from the addresses of accounts that may change this trip is taken. Anyone else’s is thrown away.',
    noAddress:
      'This server receives no mail. Drop the mail or its file here, or paste its text.',
    or: 'Or',
    dropHint: '.eml, .pkpass (Apple Wallet), .ics, PDF or a picture',
    drop: 'Drop a mail or a file here',
    choose: 'Choose a file',
    paste: 'The mail’s text',
    pasteHint: 'Paste the confirmation’s text',
    read: 'Read the text',
    reading: 'Reading…',
    failed: 'It could not be read. Try again.',
    received_one: 'Received · {{count}}',
    received_other: 'Received · {{count}}',
    none: 'Nothing received yet.',
    today: 'today {{time}}',
    yesterday: 'yesterday',
    pasted: 'Pasted',
    byHand: 'open and add by hand',
    status: {
      review: 'To review',
      added: 'Added',
      left: 'Left out',
      none: 'No booking found',
    },
    readFrom: {
      schema: 'Read from the schema.org data inside the mail',
      wallet: 'Read from the Apple Wallet pass',
      calendar: 'Read from the calendar event',
      pdf: 'Read from the attached PDF',
      picture: 'Read from the attached picture',
      text: 'Read from the mail’s text',
    },
    pick: 'Pick a mail to see what was read in it.',
    kind: 'Kind',
    kinds: { stay: 'Stay', ticket: 'Ticket' },
    stop: 'Stop',
    noStop: 'No stop on these days',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    departs: 'Departs',
    localTime: '{{city}} time',
    nights: 'Nights',
    fits: 'fits the stop',
    longer: 'longer than the stop',
    price: 'Price',
    pay: 'Payment',
    paid: 'paid',
    later: 'pay at the property',
    payLater: 'pay later',
    reference: 'Confirmation code',
    address: 'Address',
    flight: 'Flight',
    seat: 'Seat',
    from: 'From: {{sender}}',
    forwardedBy: 'Forwarded by: {{sender}} · {{when}}',
    original: 'The mail as received',
    gone: 'The mail itself is kept for 30 days; only what became of its booking is left.',
    notFound: 'No booking was found in this mail. Add it by hand if it is one.',
    leave: 'Leave out',
    change: 'Change before adding',
    add: 'Add booking',
    addByHand: 'Add by hand',
    again: 'Back to review',
    seeBookings: 'See the bookings',
    note1:
      'First the schema.org booking hidden inside a mail (Booking.com, airlines, Agoda), then an Apple Wallet pass (.pkpass), then a calendar event, then an attached PDF or picture as the bookings page reads them, last the mail’s own text.',
    note2:
      'The marked lines show where each part was read from; pressing a part scrolls the mail to it.',
    note3: 'The mail is kept for 30 days, then only the booking is left.',
  },
  flights: {
    title: 'Flight watch and notices',
    lead: 'Your flights on their day: asked more and more often as each nears, and every change sent to your phone.',
    none: 'No flight with a number and a time on this trip yet.',
    toBookings: 'Add one in Bookings',
    pick: 'Flights',
    late: 'Delayed {{minutes}} min',
    onTime: 'On time',
    notYet: 'Not asked yet',
    cancelled: 'Cancelled',
    departed: 'Departed',
    landed: 'Landed',
    leaves: 'leaves {{time}}',
    atGate: 'gate {{gate}}',
    gateMoved: 'Gate changed',
    terminalMoved: 'Terminal changed',
    checkInDesks: 'Check-in desks',
    beltSaid: 'Bags on belt',
    takes: '{{hours}} h {{minutes}} min',
    terminal: 'Terminal',
    gate: 'Gate',
    gateChanged: 'Gate changed',
    checkIn: 'Check-in',
    belt: 'Bags in {{city}}',
    changes: 'Changes',
    nextCheck: 'Asked next at {{time}}',
    notices: 'Notices',
    yours: 'your account · each traveller their own',
    event: 'Event',
    phone: 'Phone',
    email: 'Email',
    calendar: 'Calendar',
    alarm: 'reminder',
    events: {
      delay: 'Flight delayed over 15 min, or cancelled',
      gate: 'Gate or terminal changed',
      checkin: 'Check-in opens',
      tomorrow: 'Flight, train or ferry tomorrow',
      border: 'To do before a border (visa, form)',
      free: 'Free cancellation ends',
      pay: 'To pay at the property today',
      companion: 'A fellow traveller’s change',
    },
    noMail: 'This server sends no mail.',
    devices_zero: 'No device of yours gets notices yet.',
    devices_one: '{{count}} device of yours gets notices.',
    devices_other: '{{count}} devices of yours get notices.',
    turnOn: 'Allow notifications on this device',
    test: 'Send a test notice',
    turnedOn: 'This device now gets notices.',
    denied: 'Notices are blocked for this site in the browser.',
    noPush: 'This browser cannot get notices.',
    tested_one: 'A test notice was sent to {{count}} device.',
    tested_other: 'A test notice was sent to {{count}} devices.',
    notKept: 'Saving failed. Try again.',
    preview: 'A notice on the phone',
    now: 'TripPath · now',
    schedule: 'When a flight is asked',
    when: { day: '−24 h', hours: '−6 h, −3 h', near: '−2 h … departure', air: 'in the air' },
    how: {
      day: 'once',
      hours: 'once each',
      near: 'every 20 min',
      air: 'once an hour, and landing',
    },
    quota:
      'About 12 requests a flight. The free allowances together (AeroDataBox, AirLabs, Aviationstack) last for dozens of flights a month; the next service is asked when one’s month is spent.',
    offline: 'Offline',
    offline1:
      'A notice arrives by Web Push even when Chrome is closed (Android) or TripPath is on the home screen (iPhone).',
    offline2:
      'A phone with no network gets the notice when it is back. That is why “tomorrow” and “check-in” are calendar alarms too: they ring without a network.',
    link: 'Flight watch',
  },
  lists: {
    title: 'Tasks and files',
    lead: 'What to do before you go, the trip’s files, several things changed at once, and how you are told. All of it is for this trip; how you are told holds for all your trips.',
    open: 'Tasks and files',
    tasks: 'To do before you go',
    count: '{{done}} / {{all}} done',
    add: 'Add',
    what: 'Task',
    remindDay: 'Remind me on',
    remindTime: 'At',
    anyTime: 'Any time',
    save: 'Save',
    remind: 'Remind me',
    noReminder: 'No reminder',
    late: 'Overdue',
    week: 'This week · {{range}}',
    later: 'Before you leave · {{day}}',
    none: 'No date',
    done: 'Done',
    doneOn: 'done on {{day}}',
    tasksNote:
      'Reminders go where you choose under Notifications. A time is your home time; one tied to a place is that place’s time.',
    task: {
      border: '{{form}} for {{country}}',
      borderAbout: 'Countries · {{country}} · takes up to {{days}} days',
      pay: 'Pay the rest at {{name}}',
      payAbout: 'Bookings · {{city}}',
      free: 'free cancellation until {{day}}',
      checkin: 'Online check-in, {{flight}}',
      checkinAbout: 'Opens {{hours}} h before departure',
      maps: 'Keep the day maps on your phone',
      mapsAbout: 'Offline maps · over Wi-Fi',
      mapsOpen: 'Open',
      noDay: 'A reminder with no date',
    },
    files: 'Files',
    filesCount_one: '{{count}} file · {{size}}',
    filesCount_other: '{{count}} files · {{size}}',
    zip: 'Download all (ZIP)',
    drop: 'Drop files here',
    dropAbout: 'PDFs and photos · up to 20 MB each',
    uploading: 'Uploading…',
    tied: 'for: {{what}}',
    wholeTrip: 'the whole trip',
    tieTo: 'What {{name}} is for',
    remove: 'Delete {{name}}',
    removeSure: 'Delete for good',
    filesNote: 'The trip’s members see its files.',
    things: '{{city}}: things to do',
    thingsNone: 'Things to do',
    thingsNoneAbout: 'No stop has things to do yet. Add them on a stop’s days.',
    thingsHow: 'Tick rows to change them together',
    stop: 'Stop',
    chosen_one: '{{count}} thing',
    chosen_other: '{{count}} things',
    move: 'Move to another day',
    moveTo: 'Move to',
    status: 'Change status',
    tag: 'Add a tag',
    tagName: 'Tag',
    copy: 'Copy',
    copyTo: 'Copy to',
    delete: 'Delete',
    deleteSure_one: 'Delete {{count}} for good',
    deleteSure_other: 'Delete {{count}} for good',
    choose: 'Choose',
    day: 'Day',
    time: 'Time',
    thing: 'Thing to do',
    state: 'Status',
    price: 'Price',
    booked: 'booked',
    planned: 'planned',
    notices: 'Notifications',
    whatTold: 'Notification types',
    channel: { email: 'Email', push: 'Browser', telegram: 'Telegram' },
    event: {
      before: {
        name: 'A reminder from “To do before you go”',
        about: 'At the time set on the row',
      },
      invite: { name: 'An invitation to a trip', about: 'Someone added you to a trip' },
      companion: {
        name: 'A change to the trip',
        about: 'Someone changed a stop, a booking or a cost',
      },
      debt: { name: 'A cost shared with you', about: 'Someone added a cost that includes you' },
      weekly: {
        name: 'Weekly summary',
        about: 'Mondays at 08:00: what is ahead and what to pay',
      },
    },
    noMail: 'This server sends no mail.',
    inviteMail: 'An invitation always comes by email.',
    noBot: 'This server runs no Telegram bot.',
    notTied: 'Connect Telegram first.',
    testMail: 'Send a test email',
    mailSent: 'Test email sent. Check your inbox.',
    mailFailed: 'The test email could not be sent.',
    tie: 'Connect Telegram',
    tieHow: 'Telegram opened: press Start, and your notifications go there.',
    untie: 'Disconnect Telegram',
    mailNote:
      'Emails go through the server’s SMTP, as invitations do. Each email ends with a link that stops emails of its kind.',
  },
  stats: {
    title: 'Statistics',
    lead: 'All your trips together, worked out from the plan and from what you added after each trip. Only finished trips count.',
    open: 'Statistics',
    explored: 'Places visited',
    exploredAs: 'Show as',
    heat: 'Heatmap',
    heatFew: 'Little',
    heatMany: 'Much',
    heatWhat:
      'The colour says how much of your trips is there: recorded tracks, routes, stops, journal places and photos. Red where you were most.',
    fog: 'Unexplored areas',
    exploredSize: '{{cells}} squares of about 2 × 2 km · about {{km2}} km²',
    exploredMap: 'Map of places visited',
    noExplored:
      'Recorded tracks, places from the journal and the stops of finished trips show here.',
    failed: 'The statistics could not be loaded. Try again.',
    year: 'Year',
    allYears: 'All years',
    allTrips: 'All finished trips',
    tripsPick: 'Trips',
    leadTrip: 'This trip alone, worked out from its plan: before it starts too.',
    onTheWay: 'On the way',
    byTrain: '{{share}}% by train',
    nightsAway: 'Nights away',
    trips_one: '{{count}} trip',
    trips_other: '{{count}} trips',
    countries: 'Countries',
    new_one: '{{count}} new',
    new_other: '{{count}} new',
    towns: 'Towns',
    longestTown: 'longest in {{town}}',
    flown: 'flights {{share}}%',
    modes: 'Modes of travel',
    noWays: 'No finished trip with ways yet.',
    longest: 'Records',
    longestFlight: 'Longest flight',
    longestTrain: 'Longest train ride',
    longestStay: 'Longest stay',
    highest: 'Highest point',
    hours: '{{hours}} h {{minutes}} min',
    nights_one: '{{count}} night',
    nights_other: '{{count}} nights',
    byCountry: '{{year}} by country',
    badges: 'Achievements',
    progress: '{{have}} / {{need}}',
    badge: {
      'first-trip': { name: 'First trip', how: 'Finish a trip' },
      'four-countries': {
        name: 'Four countries in one trip',
        how: 'Four countries on one trip',
      },
      'fast-train': { name: 'High-speed train', how: 'A train at over 200 km/h' },
      'night-train': { name: 'Night train', how: 'Sleep one night on a train' },
      ferry: { name: 'Over the water', how: 'Go one way by ferry' },
      'long-flight': { name: 'Long haul', how: 'A flight of 5,000 km or more' },
      climb: { name: 'Up the mountain', how: 'Climb 400 m on one way' },
      slow: { name: 'Slow travel', how: 'Seven nights in one town' },
      equator: { name: 'Both hemispheres', how: 'Stops north and south of the equator' },
      continents: { name: 'Every continent', how: 'Stops on all six continents' },
      'countries-10': { name: '10 countries', how: 'Stay in 10 countries' },
      'countries-25': { name: '25 countries', how: 'Stay in 25 countries' },
      'nights-100': { name: '100 nights away', how: 'Spend 100 nights away' },
      'train-10000': { name: '10,000 km by train', how: 'Go 10,000 km by train' },
      'foot-100': { name: '100 km on foot', how: 'Walk 100 km of ways' },
      'around-the-world': { name: 'Around the world', how: 'Go 40,075 km in all' },
    },
    studio: 'Poster and route video',
    noTrips: 'A poster or a video can be made once a trip is over.',
    noMap: 'None of this trip’s stops is on the map yet.',
    trip: 'Trip',
    making: 'Format',
    poster: 'Poster',
    video: 'Video',
    sizeLabel: 'Size',
    size: { a4: 'A4', a3: 'A3', '50x70': '50×70' },
    ground: 'Background',
    dark: 'Dark',
    light: 'Light',
    names: 'Stop names and dates',
    arcs: 'Flights as arcs',
    arcsOff: 'left out, only the ways over land and water show',
    posterPreview: 'Poster preview, {{size}}',
    posterMap: 'The route',
    posterLine: '{{nights}} nights · {{km}}',
    posterNote: 'The poster is a PDF, ready to print, made from the trip’s route.',
    videoPreview: 'Video preview',
    videoNote:
      'Video: 30 s in 1080p, the route drawn day by day with the date and kilometres in the corner. It is made on this device and saved here; nothing is uploaded.',
    preview: 'Preview',
    makePoster: 'Make poster (PDF)',
    makeVideo: 'Make video',
    recording: 'Making the video…',
    saveVideo: 'Save the video',
  },
  actual: {
    title: 'Plan and what happened',
    lead: 'Add files that show where you really were, and TripPath sets them beside the plan: nights, ways and kilometres. Nothing is tracked; only the files you add count.',
    open: 'Plan and what happened',
    sources: 'Sources',
    add: 'Add a file',
    working: 'Working…',
    noSources:
      'No files yet. Add a Google Timeline export (JSON) or tracks from your phone or watch: GPX, KML, KMZ, GeoJSON, CSV, FIT or TCX.',
    sourceLine: '{{name}} · {{range}}',
    points_one: '{{n}} point',
    points_other: '{{n}} points',
    remove: 'Remove {{name}}',
    again: '{{name}} is already there as {{kept}}, so it was skipped.',
    unread: '{{name}} is neither a timeline nor a track.',
    outside: 'Nothing in {{name}} falls within the trip.',
    failed: 'Could not reach the server. Try again.',
    archived: 'This trip is archived. Take it out of the archive to change it.',
    once: 'The same way is never counted twice: a file is known by its id (opentracks:trackid) or by its time and place.',
    nights: 'Nights',
    nightsSub: 'planned / as it was',
    stops: 'Stops',
    stopsAdded: '{{names}} added',
    km: 'Over land and water',
    kmSub: '{{km}} km planned, as the crow flies',
    flights: 'Flights',
    flightsSub: 'planned / flown',
    stopsTitle: 'Stops',
    stop: 'Stop',
    plan: 'Plan',
    actual: 'Actual',
    nightsCol: 'Nights',
    difference: 'Difference',
    same: 'no difference',
    newStop: 'new stop',
    notReached: 'not visited',
    otherDays: 'other dates',
    more_one: '{{count}} night more',
    more_other: '{{count}} nights more',
    fewer_one: '{{count}} night fewer',
    fewer_other: '{{count}} nights fewer',
    assumed: 'no data, counted as planned',
    waysTitle: 'Ways',
    noWays: 'No way between stops yet.',
    way: 'Way',
    cost: 'Cost',
    plannedTo: '{{mode}} to {{city}}',
    going: { flight: 'Flight', train: 'Train', road: 'By road', foot: 'On foot' },
    unknown: 'no data',
    keep: 'Leave the plan unchanged',
    write: 'Update the plan to what happened',
    wroteIn: 'The plan is updated. The old plan is kept here to compare with.',
    writeNote:
      '“Update the plan” sets each stop’s nights as they were and adds the new stops and the ways as they went. The plan is kept here to compare with, and it can be taken back.',
    written: 'Written into the trip on {{day}}.',
    takeBack: 'Take it back',
    takenBack: 'The plan is restored.',
    notBack: 'The trip has changed since, so this can no longer be taken back.',
    countriesTitle: 'Days in countries',
    noPassport: 'Add your passport on the Docs page to see what each country allows.',
    daysOf: '{{days}} / {{of}} d',
    days_one: '{{count}} day',
    days_other: '{{count}} days',
    rule: {
      free: 'visa-free, up to {{days}} days',
      card: 'visa-free with an arrival card, up to {{days}} days',
      eta: 'with an ETA, up to {{days}} days',
      evisa: 'with an e-visa, up to {{days}} days',
      arrival: 'visa on arrival, up to {{days}} days',
      visa: 'with a visa, up to {{days}} days',
    },
    freeToMove: 'free to move with your passport',
    noRule: 'no rule known for your passport',
    countriesNote:
      'A day counts in a country if you were there at any hour, local time; a travel day counts in both. The rules come from the Docs page, for your passport.',
    schengenTitle: 'Schengen rule 90/180',
    schengenWho: '{{name}} ({{country}} passport) · the 180 days to {{day}}',
    schengenSquares: 'The 180 days of the window, each square 6 days',
    schengenUsed: '{{used}} days used, {{planned}} more planned',
    schengenLeft: '{{left}} days to spare',
    schengenOver: '{{over}} days over the limit',
    schengenFrees_one: '; on {{day}} {{count}} day frees up',
    schengenFrees_other: '; on {{day}} {{count}} days free up',
    schengenNone:
      'No one on this trip has a passport that counts Schengen’s 90 days in 180, or no days in the area are planned.',
    yearTitle: '{{year}} in all',
    home: 'home',
    yearNote: 'For tax residence: more than 183 days in one country within 12 months.',
  },
  record: {
    title: 'Record the way {{from}} → {{to}}',
    titleAlone: 'Record the way from {{from}}',
    lead: 'Only when you start it. Where you are stays on this phone and goes to the server only when you have looked the recording over and pressed “Add to trip”: TripPath does not track where you are.',
    how: 'How it works',
    how1: 'The phone writes a point every 10 m or 30 s.',
    how2: 'The points stay on this phone only.',
    how3: 'At the end you see everything and choose what goes into the trip.',
    how4: 'The recording stops by itself after 6 hours.',
    going: 'How you go',
    goingAs: {
      detect: 'Automatic',
      foot: 'On foot',
      bike: 'By bike',
      car: 'By car',
      train: 'By train',
    },
    start: 'Start recording',
    startHow:
      'The browser asks to know where you are “while using the app”. The screen stays on.',
    elapsed: 'on the way',
    km: 'km',
    up: 'm up',
    now: 'Now: {{going}}',
    before: 'before {{emoji}} {{going}} {{km}}',
    pause: 'Pause',
    resume: 'Go on',
    finish: 'Finish',
    paused: 'Paused',
    arrived: 'You have arrived at {{city}}',
    arrivedStay: 'Here {{nights}} nights · {{stay}} {{metres}} m · check-in {{time}}',
    arrivedNights: 'Here {{nights}} nights',
    guide: 'Guide me to {{stay}} on foot',
    lost: 'Where you are could not be found. Recording waits for it.',
    guiding: 'Guiding',
    turn: {
      depart: 'Start',
      arrive: 'You have arrived',
      straight: 'Straight on',
      left: 'Turn left',
      right: 'Turn right',
      'slight left': 'Bear left',
      'slight right': 'Bear right',
      'sharp left': 'Turn sharp left',
      'sharp right': 'Turn sharp right',
      uturn: 'Turn back',
      other: 'Go on',
    },
    onto: '{{turn}}: {{street}}',
    in: 'in {{metres}} m',
    then: 'Then: {{turn}} {{metres}} m',
    left: '{{metres}} m in all · {{minutes}} min',
    quiet: 'Voice off · map on this phone',
    endGuide: 'End guiding',
    noGuide: 'No way on foot was found to {{stay}}.',
    done: 'Recording done',
    ways: 'Ways',
    stays: 'Stops',
    wayLine: '{{going}} {{km}}',
    wayWhen: '{{from}}–{{to}} · likely {{sure}}%',
    stayLine: '{{name}} · {{minutes}} min',
    stayWhen: '{{from}}–{{to}} · {{planned}}',
    inPlan: 'in the day’s plan',
    notInPlan: 'not in the plan',
    stayAt: 'Stop at {{time}}',
    nothing: 'Nothing was recorded.',
    deleteAll: 'Delete all',
    add: 'Add to trip',
    added:
      'Added to the trip: the ways as the way on from {{city}}, the stops not planned as things done.',
    deleted: 'The recording is deleted from this phone.',
    failed: 'Could not reach the server; the recording stays on this phone.',
    notes: 'Data and how it is worked out',
    note1:
      'The points are gathered by the browser on this phone (Geolocation watchPosition) only while the page is open; to record in the background, record with OpenTracks or OsmAnd and import the file afterwards.',
    note2:
      'How you go is read from the speed of each minute; a stretch of a single minute goes with the way before it.',
    note3:
      'A stop: at least 10 min within 100 m. Arriving: within 300 m of the stop’s point or its stay.',
    note4: 'Guiding: OSRM’s turns (steps=true); the day’s map is on this phone already.',
    note5: 'Every time is local where it was; the recording keeps each time with its offset.',
    link: 'Record this way',
    mapLabel: 'Map of the way recorded',
  },
  wayDraw: {
    caps: 'Way · {{day}}',
    title: 'Way from {{from}}',
    name: 'Name of the way',
    how: 'Line type',
    mode: { foot: 'On foot', bike: 'By bike', car: 'By car', straight: 'Straight line' },
    undo: 'Undo',
    finish: 'Finish drawing',
    draw: 'Draw on the map',
    hint: 'A click on the map adds a point; the line goes along roads and paths (OSRM). A point can be dragged.',
    anchors: 'Points of the way',
    none: 'No point yet: click the map to start.',
    point: 'Point {{n}}',
    remove: 'Remove {{name}}',
    segment: '{{km}} · ↑ {{up}} m ↓ {{down}} m · {{time}}',
    file: 'Or take the line from a file',
    drop: 'Drop a file here',
    choose: 'Choose a file',
    kinds: 'GPX, KML, KMZ, GeoJSON, CSV, FIT, TCX',
    read: 'read',
    fileInfo: '{{km}} · {{points}} points',
    snap: 'Snap to roads and paths',
    snapHow:
      'The GPS’s wobble is smoothed; where there is no path, the line stays as in the file.',
    asFile: 'As in the file',
    onRoads: 'On roads',
    snapFailed: 'No road or path matched: the line stays as in the file.',
    fileFailed: 'That file could not be read as a way: it needs two points at least.',
    photos: 'Points from photos',
    placed_one: '{{count}} photo became a point beside the way, where it was taken.',
    placed_other: '{{count}} photos became points beside the way, where they were taken.',
    unplaced_one: '{{count}} photo has no place; it stays with the way without a point.',
    unplaced_other: '{{count}} photos have no place; they stay with the way without a point.',
    noPhotos: 'No photos yet.',
    addPhotos: 'Add photos',
    removePhoto: 'Remove this photo',
    photo: 'Photo taken {{when}}',
    photoUnknown: 'Photo, time not known',
    profile: 'Height profile',
    length: 'Length',
    ascent: 'Ascent',
    descent: 'Descent',
    highest: 'Highest',
    lowest: 'Lowest',
    heightsFrom: 'Heights: Terrarium data; the heights in a file come first.',
    noHeights: 'No heights yet.',
    profileSaid: 'Height along the way: {{low}}–{{high}} m',
    cancel: 'Cancel',
    save: 'Save the way',
    saved: 'The way is kept: {{name}}.',
    toPlan: 'See it on the plan',
    failed: 'Could not reach the server.',
    mapLabel: 'Map of the way from {{from}}',
    link: 'Draw the way on the map',
  },
  mapAdd: {
    title: 'Add a place to {{city}}',
    how: 'How you add it',
    search: 'Search',
    paste: 'Link or coordinates',
    searchLabel: 'Name of the place',
    searching: 'Searching OpenStreetMap…',
    noSpots: 'Nothing found near {{city}}.',
    pasteLabel: 'Paste a Google Maps link, coordinates or a Plus Code',
    reading: 'Reading…',
    readAs: {
      decimal: 'decimal degrees',
      seconds: 'degrees, minutes, seconds',
      minutes: 'degrees and minutes',
      plusCode: 'full Plus Code',
      shortCode: 'short Plus Code + town',
      link: 'read from the link',
    },
    unread: 'Not a link, coordinates or a Plus Code: searched by name instead.',
    readHow:
      'The name and coordinates come from the link itself; no Google key is needed. The address comes from OpenStreetMap.',
    clear: 'Clear',
    addTo: 'Add to {{city}}',
    formats: 'Supported formats',
    longLink: 'long link',
    nameOnly: 'A field with only a number that cannot be a coordinate is searched by name.',
    hint: 'Hint',
    hintText:
      'Press a spot on the map (or hold it, or right-click): you see what is there and can add it at once.',
    here: 'Here',
    near: 'Near',
    add: 'Add',
    addPoint: 'Add this point to {{city}}',
    moveStop: 'Put the stop here',
    close: 'Close',
    point: 'Point on the map',
    added: 'Added to {{city}}: {{name}}. Put it on one of the stop’s days.',
    moved: '{{city}} is now at {{address}}.',
    toDays: 'See the stop’s days',
    link: 'Add a place from the map',
    failed: 'Could not reach the server.',
    mapLabel: 'Map of {{city}}',
  },
  myPlaces: {
    menu: 'My places',
    title: 'My places',
    count_one: '{{count}} place',
    count_other: '{{count}} places',
    countries_one: '{{count}} country',
    countries_other: '{{count}} countries',
    lead: 'Places you want to see some day, kept apart from your trips: add one to a trip when it fits the way.',
    search: 'Search by name, town or tag',
    tags: 'Tags',
    all: 'All',
    newTag: 'New tag',
    favouritesOnly: 'Favourites only',
    showVisited: 'Show places visited',
    visitedOn: 'visited {{day}}',
    favourite: 'Favourite',
    none: 'No places kept yet. Add them from a Google Maps or Naver list, a file, or the map of a trip.',
    noneFound: 'No place matches.',
    mapLabel: 'Map of my places',
    mapNote:
      'Places close together are one circle with a number. Zoom in or press the circle to see each place. Places visited are faded.',
    place: 'Place',
    photoOf: 'Photo {{n}} of {{count}} of {{name}}',
    morePhotos: '+{{count}}',
    edit: 'Edit',
    addTag: 'Tag',
    markFavourite: 'Favourite',
    markVisited: 'Mark as visited',
    visited: 'Visited',
    addTo: 'Add to trip: {{trip}} · {{city}}, {{days}}',
    onTrip: 'On {{trip}} · {{city}}',
    noStop: 'No stop of your trips within 30 km.',
    similar: 'A similar place is already kept',
    similarSays:
      '“{{name}}” {{metres}} m away, added {{day}} {{source}}. Is it the same place?',
    merge: 'Merge into one',
    keepApart: 'Keep apart',
    mergeHow:
      'Merging keeps the photos, tags and notes of both; trips with either place point to the one place.',
    note: 'Notes',
    hours: 'Opening hours',
    hoursFrom: '{{hours}} (OpenStreetMap)',
    noHours: 'Not given on OpenStreetMap',
    asking: 'Asking OpenStreetMap…',
    trips: 'On trips',
    noTrips: 'On no trip yet',
    added: 'Added',
    addedHow: '{{day}}, {{source}}',
    source: {
      google: 'from a Google Maps list',
      naver: 'from a Naver Map list',
      file: 'from a file',
      search: 'from a search',
      map: 'from the map',
    },
    addPhotos: 'Add photos',
    removePhoto: 'Remove this photo',
    photoFailed: 'This file is not a photo.',
    remove: 'Remove place',
    removeAsk:
      'Remove “{{name}}” from your places? Its photos go too; things to do it became stay on their trips.',
    editTitle: 'Edit place',
    name: 'Name',
    save: 'Save',
    cancel: 'Cancel',
    tagTitle: 'New tag',
    tagEditTitle: 'Edit tag',
    tagName: 'Name',
    emoji: 'Emoji',
    colour: 'Colour',
    taken: 'You already have a tag of this name.',
    removeTag: 'Remove tag',
    tagsOf: 'Tags of {{name}}',
    failed: 'Could not reach the server.',
    noneChosen: 'Choose a place on the list or the map.',
  },
  offline: {
    title: 'Maps offline',
    lead: 'Each stop’s surroundings kept on the phone, so the map, its places and its roads work in flight mode and without a SIM.',
    areas: '{{trip}} · areas',
    total: '{{size}} in all',
    noPlaces: 'No stop of the trip is on the map yet.',
    route: 'The route at a glance',
    routeHow_one: 'its {{count}} stop and the way between · close in to a town',
    routeHow_other: 'all {{count}} stops and the ways between · close in to a town',
    stopHow: '{{days}} · {{km}} km round · to the streets',
    loading: 'loading · {{done}} / {{all}}',
    waiting: 'Waiting',
    old_one: 'Old · {{count}} day',
    old_other: 'Old · {{count}} days',
    kept: 'On the phone',
    none: 'Not kept',
    progress: 'Keeping {{name}}',
    radiusOf: 'Radius round {{name}}',
    km: '{{km}} km',
    remove: 'Remove {{name}}',
    loadAll: 'Download all · {{size}} more',
    renew: 'Renew old maps',
    changeRadius: 'Change the radius',
    waitingWifi: 'On a phone network: the maps wait for Wi-Fi.',
    settings: 'Settings',
    wifiOnly: 'Over Wi-Fi only',
    wifiOnlyHow:
      'Nothing is downloaded over mobile data; areas waiting go on when Wi-Fi comes.',
    autoNew: 'New stops by themselves',
    autoNewHow: 'A stop added to the trip is downloaded on the next Wi-Fi.',
    renewing: 'Renewing',
    renewingHow:
      'A map is old after {{count}} days; the old one works on until the new one is kept.',
    storage: 'Storage on this phone',
    noStorage: 'This browser does not report its storage space.',
    maps: 'Maps',
    documents: 'Documents and receipts',
    free: 'About {{size}} GB free',
    persisted: 'kept for good',
    notPersisted: 'may be cleared when the phone runs short',
    persist: 'Keep for good',
    persistHow:
      'Kept for good, the browser does not clear the maps by itself when space runs short.',
    radiusUsual: 'A stop is kept {{km}} km round unless its radius is changed.',
    today: 'Offline · the map from the phone, {{day}}',
    next: 'Next: {{name}}',
    walk: 'on foot {{km}} km · {{minutes}} min · where you are by GPS',
    whereAmI: 'My location',
    noGps: 'Where you are could not be found.',
    gpsHow:
      'GPS works without a network; the walk is from the day’s plan, the map from the phone.',
  },
  emissions: {
    title: 'Carbon footprint',
    lead: 'Each way’s CO₂ for one traveller: the trip in all, by kind, and what one swap would change.',
    open: 'Carbon footprint',
    oneTraveller: '{{name}} · one traveller',
    unit: 'kg CO₂e',
    perDay: '{{kg}} kg a day · {{km}} km of ways',
    split: 'By kind',
    flights_one: 'Flights · {{count}}',
    flights_other: 'Flights · {{count}}',
    trains_one: 'Trains · {{count}}',
    trains_other: 'Trains · {{count}}',
    ground: 'Buses, ferries, cars and rides in town',
    stays_one: 'Stays · {{count}} night',
    stays_other: 'Stays · {{count}} nights',
    basis:
      'Counted from each way’s length and kind. A flight: airport to airport, its changes and its cabin. Stays: below.',
    rf: 'Count a flight’s effect high up (radiative forcing)',
    ways: 'Ways and stays',
    largest: 'the largest first',
    way: 'Way',
    kind: 'Kind',
    km: 'km',
    kg: 'kg CO₂e',
    via: 'changes at {{cities}}',
    cabin: {
      economy: 'economy',
      premium: 'premium economy',
      business: 'business',
      first: 'first',
    },
    stay_one: 'Stay · {{country}}, {{count}} night',
    stay_other: 'Stay · {{country}}, {{count}} nights',
    stayHow: '{{nights}} × {{per}} kg a room and night ÷ {{sharing}} in a room',
    estimated: 'estimate',
    hotel: 'Hotel',
    towns_one: 'In towns · {{count}} ride',
    towns_other: 'In towns · {{count}} rides',
    townsKind: 'Metro, bus, taxi',
    swaps: 'Swaps to think about',
    swap: '{{from}} → {{to}} by {{mode}}',
    saves: '−{{kg}} kg',
    longer: '+{{time}}',
    dearer: '+{{amount}}',
    cheaper: '−{{amount}}',
    openRoutes: 'Open in the route comparison',
    noSwaps: 'No flight has a sensible way over land or sea instead.',
    routesNote: 'Routes shows each way’s CO₂ beside its price, and puts them in order by it.',
    data: 'Data and how it is counted',
    note1:
      'The factors are in the repository’s table data/emissions.tsv: kg CO₂e a passenger-km by kind and cabin, each row with its source (DESNZ/Defra 2025 conversion factors). No key or service is needed.',
    note2:
      'A flight: airport to airport, with the changes on its ticket, 8 % longer as DESNZ recommends for the way planes fly; within one country at the domestic factor, else at the international one for its cabin. Its effect high up is in or out above.',
    note3:
      'Stays: nights × the country’s factor a room and night ÷ those in the room (up to two travellers). A country not in the table takes the table’s average, marked “estimate”.',
    note4:
      'Trains, buses, ferries and rides in town at Defra’s average factors; a car is shared by the travellers.',
    note5: 'The figures are estimates, rounded to kilograms; they do not touch the budget.',
    loading: 'Counting…',
  },
  importPlaces: {
    title: 'Import places',
    kinds: 'Source',
    list: 'Google Maps or Naver list',
    file: 'File: GPX, KML, KMZ',
    link: 'The list’s share link',
    read: 'Read the list',
    reading: 'Reading…',
    summary_one: '“{{name}}” · {{count}} place',
    summary_other: '“{{name}}” · {{count}} places',
    toStops_one: '{{count}} goes to stops',
    toStops_other: '{{count}} go to stops',
    farOff_one: '{{count}} is far from the route',
    farOff_other: '{{count}} are far from the route',
    all: 'Choose all',
    take: 'Take in',
    place: 'Place',
    kind: 'Kind in the list',
    stop: 'Stop',
    day: 'Day',
    noDay: 'No day yet',
    newStop: 'New stop: {{city}}',
    newStopAfter: 'New stop: {{city}} · after {{after}}',
    away: '{{city}} · {{km}} km, nearest stop {{from}}',
    none: 'Leave without a stop',
    noneDay: '—',
    note: 'A place goes to the nearest stop under 30 km away. One further off can be a new stop in its place on the route, or stay without one. Its name, address and point come from the list; every place is also kept among your own places.',
    drop: 'Drop a file here or choose one',
    choose: 'Choose a file',
    dropHint: 'GPX, KML or KMZ · up to 20 MB · Google My Maps, Komoot, AllTrails, Garmin',
    fileRead: '{{file}} · {{trails}} · {{points}}',
    trails_one: '{{count}} trail',
    trails_other: '{{count}} trails',
    points_one: '{{count}} point',
    points_other: '{{count}} points',
    trail: 'Trail “{{name}}”',
    trailFacts: '{{km}} km · {{days}} on foot. Added as a way, not as a flight.',
    trailClimb:
      '{{km}} km · climb {{ascent}} m · {{days}} on foot. Added as a way, not as a flight.',
    walk: 'On foot',
    from: 'From',
    endTown: 'Name of the stop at its end',
    pointInFile: 'Point in the file',
    nothingYet: 'Nothing is added until you press “Add”.',
    cancel: 'Cancel',
    add_one: 'Add {{count}} place',
    add_other: 'Add {{count}} places',
    addTrail: 'Add the trail',
    addBoth_one: 'Add the trail and {{count}} place',
    addBoth_other: 'Add the trail and {{count}} places',
    badLink: 'That is not a Google Maps or Naver Map list link.',
    notList: 'No list was found at that link. Is it shared with a link?',
    badFile: 'That file has no trail and no named places.',
    failed: 'Could not reach the server.',
    archived:
      'This trip is archived. Take it out of the archive in its settings to add places.',
    waiting: 'Places without a day',
    waitingHint:
      'Taken in, or your own places within 15 km: choose a day, and it is a thing to do then.',
    onDay: 'Put on a day',
    pickDay: 'Day for {{name}}',
    takeOff: 'Take off {{name}}',
  },
  exportTrip: {
    title: 'Export the trip “{{name}}”',
    kinds: 'File type',
    pdf: 'PDF trip book',
    gpx: 'GPX for a map app',
    geo: 'KML, GeoJSON',
    table: 'Table (CSV, Excel)',
    ics: 'Calendar (.ics)',
    contents: 'What the book holds',
    cover: 'Cover and route map',
    coverHint: 'The trip’s name, dates, travellers, every stop on the map',
    days: 'Days',
    daysHint:
      'Each day: the place, the night, the way on in local time, the doings, the weather when known',
    bookings: 'Bookings and tickets',
    bookingsHint: 'Confirmation code, address, check-in, seat, bags',
    entry: 'Entry and documents',
    entryHint:
      'Each country’s rule for your passport, the days allowed, what to do before the border',
    money: 'Money',
    moneyHint:
      'Budget, paid, still to pay, by country. Left out, the book has no amount at all',
    format: 'Format',
    language: 'Language',
    paper: 'Paper',
    dayAPage: 'Each day on a new page',
    branches: 'With the branch trips',
    branchesHint: '{{names}} as chapters of their own',
    preview: 'Preview',
    coverPage: '{{page}} · Cover',
    dayPage: '{{page}} · Day',
    bookingPage: '{{page}} · Booking',
    entryPage: '{{page}} · Country',
    entryOf: '{{country}} · entry',
    pages_one: 'About {{count}} page · {{size}}.',
    pages_other: 'About {{count}} pages · {{size}}.',
    serverMade:
      'The server makes the PDF; the browser only downloads it, so the file is the same on the phone and the computer.',
    counting: 'Counting the pages…',
    stops: 'Stops as points',
    stopsHint: 'Name, nights, dates; Organic Maps, OsmAnd and Google My Maps show them',
    ways: 'Ways over land and sea as tracks',
    waysHint: 'Train, bus, ferry, car: the route’s line as on the map, each way its own track',
    flights: 'Flights as straight lines',
    flightsHint: 'Many apps draw them across the whole map; off unless chosen',
    sights: 'The days’ doings as points',
    sightsHint_one: '{{count}} place, with its day and stop',
    sightsHint_other: '{{count}} places, each with its day and stop',
    gpxFile: 'File: {{file}} · GPX 1.1 · times in UTC, as GPX requires.',
    kml: 'KML (Google Earth, My Maps)',
    kmz: 'KMZ with pictures',
    geojson: 'GeoJSON',
    places: 'Stops and the days’ places',
    placesHint: 'Each place in a folder by its day; name, dates, notes',
    lines: 'Ways as lines, coloured by how',
    linesHint: 'Train green, bus orange, ferry teal, as on TripPath’s map',
    amounts: 'Amounts',
    amountsHint: 'In GeoJSON the properties cost and currency; in KML in the description',
    geoNote:
      'GeoJSON: one FeatureCollection of points and lines, each with its kind, arrivalAt and departureAt with the time zone’s offset. For QGIS, uMap and a map of your own.',
    whatGoes: 'Table contents',
    costs: 'Expenses',
    costsHint:
      'Date, country, category, the amount in the local money and the trip’s, the rate, who paid, how it was shared',
    tableBookings: 'Bookings',
    tableBookingsHint:
      'What, when in local time, the confirmation code, the price, paid or to pay',
    tableDays: 'Days and doings',
    tableDaysHint: 'A row for each doing',
    xlsx: 'Excel (.xlsx), each part a sheet',
    csv: 'CSV, each part a file (ZIP)',
    tableNote:
      'CSV: UTF-8, separated by commas, a point before the decimals. In Excel amounts are numbers, dates are dates, and a summary sheet adds them up with formulas.',
    noRows: 'Nothing to show yet.',
    icsLead:
      'This is also on the Calendar page: a file once, or a link your calendar keeps up to date by itself.',
    feed: 'Subscription link',
    feedNone: 'No link yet.',
    feedNote:
      'Whoever has the link sees the dates and places, not the amounts or the confirmation codes. A new link ends the old one.',
    newFeed: 'Make a new link',
    icsOnce: 'Download the .ics file',
    cancel: 'Cancel',
    download: 'Download {{what}}',
    copy: 'Copy the link',
    copied: 'Link copied',
    makeFeed: 'Make the link',
    failed: 'Could not reach the server.',
  },
  sharing: {
    title: 'Sharing and access',
    members: 'Members',
  },
  history: {
    title: 'Changes',
  },
  docs: {
    open: 'Docs',
    title: 'Documents',
    back: 'Back to the trip',
    rules: 'Entry rules on this route',
    rulesLead:
      'Days counted from the plan · a reminder before each border that needs something',
    passport: 'Passport',
    noPassport: 'Choose the passport you travel on to see what each border needs.',
    country: 'Country',
    border: 'Enters',
    stay: 'Days there',
    todo: 'Needed before entry',
    reminder: 'Done',
    pickPassport: 'choose a passport',
    noCountries: 'Add stops to the route to see its borders.',
    undecided: 'undecided',
    days: '{{days}} of {{max}} allowed',
    over_one: '{{count}} day over the limit',
    over_other: '{{count}} days over the limit',
    unknown: 'Not known: check the official sources, or say it yourself',
    kind: {
      free: 'No visa or permit needed',
      card: 'Arrival card: {{form}}',
      eta: 'Travel authorisation: {{form}}',
      evisa: 'E-visa: {{form}}',
      arrival: 'Visa on arrival',
      visa: 'Visa from an embassy: {{form}}',
    },
    official: 'official page',
    officialFor: 'Official page for {{country}}',
    wikipedia: 'Wikipedia',
    wikipediaFor: 'Wikipedia: {{country}}',
    yours: 'as you said',
    ownRule: 'Your own rule for {{country}}',
    whose: 'Traveller',
    mine: 'Mine',
    actions: 'Actions',
    needs: 'Requirement',
    stayDays: 'Days allowed',
    forgetOwn: 'Delete my rule',
    kindName: {
      free: 'Not needed',
      card: 'Arrival card',
      eta: 'Travel authorisation',
      evisa: 'E-visa',
      arrival: 'Visa on arrival',
      visa: 'Visa from an embassy',
    },
    due: 'by {{day}}',
    doneLabel: 'done',
    tick: '{{todo}} for {{country}} done',
    indicative: 'Indicative only. Check official sources before you travel.',
    documents: 'Documents',
    scan: 'Scan with camera',
    upload: 'Upload',
    addPlain: 'Add without a file',
    identity: 'Identity & cover',
    linked: 'All documents',
    noDocuments: 'No documents yet.',
    passportOk: 'valid to {{day}} · 6-month rule OK',
    passportShort: 'expires {{day}}: under 6 months after the trip',
    passportNoDate: 'add the expiry date',
    notAdded: 'not added',
    optional: 'optional',
    insuranceMissing: 'Travel insurance missing',
    addPolicy: 'Add policy',
    privateToYou: 'private to you',
    sharedBy: 'from {{name}}',
    openFile: 'Open {{name}}',
    edit: 'Change {{name}}',
    remove: 'Remove {{name}}',
    removeTitle: 'Remove this document?',
    removeBody: '{{name}} and its file are removed for everyone who sees it.',
    offline: 'Keep the files on this phone',
    offlineDone: 'Saved for offline use on this phone',
    kinds: {
      passport: 'Passport',
      id_card: 'ID card',
      visa: 'Visa',
      driving_licence: 'Driving licence',
      permit: 'International driving permit (IDP)',
      vehicle: 'Vehicle registration',
      insurance: 'Travel insurance',
      motor_insurance: 'Motor insurance (Green Card)',
      casco_insurance: 'Comprehensive car insurance',
      accident_insurance: 'Accident insurance',
      bike_insurance: 'Bicycle insurance',
      electronics_insurance: 'Electronics insurance',
      liability_insurance: 'Liability insurance',
      vaccination: 'Vaccination card',
      ticket: 'Ticket',
      booking: 'Booking',
      other: 'Other',
    },
    add: 'Add a document',
    change: 'Change the document',
    what: 'Type',
    name: 'Name',
    note: 'Note',
    valid: 'Validity period',
    validFrom: 'Valid from',
    validTo: 'Valid to',
    validSince: 'valid from {{day}}',
    validUntil: 'valid to {{day}}',
    validReversed: 'The last valid day is before the first',
    price: 'Price',
    stop: 'Stop',
    noStop: 'No stop',
    private: 'Only I see it',
    file: 'File',
    attach: 'Attach a file',
    replace: 'Replace the file',
    removeFile: 'Remove the file',
    attached: '{{name}} attached',
    kept: 'A file is kept',
    save: 'Save',
    failed: 'Saving failed. Try again.',
    before: 'Before you leave',
    beforeIn_zero: 'Before you leave · today',
    beforeIn_one: 'Before you leave · 1 day',
    beforeIn_other: 'Before you leave · {{count}} days',
    newItem: 'New task',
    addItem: 'Add',
    usual: 'Add the usual ones',
    usualItems: {
      bank: 'Tell the bank about the trip',
      card: 'Order a fee-free travel card',
      maps: 'Download offline maps',
      esim: 'Get an eSIM or a local SIM',
    },
    removeItem: 'Remove {{text}}',
    reminders: 'Before the border',
    remindersLink: 'Open the documents',
    reminderLine: '{{todo}} for {{country}} · {{due}}',
  },
  activity: {
    title: 'Changes',
    online: 'Looking at this trip now: {{names}}',
    none: 'No changes yet.',
    someone: 'Someone',
    changed: '{{name}} made a change',
    code: {
      trip_change: '{{name}} changed the trip',
      route_add: '{{name}} added to the route',
      route_change: '{{name}} changed the route',
      areas_arrive: '{{name}} arrived: {{about}}',
      areas_leave: '{{name}} left: {{about}}',
      areas_change: '{{name}} changed the areas',
      route_compare: '{{name}} compared routes',
      bookings_add: '{{name}} added a booking',
      bookings_change: '{{name}} changed the bookings',
      travellers_change: '{{name}} changed the travel companions',
      people_change: '{{name}} changed the trip’s members',
      costs_add: '{{name}} added a shared cost',
      costs_settle: '{{name}} settled up',
      costs_bill: '{{name}} added a bill photo',
      costs_change: '{{name}} changed the shared costs',
      share_open: '{{name}} opened the read-only link',
      documents_add: '{{name}} added a document',
      documents_change: '{{name}} changed the documents',
      before_change: '{{name}} changed the list before leaving',
      journal_change: '{{name}} wrote in the journal',
      trip_archive: '{{name}} archived the trip',
      trip_restore: '{{name}} restored the trip',
      day_change: '{{name}} changed a day’s plan',
      car_change: '{{name}} changed the car',
      leave_change: '{{name}} changed the days off work',
      branches_change: '{{name}} changed the trip’s branches',
      mail_add: '{{name}} sent a booking by mail',
      flights_change: 'A flight changed',
    },
  },
  costs: {
    title: 'Shared costs',
    lead: 'What travellers pay for one another: who paid, how it is split, and who owes whom.',
    add: 'Add a cost',
    none: 'No shared costs yet.',
    day: 'Date',
    item: 'Description',
    itemHint: 'e.g. Dinner in Shinjuku',
    paidBy: 'Paid by',
    split: 'Split',
    equally: 'Equally: {{names}}',
    amount: 'Amount',
    currency: 'Currency',
    charged: 'Actual amount',
    chargedHint:
      "What it really came to — the card's charge or the cash it took. Left empty, the day's rate is used.",
    noRate: 'No rate for this money on that day: type the actual amount.',
    bill: 'Receipt',
    total: 'Total',
    remove: 'Remove',
    removeOne: 'Remove {{item}}',
    to: '→ {{name}}',
    settled: 'Paid back',
    showBill: 'View the receipt of {{item}}',
    addBill: 'Add a receipt photo for {{item}}',
    viewBill: 'View',
    addPhoto: 'Add photo',
    balances: 'Balances',
    person: 'Traveller',
    paidSum: 'Paid',
    share: 'Share',
    net: 'Balance',
    settleTitle: 'To settle up',
    pays: '{{from}} pays {{to}} {{amount}}',
    square: 'Nobody owes anybody anything.',
    markSettled: 'Mark as paid',
    allPhotos: 'Every cost has a receipt photo.',
    missing_one: '{{count}} cost has no receipt photo yet.',
    missing_other: '{{count}} costs have no receipt photo yet.',
    newTitle: 'New shared cost',
    sharedWith: 'Split equally between',
    save: 'Add',
    billOf: 'Receipt: {{item}}',
    billAlt: 'Photo of the receipt for {{item}}',
    billKept: 'Paid by {{name}} · the photo is kept on your server until someone deletes it.',
    deleteBill: 'Delete the photo',
    notPhoto: 'That file is not a photo this can read: take a JPEG, PNG or WebP.',
    removeQuestion: 'Remove {{item}}?',
    removeBody: 'It no longer counts in the balances. Its receipt photo is deleted too.',
    removeConfirm: 'Remove',
  },
  group: {
    rolesTitle: 'Roles and permissions',
    manage: 'Manage access',
    access: 'Access',
    can: {
      route: 'Change the route',
      money: 'See the money',
      documents: 'Open shared documents',
      people: 'Invite and share',
    },
    yes: 'yes',
    no: 'no',
    open: 'Group',
    back: 'Back to the trip',
    title: 'Group',
    lead: 'Who travels on this trip, and who can see it.',
    people: 'Travellers',
    roleOf: 'Role of {{name}}',
    remove: 'Take {{name}} off the trip',
    removeShort: 'Remove from trip',
    removeQuestion: 'Take {{name}} off the trip?',
    removeBody: 'They no longer see it. Their account stays.',
    removeConfirm: 'Take off',
    lastOwner: 'A trip keeps at least one owner.',
    invited: 'Invited',
    invitedAs: 'as {{role}} · until {{until}}',
    withdraw: 'Withdraw the invitation to {{email}}',
    withdrawShort: 'Withdraw',
    invite: 'Invite to the trip',
    as: 'Role',
    byMail: 'They get an e-mail with a link to join.',
    noMail: 'The invitation is a link: you send it yourself by e-mail or message.',
    send: 'Send invitation',
    makeInvite: 'Create invitation link',
    sent: 'Invitation e-mailed to {{email}}.',
    sendYourself: 'Send this link to {{email}} yourself. It works once, for 14 days.',
    inviteLink: 'Invitation link',
    copy: 'Copy',
    copied: 'Copied',
    readOnly: 'Read-only link',
    readOnlyLead:
      'Anyone with the link sees the route and the dates; amounts, files and names only as you choose. Booking numbers are always hidden.',
    sharedSince: 'A read-only link is on since {{day}}.',
    readOnlyLink: 'Link to share',
    makeLink: 'Make a read-only link',
    newLink: 'Make a new link',
    turnOff: 'Turn the link off',
  },
  invitation: {
    title: '{{who}} invites you to {{trip}}',
    someone: 'Someone',
    as: 'You join as {{role}}.',
    join: 'Join as {{name}}',
    signInFirst: '{{email}} already has an account: sign in to accept.',
    newAccount: 'Make your account for {{email}} and join.',
    create: 'Make the account and join',
    gone: 'This invitation is no longer good: it was used, withdrawn, or ran out.',
  },
  seatMap: {
    needsAircraft: 'Seat map · {{leg}}: say the aircraft in Flights',
    open: 'Seat map',
    openFor: 'Seat map · {{leg}}',
    whole: 'The aircraft from above, nose up, to scale',
    close: 'The rows around row {{row}}, close',
    exitRow: 'exit row',
    place: '{{seat}}: {{kind}}, {{side}}',
    placeShort: '{{kind}}, {{side}}',
    kind: { window: 'window', middle: 'middle', aisle: 'aisle' },
    side: { left: 'left side', right: 'right side', centre: 'in the middle' },
    placeCentre: '{{seat}}: in the middle',
    windowBeside: 'A window right beside you',
    kindOf: 'Seat {{seat}}',
    kindByMap: '{{kind}}, as the seat map says',
    row: 'Row {{row}}',
    wing: { ahead: 'ahead of the wing', over: 'over the wing', behind: 'behind the wing' },
    byDoor: 'by a door: out among the first',
    noWindow:
      'No window right beside this row: the windows follow the frames, the rows the seat pitch',
    notOnMap: '{{seat}} is not on this layout: check the layout below.',
    unknown: 'No seat map for {{model}} yet.',
    type: 'Aircraft',
    size: 'Size',
    sizeSaid: '{{length}} m long · wingspan {{span}} m',
    body: 'Fuselage',
    bodySaid: '{{width}} m wide · frames every {{frame}} cm, a window between each',
    doors: 'Doors, exits',
    doorsSaid: '{{doors}} pairs of doors · {{exits}} pairs over the wing',
    maker: 'maker',
    layout: 'Layout',
    rows_one: '{{count}} row',
    rows_other: '{{count}} rows',
    source: { own: 'yours', wikipedia: 'Wikipedia', typical: 'typical for the type' },
    own: 'your seat',
    mate: 'fellow traveller',
    window: 'window',
    exit: 'emergency exit',
    note: 'Drawn from the type and the airline’s layout; each airline lays its cabins out its own way. Check-in shows the exact map.',
    correct: 'Correct {{airline}}’s layout',
    layoutLead:
      '{{airline}} · {{model}}. Kept for every {{airline}} flight with this aircraft.',
    classes: 'Seats by class',
    pitch: 'Seat pitch, cm',
    skipRows: 'Rows not numbered',
    exitRows: 'Exit rows',
    layoutWrong: 'Write the seats as C12 Y168, and rows as 11, 12.',
    failed: 'The seat map could not be read or saved. Try again.',
    save: 'Save layout',
    restore: 'Restore from Wikipedia',
  },
  flightApis: {
    title: 'Services',
    lead: 'The services TripPath asks. A flight is looked up by its number and day from them in turn: when one fails, has no key or has used its month, the next is asked; each flight found is kept. Drag them into the order to ask them in.',
    failed: 'The services could not be read or changed. Try again.',
    state: { on: 'In use', off: 'Off', noKey: 'No key' },
    move: 'Move {{name}}: drag it, or press Alt and an arrow',
    key: 'API key',
    saveKey: 'Save key',
    removeKey: 'Remove key',
    signup: 'Get a free key from {{name}}',
    limit: 'Requests a month',
    enabled: 'Ask this service',
    used: 'Today {{day}} · this year {{year}}',
    aMonth: 'a month',
    flights: 'Flights',
    settings: 'Settings: {{name}}',
    showKey: 'Show the key',
    unlimited: 'No monthly limit (∞)',
    save: 'Save',
    cancel: 'Cancel',
    add: 'Add a service',
    addOne: 'Add {{name}}',
    addThis: 'Add',
    remove: 'Remove the service',
    none: 'No service added: flights are not looked up by their number. Add one to start.',
    what: {
      aerodatabox: 'Flights by number and day · RapidAPI',
      airlabs: 'The nearest flight of a number',
      aviationstack: 'Flights by number and day',
    },
    lastError: 'Last error: {{error}}',
    requests: 'Latest requests',
    noRequests: 'No flight looked up yet.',
    at: 'When',
    flight: 'Flight',
    service: 'Service',
    outcome: 'Result',
    cache: 'Cached',
    outcomes: { found: 'Found', none: 'Not found', failed: 'Failed', cached: 'From cache' },
  },
  data: {
    title: 'Data sources',
    lead: 'The reference data this server knows. Each list is refreshed from its source or from a file; the log shows how it went.',
    sets: 'Lists',
    set: 'List',
    records: 'Records',
    source: 'Source',
    updated: 'Updated',
    actions: 'Actions',
    builtIn: 'Built into the release',
    notLoaded: 'Not loaded yet',
    refresh: 'Refresh from source',
    actionsOf: 'Refresh: {{name}}',
    uploadHint: 'CSV, gzip or Excel',
    upload: 'Upload file',
    uploadOf: 'Upload a file of {{name}}',
    failed: 'The refresh could not be started. Try again.',
    ownerOnly: 'Only the account that runs this server sees its data sources.',
    log: 'Log',
    tabs: { lists: 'Lists', services: 'Services', log: 'Log' },
    noRuns: 'No refresh yet.',
    again: 'Try again',
    notTheFile: 'nothing in the file reads as this list — not the file it takes',
    name: {
      airlines: 'Airlines',
      aircraft: 'Aircraft types',
      registrations: 'Aircraft registrations',
      dimensions: 'Aircraft dimensions',
      ships: 'Ships',
      shipping_lines: 'Shipping lines',
      rail_operators: 'Railway companies',
      bus_operators: 'Bus companies',
      train_types: 'Train types',
      stations: 'Stations',
      transit: 'Public transport operators',
      price_levels: 'Price levels',
      europe_price_levels: 'Price levels in Europe',
      car_models: 'Car models',
      car_rentals: 'Car rental companies',
      bike_shares: 'Shared bikes and scooters',
      banknotes: 'Banknotes and coins',
    },
    what: {
      airlines: 'OpenFlights · airlines.dat',
      aircraft: 'OpenFlights · planes.dat',
      registrations:
        'tar1090-db · aircraft.csv.gz — fills in the aircraft from its registration',
      dimensions:
        'FAA Aircraft Characteristics Database · length, wingspan, engines, weight by ICAO code — the seat map to scale',
      ships: 'Wikidata · passenger ships and ferries by IMO number, and who runs each',
      shipping_lines: 'Wikidata · shipping lines, and whoever runs a ferry',
      rail_operators: 'Wikidata · railway companies and train operators',
      bus_operators: 'Wikidata · bus and coach companies',
      train_types: 'Wikidata · train types and who makes them',
      stations: 'trainline-eu/stations · Europe’s stations, each at its place and in its zone',
      transit: 'MobilityData · who runs each public transport timetable (GTFS)',
      price_levels:
        'World Bank ICP 2021 · how dear each country is — hotels, food, transport, leisure — to price one TripPath has no prices of',
      europe_price_levels:
        'Eurostat · price levels in Europe, newer than the World Bank’s, used first there',
      car_models: 'Wikidata · car models and their makes',
      car_rentals: 'Wikidata · car rental companies',
      bike_shares:
        'MobilityData · each shared bike and scooter system’s public GBFS feed — its stations, bikes and prices',
      banknotes:
        'Wikipedia · each currency’s notes and coins in use, from its article’s infobox (CC BY-SA)',
    },
    way: { source: 'from source', file: 'from file' },
    status: { running: 'Running', ok: 'Done', failed: 'Failed' },
    step: {
      fetch: 'Fetching {{source}}',
      fetched: 'Received {{size}}',
      file: 'File {{name}}, {{size}}',
      read_one: 'Read {{n}} record',
      read_other: 'Read {{n}} records',
      kept: 'Saved in {{seconds}} s',
      failed: 'Failed: {{error}}',
      stopped: 'The server stopped before it finished',
    },
  },
  reset: {
    forgot: 'Forgot your password?',
    title: 'Reset your password',
    lead: 'We send you a link to set a new password.',
    email: 'E-mail',
    send: 'Send the link',
    sent: 'If this address has an account, the mail arrives within a few minutes. Check the spam folder too.',
    noMail: 'This server sends no mail. Ask whoever runs it to set a new password for you.',
    back: 'Back to sign-in',
    newTitle: 'New password',
    password: 'New password',
    hint: 'At least 10 characters. A password found in known lists of leaked passwords will not do.',
    strength: 'Password strength: {{said}}',
    strong: { 0: 'too short', 1: 'weak', 2: 'fair', 3: 'good', 4: 'strong' },
    others: 'Sign out of all other devices',
    save: 'Save the password',
    done: 'Your new password is set.',
    signIn: 'Sign in',
    gone: 'This link has ended or was used already.',
    again: 'Ask for a new link',
  },
  account: {
    title: 'Sign in',
    firstTitle: 'Make the first account',
    firstLead: 'This server has no account yet. The first one owns every trip already on it.',
    name: 'Your name',
    email: 'E-mail',
    password: 'Password',
    passwordHint: 'At least 10 characters.',
    create: 'Make the account',
    signIn: 'Sign in',
    signOut: 'Sign out',
    dataSources: 'Data sources',
    menuOf: 'Account: {{name}}',
    wrong: 'That e-mail and password do not match an account.',
    turnedOff: 'This account is turned off. Ask whoever runs this TripPath.',
    shared: 'shared with you · {{role}}',
    role: { owner: 'owner', editor: 'editor', viewer: 'viewer' },
  },
  areas: {
    title: 'Areas',
    lead: 'A circle around a stop or a room. When a traveller’s phone is recording its way and crosses one, everyone on the trip is told. Nobody is followed: only the crossing is said.',
    none: 'No areas yet.',
    around: 'Around',
    aroundPick: 'Choose a stop or a room',
    name: 'Area name',
    radius: 'Radius',
    tell: 'Tell when someone',
    tells: { arrive: 'arrives', leave: 'leaves', both: 'arrives or leaves' },
    told: 'told when someone {{what}}',
    add: 'Add area',
    remove: 'Remove {{name}}',
    removeShort: 'Remove',
    invalid: 'Check the name and the radius.',
    failed: 'The areas could not be saved. Try again.',
  },
  people: {
    menu: 'Users',
    title: 'Users',
    lead: 'Everyone with an account on this TripPath. An account turned off cannot sign in; its trips and costs stay.',
    count: 'Accounts · {{count}}',
    you: 'you',
    first: 'runs this server',
    admin: 'admin',
    off: 'turned off',
    since: 'since {{day}}',
    lastSeen: 'last seen {{day}}',
    neverSeen: 'not signed in now',
    trips_one: '{{count}} trip',
    trips_other: '{{count}} trips',
    twoStep: 'two-step sign-in',
    passkeys_one: '{{count}} passkey',
    passkeys_other: '{{count}} passkeys',
    assistants_one: '{{count}} assistant or key',
    assistants_other: '{{count}} assistants or keys',
    actions: 'Actions for {{name}}',
    makeAdmin: 'Make admin',
    unmakeAdmin: 'Remove admin',
    turnOff: 'Turn off',
    turnOn: 'Turn on',
    signOut: 'Sign out everywhere',
    reset: 'Send a password link',
    resetSent: 'A link to set a new password was mailed to {{email}}.',
    resetLink:
      'This server sends no mail. Pass this link to {{email}}; it works once, for 30 minutes:',
    delete: 'Delete',
    deleteTitle: 'Delete {{name}}?',
    deleteBody: 'The account and the trips only it is on are deleted. This cannot be undone.',
    shared:
      'This account shares trips with others; their costs and bookings are theirs too. Turn it off instead.',
    failed: 'That did not work. Try again.',
    loadFailed: 'The users could not be loaded.',
    invite: 'Invite someone',
    inviteHow: 'They get a link that makes their account; it works once, for 7 days.',
    email: 'E-mail',
    language: 'Language of the mail',
    send: 'Send invitation',
    sentMail: 'The invitation was mailed to {{email}}.',
    sentLink: 'This server sends no mail. Pass this link to {{email}}:',
    waiting: 'Invitations waiting · {{count}}',
    until: 'until {{day}}',
    takeBack: 'Take back',
  },
  join: {
    title: 'Make your TripPath account',
    lead: 'You were invited with {{email}}.',
    gone: 'This invitation is used or has ended. Ask for a new one.',
    create: 'Make account',
  },
  assistant: {
    menu: 'AI assistant',
    title: 'AI assistant',
    lead: 'Your own AI assistant (Claude, ChatGPT or any other that speaks MCP) can read and change your trips as far as you allow. The TripPath server itself calls no AI and pays for none.',
    connect: 'Connecting',
    connectHow:
      'Add this address to your AI assistant’s connections. Signing in and the permissions are asked on TripPath.',
    copy: 'Copy',
    copied: 'Copied',
    connected: 'Connected assistants · {{count}}',
    none: 'No assistant connected yet.',
    since: 'connected {{day}}',
    lastUsed: 'last used {{when}}',
    today: 'today {{time}}',
    uses_one: '{{count}} action',
    uses_other: '{{count}} actions',
    revoke: 'Disconnect',
    revokeKey: 'Delete',
    revokeFailed: 'Could not take it back. Try again.',
    failed: 'Could not load your assistants. Try again.',
    allTrips: 'All trips',
    onlyTrip: 'Only “{{name}}” · {{days}}',
    tripsOf: 'trips: {{trips}}',
    allElse: 'everything else: no',
    scope: {
      trips: 'trips: read',
      plan: 'plan: change',
      bookings: 'bookings: read',
      money: 'money: read and add',
      documents: 'documents: read',
    },
    scopeNo: {
      trips: 'trips: no',
      plan: 'plan: no',
      bookings: 'bookings: no',
      money: 'money: no',
      documents: 'documents: no',
    },
    keys: 'Personal API keys · {{count}}',
    keysHow:
      'For your own scripts: send the key as “Authorization: Bearer …” to the same address.',
    noKeys: 'No keys yet.',
    keyName: 'Key name',
    keyNamePlaceholder: 'Budget sheet',
    makeKey: 'Create key',
    keyMade: 'Your new key. It is shown only now — copy it somewhere safe.',
    keyFailed: 'Name the key and choose at least one permission.',
    trips: 'Trips',
    ask: {
      title: '{{name}} wants access to your TripPath',
      lead: 'You can take this back at any time under Menu → AI assistant.',
      unknown:
        'This assistant is not registered, or it asked to be sent back somewhere it did not say. Start connecting again from the assistant.',
      broken:
        'This link is missing what the assistant must send. Start connecting again from the assistant.',
      deny: 'Deny',
      allow: 'Allow',
      failed: 'Could not give the permission. Try again.',
    },
    give: {
      trips: 'Read trips',
      tripsMore: 'Stops, nights, ways, the budget in total',
      plan: 'Change the plan',
      planMore: 'A stop’s nights; every change is in the change log and can be undone',
      bookings: 'Read bookings',
      bookingsMore: 'Stays and tickets, their prices and whether paid',
      money: 'Costs and debts',
      moneyMore: 'Read and add',
      documents: 'Documents and passport details',
      documentsMore: 'Names, kinds and validity; no files are sent',
    },
    example: {
      title: 'An example conversation in the assistant',
      ask: 'Add 2 nights to Kyoto and tell me what happens to the budget. Is Seoul still visa-free then?',
      read: 'get_trip · Asia 2027',
      preview: 'preview_change · Kyoto 3 → 5 nights',
      says: 'Five nights in Kyoto move Seoul two days on: 19–24 March. The Osaka → Seoul flight is paid and will need rebooking.',
      shown: 'Preview · not saved yet',
      more: '+€230 expected',
      budget: '€2,672 / budget €3,420',
      visa: 'South Korea 5 days / 90 visa-free',
      unused: 'The paid ticket KE 2118 is left unused unless its date is changed.',
      yes: 'Do it.',
      apply: 'apply_change · Kyoto 5 nights · in the history as “Claude (John)”',
      done: 'Done. Kyoto now has 5 nights; in TripPath’s change log you can undo it with one press.',
      twoSteps:
        'Changes go in two steps: the assistant shows a preview and saves only after your “yes”.',
      history:
        'Every action is in the change log with the assistant’s and your name, and shows on everyone’s screen at once.',
      limits:
        'The assistant sees only the trips and parts you allowed; you can take the permission back at any time.',
    },
  },
  security: {
    menu: 'Account and security',
    title: 'Account and security',
    lead: 'Password, passkeys, two-step sign-in and devices.',
    signingIn: 'Signing in',
    password: 'Password',
    changed: 'Changed {{date}}',
    changePassword: 'Change password',
    current: 'Current password',
    newPassword: 'New password',
    newHint: 'At least 10 characters. Your other devices are signed out.',
    wrongCurrent: 'That is not the account’s current password.',
    saved: 'Password changed. Your other devices are signed out.',
    save: 'Save',
    cancel: 'Cancel',
    passkeys_one: 'Passkeys · {{count}}',
    passkeys_other: 'Passkeys · {{count}}',
    passkeysHint:
      'Fingerprint, face, PIN or security key: no password needed, and it counts as the second step too',
    passkeysNeedName:
      'Passkeys need the app at a named address — localhost, or your own domain over https — not an IP address.',
    passkeysNoBrowser: 'This browser does not make passkeys.',
    addPasskey: 'Add passkey',
    passkey: 'Passkey',
    passkeyFailed: 'No passkey was added.',
    passkeyName: 'Name',
    added: 'Added',
    lastUsed: 'Last used',
    neverUsed: 'not used yet',
    rename: 'Rename',
    remove: 'Remove',
    renameTitle: 'Passkey name',
    removeTitle: 'Remove the passkey “{{name}}”',
    removeLead: 'It can no longer be used to sign in.',
    twoStep: 'Two-step sign-in',
    on: 'On',
    off: 'Off',
    app: 'Authenticator app',
    appHint: 'Google Authenticator, Aegis, 1Password, Bitwarden…',
    appSince: 'added {{date}}',
    turnOn: 'Turn on',
    newPhone: 'Move to a new phone',
    backup: 'Backup codes · {{left}} of {{total}} left',
    backupHint: 'For a lost phone. Each code works once.',
    newCodes: 'Make new codes',
    offHint: 'Turning it off asks for the password and a code.',
    turnOff: 'Turn off',
    devices: 'Devices signed in',
    device: 'Device',
    lastSeen: 'Last used',
    how: 'Signed in with',
    thisDevice: 'this device',
    now: 'now',
    byPassword: 'password',
    byCode: 'password + code',
    byPasskey: 'passkey',
    unknownDevice: 'Browser not known',
    signOut: 'Sign out',
    signOutOthers: 'Sign out of all others',
    setupTitle: 'Two-step sign-in',
    phoneTitle: 'A new phone',
    scan: 'Scan the code with the app',
    manual: 'Key to type in by hand:',
    enterCode: 'Enter the app’s 6 digits',
    code: 'Code',
    wrongCode: 'That code does not match. Take the newest one from the app.',
    confirm: 'Confirm',
    keepCodes: 'Keep the backup codes',
    codesLead: 'They are shown only now.',
    download: 'Download .txt',
    copy: 'Copy',
    copied: 'Copied',
    done: 'Done',
    codesTitle: 'New backup codes',
    offTitle: 'Turn two-step sign-in off',
    codeOrBackup: 'Code from the app, or a backup code',
    wrongPassword: 'That is not the account’s password.',
    failed: 'That did not go through. Try again.',
    secondTitle: 'Enter the code from the app',
    trust: 'Trust this device for 30 days',
    or: 'or',
    usePasskey: 'Use a passkey',
    useBackup: 'Use a backup code',
    useApp: 'Use the app’s code',
    backupCode: 'Backup code',
    backupTitle: 'Enter a backup code',
    ticketGone: 'The sign-in ran out of time. Enter the password again.',
    passkeyWrong: 'That passkey did not sign in.',
    passkeyCancelled: 'No passkey was used.',
  },
  view: {
    lead: 'You can see this trip’s route and dates. Its money, bookings and documents stay with its owner and editors.',
    noStops: 'No stops yet.',
    gone: 'This link is turned off, or was never one.',
    undecided: 'not decided yet',
    then: 'then by {{how}}',
    joins: '{{name}} joins {{dates}}',
    traveller: 'Traveller {{n}}',
    leadAmounts:
      'You can see this trip’s route, dates and amounts. Its bookings stay with its owner and editors.',
    amounts: 'Expected {{expected}} of a {{budget}} budget',
    files: 'Files',
    journal: 'Journal',
    ended: 'This link has ended.',
    locked: 'Too many wrong phrases. Try again in 15 minutes.',
    phraseTitle: 'This trip asks for a phrase',
    phraseLead:
      'Whoever shared the link told you its phrase separately: by voice, or in another message.',
    phrase: 'Phrase',
    phraseWrong: 'That is not the phrase.',
    open: 'Open the trip',
  },
  sights: {
    title: 'Along the road',
    back: 'Back to the plan',
    way: 'Way: {{from}} → {{to}} · {{how}}',
    by: {
      car: 'by car',
      bus: 'by bus',
      train: 'by train',
      foot: 'on foot',
      flight: 'by air',
      ferry: 'by ferry',
    },
    km: '{{km}} km',
    hours: '{{hours}} h {{minutes}} min',
    minutes: '{{minutes}} min',
    detour: 'Detour',
    upTo10: 'up to 10 min',
    upTo30: 'up to 30 min',
    upTo60: 'up to 1 h',
    kind: 'Kind',
    kinds: {
      view: 'Views',
      nature: 'Nature',
      town: 'Towns',
      food: 'Food',
      fuel: 'Fuel and charging',
    },
    source: { wikivoyage: 'Wikivoyage', wikipedia: 'Wikipedia', osm: 'OSM' },
    score: 'Importance {{n}} / 5',
    onWay: 'on the route',
    plus: '+{{minutes}} min',
    pick: 'Pick {{name}}',
    map: 'Map: the road and its sights',
    none: 'None of these near the road.',
    failed: 'The sights could not be had; try again later.',
    noRoad: 'Sights are found for a way by road between two stops on the map.',
    howTitle: 'Importance and sources',
    howLead: 'No rating is copied from reviews. Importance is worked out from open data:',
    how1: 'in how many languages Wikipedia has an article on it (Wikidata sitelinks);',
    how2: 'how much its article is read (Wikimedia pageviews, a public API);',
    how3: 'whether Wikivoyage has it;',
    how4: 'its OSM tags: tourism=viewpoint, heritage, natural=peak.',
    detourNote: 'Detour: OSRM driving time from the road to the place and back.',
    add: 'Add the picked as stops of the way',
    addFailed: 'The stops could not be added. Try again.',
    link: 'Sights along the road',
  },
  journal: {
    title: 'Travel journal',
    lead: 'Days come from the plan, places from the photos, words from you. No GPS kept running: the battery lasts.',
    notYet: 'The journal opens on the trip’s first day, {{day}}.',
    stop: '{{city}} · {{dates}}',
    addPhotos: 'Add photos',
    unwritten: 'Not written',
    fromPlan: 'From the plan: {{things}}.',
    photos_one: '{{count}} photo of the day.',
    photos_other: '{{count}} photos of the day.',
    write: 'Write',
    edit: 'Edit',
    titleField: 'Title',
    bodyField: 'What happened',
    save: 'Save',
    cancel: 'Cancel',
    here: 'I am here',
    hereFailed: 'Your location could not be had.',
    places_one: '{{count}} place from photos',
    places_other: '{{count}} places from photos',
    spent: 'spent {{amount}}',
    way: '{{how}} {{minutes}} min',
    photoAt: 'Photo, {{at}} · {{place}}',
    photo: 'Photo',
    remove: 'Remove {{name}}',
    mapTitle: 'Route from photos',
    howTitle: 'Where, without GPS tracking',
    how1: 'The photo’s own place and time (EXIF), when the phone kept them.',
    how2: 'Without them, the place of the day’s plan whose time fits the photo.',
    how3: '“I am here”: one place, when you want it, never tracking.',
    howNote: 'Photos are made 1600 px WebP, as bills are; the originals stay on the phone.',
    shareTitle: 'Sharing and book',
    share: 'Show the journal on the read-only link, without amounts',
    noLink: 'Make a read-only link first, on the sharing page.',
    bookLead:
      'A PDF book: cover, map, each day on its own page with its photos and text. For a print shop on A5 or A4, with bleed.',
    paper: 'Paper',
    bleed: 'With bleed (3 mm)',
    book: 'Make the book',
    failed: 'The journal could not be saved. Try again.',
  },
  share: {
    unknown:
      'This link was made before links were kept to copy again. Make a new link to copy it.',
    views: 'Viewed',
    times_one: '{{count}} time',
    times_other: '{{count}} times',
    last: 'Last',
    until: 'Works until',
    noEnd: 'no end',
    choice: {
      expires: 'Ends by itself',
      phrase: 'Ask for a phrase',
      amounts: 'Show amounts',
      files: 'Show files',
      names: 'Show fellow travellers’ names',
      journal: 'Show the journal',
    },
    about: {
      expires: 'A week after the trip ends, on {{when}} (your home time)',
      phrase: 'Say the phrase by voice or in another message; it does not travel with the link',
      amounts: 'When off, the page shows no price and no debt',
      files: 'Tickets and confirmations; booking numbers are always hidden',
      names: 'When off: “Traveller 2”, “Traveller 3”',
      journal: 'The days written and their photos, without what was spent',
    },
    phraseLabel: 'Phrase',
    stop: 'Stop sharing',
    renew: 'Make a new link',
    renewNote:
      '“Make a new link” gives a new address and a new phrase; the old link then says “This link is turned off”.',
    log: 'Views',
    none: 'Nobody has opened the link yet.',
    device: { phone: 'phone', tablet: 'tablet', computer: 'computer' },
    outcome: { open: 'opened', ok: 'phrase right' },
    wrong_one: 'phrase wrong once',
    wrong_other: 'phrase wrong {{count}} times',
    logNote:
      'The town comes roughly from the IP address; the address itself is not kept. After 5 wrong phrases the link is locked for 15 minutes.',
  },
  plan: {
    route: 'Route',
    undecidedHint: 'Not decided yet: open it to pick the place, or drag it where it goes.',
    openStop: 'Open {{city}}',
    removeStop: 'Remove {{city}}',
    showVariant: 'Show the variant {{name}}',
    showPlan: 'Show the plan as it is',
    addStop: 'Add stop',
    noStops: 'No stops yet. Add the first one.',
    budget: 'Trip vs budget',
    reckoned: 'Reckoned for',
    wholeTrip: 'Whole trip',
    perPerson_one: 'Per person',
    perPerson_other: 'Per person ({{count}})',
    min: 'Min',
    expected: 'Expected',
    max: 'Max',
    paid: 'Paid',
    toPay: 'Booked, to pay',
    estimated: 'Estimated',
    budgetIs: 'budget {{amount}}',
    over: 'Max exceeds budget by {{amount}}.',
    share: '{{country}} = {{percent}} of total.',
    countries: 'Countries',
    country: 'Country',
    days: 'Days',
    perDay: 'Per day',
    aDay: '{{amount}} a day',
    total: 'Total',
    edit: 'Edit trip',
    delete: 'Delete trip',
    deleteQuestion: 'Delete “{{name}}”?',
    deleteBody: 'Its stops go with it. This cannot be undone.',
    deleteConfirm: 'Delete',
    failed: 'Saving failed. Try again.',
    moveUp: 'Move {{city}} up',
    moveDown: 'Move {{city}} down',
    openNights_one: '{{count}} night without a bed',
    openNights_other: '{{count}} nights without a bed',
    cashNeeded: 'cash still needed {{amount}}',
    lost: 'lost {{amount}}',
    refundsPending: 'to come back {{amount}}',
    stats: 'The trip in numbers',
    statDays: 'days · {{from}} – {{to}}',
    statAverage: 'avg / day',
    statPlaces: 'countries · cities',
    statBooked: 'booked · {{paid}} paid',
    visaTitle: 'Visa-free days',
    visaPassport: 'Your passport',
    visaNone: 'No country on this route limits your days.',
    visaNote: 'Indicative only — check official sources before travel.',
    validTo: 'valid to {{day}}',
    daysShort_one: '{{count}} d',
    daysShort_other: '{{count}} d',
    perDayShortAmount: '{{amount}}/d',
    stopOf: '{{when}} · {{days}}',
    placeOf: 'In the variant {{name}} · {{when}} · {{days}}',
    way: '{{mode}} {{from}} → {{to}}',
    leaves: 'Departure',
    arrives: 'Arrival',
    times: '{{leaves}} → {{arrives}}',
    wayHere: 'Arrival',
    hotel: 'Hotel',
    ticket: 'Ticket in mind',
    link: 'Link',
    hotelName: 'Hotel name',
    stayWhole_one: 'Prices for the whole stay, {{count}} night',
    stayWhole_other: 'Prices for the whole stay, {{count}} nights',
    stayPrice: 'Price for the stay',
    found: 'Found: {{name}}',
    addHotel: '+ Hotel',
    pasteHotel: 'Paste a hotel’s link…',
    readingLink: 'Reading the link…',
    removeHotel: 'Remove {{name}}',
    chooseHotel: 'Staying here: {{name}}',
    moreHotels_one: '(+{{count}} more in mind)',
    moreHotels_other: '(+{{count}} more in mind)',
    activitiesShow: '+ Activities',
    changeBed: 'Change a night at {{city}}',
    changeDaily: 'Change food, local transport and activities at {{city}}',
    changeWay: 'Change the price and times of {{way}}',
    changeDays: 'Change the days at {{city}}',
    fuel: '{{km}} km · {{litres}} l × {{price}}/l',
    charge: '{{km}} km · {{litres}} kWh × {{price}}/kWh',
    fuelEdit: 'Car…',
    addTicket: 'Add a ticket',
    ticketBooked: 'ticket booked',
    ticketLink: 'ticket',
    transferShort: 'change',
    ends: 'Start and destination',
    endFrom: 'Leaves from',
    endTo: 'Arrives at',
    endPlace: 'A station, an address',
    howOn: 'Choose transport',
    wayOpen: 'not decided',
    changeWayMode: 'Another way',
    variantUsed: 'The variant is in the plan.',
    undo: 'Undo',
    addRental: 'Add a car rental',
    showTotals: 'Show what each costs in all',
    showPerDay: 'Show a day’s cost',
    hotels: 'Hotels in mind',
    file: 'PDF',
    openFile: 'Open in a new tab',
    change: 'Change',
    changeBooking: 'Change {{name}}',
    held: 'The ticket {{from}} → {{city}} is booked: {{city}} stays where it is',
    hours: 'in {{checkIn}}, out {{checkOut}}',
    editDays: 'Days at {{city}}',
    daysUnit: 'd',
    daysHint: 'Double-click to change the days',
    gap_one: 'Unplanned · {{count}} day',
    gap_other: 'Unplanned · {{count}} days',
    gapBetween: '{{from}} → {{to}}',
    gapAdd: '+ Add a stop',
    gapLonger: 'Stay longer in {{city}}',
    gapLongerShort: 'Stay longer',
    transferHint: 'A change on the way: no days of its own, passed through',
    editStop: 'Edit stop',
    addStay: 'Add a stay',
    stopMenu: 'Stop actions',
    stopCosts: '{{city}}: bookings and estimates',
    date: 'Date',
    item: 'Cost',
    price: 'Price',
    perDayShort: '/ day',
    status: 'Status',
    nightsShort_one: '{{count}} d',
    nightsShort_other: '{{count}} d',
    payPaid: 'paid',
    payLater: 'pay at hotel',
    payLaterTicket: 'not paid yet',
    openRow_one: '{{count}} night · {{range}}/night',
    openRow_other: '{{count}} nights · {{range}}/night',
    find: 'Find',
    open: 'Not booked',
    plannedRow_one: '1 thing to do planned',
    plannedRow_other: '{{count}} things to do planned',
    estimate: {
      food: 'Food a day',
      local: 'Local transport a day',
      activities: 'Activities',
    },
    perDaySuffix: '/d',
    est: 'est.',
    changeVia: '{{via}} → {{to}}',
    inJourney: 'part of the journey {{from}} → {{to}}',
    pricedAt: 'included in {{city}}’s price',
    stopSums: 'Paid {{paid}} · to pay {{toPay}} · estimate {{estimate}}',
    perDayLabel: 'per day',
    stopWidget: 'Stop or way',
    nothingPicked: 'Pick a stop or a way on the map, in the route or on the timeline.',
    related: 'Before and after',
    stopTitle: 'Stop: {{city}}, {{country}}',
    placeTitle: 'Variant: {{city}}, {{country}}',
    runWas: 'In the plan: {{days}} · {{amount}}',
    moreWidget: 'Documents, who joins',
    fromLink: 'from its link',
    openLink: 'Open ↗',
    added: 'Added: {{cities}}',
    byHand: 'Add a stop by hand',
  },
  stopStatus: {
    booked: 'Booked',
    expensive: 'Expensive',
    estimate: 'Estimate',
    unresolved: 'Not decided',
  },
  stopForm: {
    spot: 'Exact place',
    spotHint: 'An address, a bus station, an airport, or GPS: 35.55, 139.78',
    spotNote: 'Place in town: the map draws the stop there, and a way by road starts there.',
    spotFirst: 'Pick the town first.',
    spotClear: 'Back to the town, not {{name}}',
    spots: 'Places found',
    noSpots: 'Nothing found by that name near the town.',
    spotFailed: 'OpenStreetMap could not be reached; try again later.',
    spotOff: 'Searching places is turned off on this server.',
    newTitle: 'New stop',
    editTitle: 'Edit {{city}}',
    city: 'City or place',
    towns: 'Places found',
    airport: 'has an airport',
    onMap: 'On the map at the place picked.',
    byAirport: 'On the map by its airport. Pick a place to put it exactly.',
    offMap: 'Not on the map yet: type a name and pick a place.',
    noTowns: 'No place by that name. The stop can still be saved.',
    osmFailed: 'OpenStreetMap could not be reached; only cities with an airport are offered.',
    country: 'Country',
    days: 'Days',
    accommodation: 'Accommodation per night',
    accommodationBooked: 'booked · {{price}} a night on average',
    shownIn: 'Prices shown in',
    food: 'Food per day',
    local: 'Local transport per day',
    activities: 'Activities in total',
    costs: 'Estimated costs',
    out: 'Then by',
    outNone: 'Not set',
    estimate: 'Transport cost',
    booked: 'A ticket is booked for this way: remove it in Bookings to change how it goes.',
    estimatedBy: {
      eurostat: 'Prices estimated from Eurostat’s price levels: change them to what you know.',
      icp: 'Prices estimated from the World Bank’s price levels (ICP 2021): change them to what you know.',
    },
    daysHeld_one: '{{count}} day: the tickets here and on are bought',
    daysHeld_other: '{{count}} days: the tickets here and on are bought',
    unresolved: 'Not decided yet',
    transfer: 'A change on the way: passed through, no night',
    add: 'Add stop',
    save: 'Save',
    remove: 'Remove stop',
    removeQuestion: 'Remove {{city}}?',
    removeBody: 'Its days and costs leave the plan.',
    removeConfirm: 'Remove',
  },
  mode: {
    flight: 'Flight',
    train: 'Train',
    bus: 'Bus',
    ferry: 'Ferry',
    car: 'Car',
    foot: 'On foot',
  },
  way: {
    widget: 'The way: {{from}} → {{to}}',
    took: 'Takes',
    distance: 'Distance',
    checkIn: 'Check-in opens',
    home: 'At home: {{at}} ({{zone}})',
    waitHere: '{{city}} · {{wait}} to wait',
    changeAt: 'Change at {{city}}',
    notChosen: 'Onward transport not chosen yet.',
  },
  journeys: {
    title: 'Journeys',
    none: 'No tickets yet. A ticket shows here with its legs: when and where it goes, its seats and gates, and what it allows.',
    ticket: 'Date',
    route: 'Route',
    times: 'Departure – arrival',
    leaves: 'Departure',
    arrives: 'Arrival',
    clock: 'Times by',
    went: 'Times',
    plan: 'Planned',
    real: 'As it went',
    local: 'Local time',
    gates: 'Gates',
    openLeg: 'Open {{leg}} on its ticket',
    terminal: 'Terminal {{at}}',
    flight: 'Service',
    aircraft: 'Vehicle',
    seats: 'Seats',
    rules: 'Baggage, check-in',
    numbers: '{{first}}–{{last}}',
    open: 'Open the ticket {{name}}',
    pnr: 'PNR {{ref}}',
    reference: 'Ref. {{ref}}',
    km: '{{km}} km',
    home: 'At home',
    homeHint: 'At home, {{zone}}',
    gateBy: {
      flight: 'Gate {{gate}}',
      train: 'Platform {{gate}}',
      bus: 'Platform {{gate}}',
      ferry: 'Berth {{gate}}',
      car: 'Gate {{gate}}',
      foot: 'Start {{gate}}',
      none: 'Gate {{gate}}',
    },
    coachSeat: 'coach {{coach}}, {{seat}}',
    coach: 'coach {{coach}}',
    carried: '{{kind}} aboard',
    delay: '{{by}} min against the plan',
    zones: '{{from}} · {{to}} · home {{home}}',
    legsOf: 'Legs {{numbers}}',
    totals: {
      legs: 'Legs',
      km: 'Kilometres',
      onTheWay: 'Total travel time',
      late: 'Late',
      lateBy: '{{minutes}} min on average',
      carriers: 'Carriers',
    },
    boardBy: {
      bridge: 'Boarding by air bridge',
      stairs: 'Boarding by stairs',
      bus: 'Boarding by bus',
    },
    offBy: { bridge: 'Off by air bridge', stairs: 'Off by stairs', bus: 'Off by bus' },
    terminalFrom: 'Leaves {{at}}',
    terminalTo: 'Arrives {{at}}',
    terminals: '{{from}} → {{to}}',
    cabin: 'Cabin {{what}}',
    pieces_one: '{{count}} piece',
    pieces_other: '{{count}} pieces',
    kg: '{{kg}} kg',
    personal: '+ personal item',
    checked: 'Checked {{kg}} kg',
    online: 'Online check-in {{span}}',
    desk: 'Desk {{span}}',
    opens: 'from {{at}}',
    closes: 'until {{at}}',
    hoursBefore_one: '{{count}} h before',
    hoursBefore_other: '{{count}} h before',
    minutesBefore_one: '{{count}} min before',
    minutesBefore_other: '{{count}} min before',
  },
  bookings: {
    fromMail: 'Add from mail',
    journey: 'Journey',
    estimateCol: 'Estimate',
    view: { all: 'All', stays: 'Stays', transport: 'Transport' },
    views: 'Show',
    title: 'Bookings',
    open: 'Bookings',
    add: 'Add booking',
    none: 'No bookings yet.',
    paid: 'Paid',
    later: 'Pay on arrival',
    estimated: 'Still estimated',
    day: 'Day',
    what: 'Booking',
    stop: 'Stop',
    nights: 'Nights',
    price: 'Price',
    payment: 'Payment',
    noDay: '—',
    edit: 'Edit {{name}}',
    gaps: 'Nights without a bed',
    gap_one: '{{city}}: {{count}} night',
    gap_other: '{{city}}: {{count}} nights',
    noGaps: 'Every night has a bed.',
    ticket: 'Ticket',
    stay: 'Stay',
    type: 'Type a booking or paste a link',
    typeHint:
      'For example: 25.10 BKK-SYD 160 · Lub d Siam 20.10 5n 100 later · a Booking.com, Agoda, Airbnb, Google Flights or 12Go link',
    read: 'Read',
    readFailed: 'That line could not be read. Try again.',
    drop: 'Drop a confirmation PDF or a photo here',
    choose: 'Choose a file',
    dropHint: 'Hotel, flight, train or bus confirmations. Nothing is added until you check it.',
    pdfFailed: 'That file could not be read: it is not a PDF or a photo.',
    noOcr: 'Photos and scans are not read on this server: its OCR models are not installed.',
    reading: 'Reading the file…',
    lead: 'Paste a link, TripPath reads the price and dates',
    bookedSoFar: 'Booked so far',
    pasteTitle: 'Paste a link or a line',
    openLink: 'Open the page of {{name}}',
    typePlaceholder: 'https://www.booking.com/… or 25.10 BKK-SYD 160',
    count_one: 'Bookings · {{count}}',
    count_other: 'Bookings · {{count}}',
    groupBy: 'Group by',
    by: { date: 'Date', city: 'City', country: 'Country' },
    perDay: 'Per day',
    groupBooked: '{{amount}} booked',
    bookStay: 'Book a stay in {{city}}',
    nightsCount_one: '{{count}} night',
    nightsCount_other: '{{count}} nights',
    gapNights_one: '{{count}} night not booked',
    gapNights_other: '{{count}} nights not booked',
    wayOpen: 'not booked',
    waysOpen_one: '{{count}} journey not booked',
    waysOpen_other: '{{count}} journeys not booked',
    fromBookings: 'from your bookings',
    tickets_one: '{{count}} ticket',
    tickets_other: '{{count}} tickets',
    soFar: 'So far · {{from}} – {{to}}',
    booked: 'Booked',
    perDayDays_one: 'Per day, {{count}} day',
    perDayDays_other: 'Per day, {{count}} days',
  },
  timeline: {
    sunriseOnWay: 'Sunrise seen from the plane at {{time}} · {{where}}',
    sunsetOnWay: 'Sunset seen from the plane at {{time}} · {{where}}',
    play: {
      group: 'Play the trip',
      play: 'Play',
      pause: 'Pause',
      speed: 'Speed',
      x60: '1 min/s',
      x600: '10 min/s',
      x3600: '1 h/s',
      moment: 'Time shown on the map',
    },
    title: 'Timeline',
    zoom: 'Zoom',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    zooms: { month: 'Month', week: 'Week', day: 'Day', hour: 'Hour', minute: 'Minute' },
    fit: 'Whole trip',
    fitPicked: 'Selection',
    clock: 'Time',
    local: 'Local',
    gaps_one: '{{count}} gap',
    gaps_other: '{{count}} gaps',
    gapShort: '{{count}} d',
    nextGap: 'To the next gap',
    clockAt: 'Read in the time of {{city}}',
    inAir: 'on the way',
    night: 'night',
    block: '{{city}}, {{from}}–{{to}}, {{days}}',
    keys: 'Drag it along the way to move it, its end to stretch it. ← → a day, Shift ← → another day, Alt ← → its place.',
    moved: '{{city}} moved',
    shifted: '{{city}} from {{date}}',
    heldByTicket: '{{from}} → {{to}}: its ticket is bought, its time stays.',
    today: 'today',
  },
  travellers: {
    title: 'Travelling with you',
    add: 'Add a traveller',
    none: 'Add a companion without an account to pick them as a traveller on a ticket.',
    onTickets_one: 'on {{count}} ticket',
    onTickets_other: 'on {{count}} tickets',
    noTicket: 'on no ticket yet',
    edit: 'Edit {{name}}',
    editShort: 'Edit',
    show: '{{name}}’s legs',
    together: '{{name}} is with you',
    planner: 'Leave planner · {{name}}',
    stay: 'Joins {{city}} {{dates}}',
    stayElsewhere: 'With you {{dates}}',
    leaveDays_one: '{{count}} day of leave',
    leaveDays_other: '{{count}} days of leave',
    holidays_one:
      '{{count}} Estonian public holiday falls inside the stay, so {{days}} calendar days cost {{leave}} leave days.',
    holidays_other:
      '{{count}} Estonian public holidays fall inside the stay, so {{days}} calendar days cost {{leave}} leave days.',
    noHolidays: '{{days}} calendar days, weekends free, cost {{leave}} leave days.',
    own: 'Their own leg',
  },
  holiday: {
    new_year: 'New Year’s Day',
    independence: 'Independence Day',
    good_friday: 'Good Friday',
    easter: 'Easter Sunday',
    spring: 'Spring Day',
    pentecost: 'Pentecost',
    victory: 'Victory Day',
    midsummer: 'Midsummer Day',
    restoration: 'Restoration of Independence Day',
    christmas_eve: 'Christmas Eve',
    christmas: 'Christmas Day',
    boxing_day: 'Boxing Day',
  },
  travellerForm: {
    newTitle: 'New traveller',
    editTitle: 'Edit {{name}}',
    name: 'Name',
    add: 'Add traveller',
    save: 'Save',
    remove: 'Remove traveller',
    removeQuestion: 'Remove {{name}}?',
    removeBody: 'Your own plan does not change.',
    removeConfirm: 'Remove',
  },
  routes: {
    order: 'Order of the paths',
    byPrice: 'By price',
    byCo2: 'By CO₂',
    co2: '{{kg}} kg CO₂e',
    title: 'Compare routes',
    open: 'Compare routes',
    to: 'to',
    readTitle: 'Read',
    pathsFound: 'Paths found',
    total: 'Total',
    ifPickedTitle: 'Effect of this choice',
    howTitle: 'How paths are priced',
    saved: 'Saved comparisons',
    noLegs: 'A trip needs two stops to have a leg to compare.',
    leg: 'Leg {{number}} of {{count}} · leave {{day}}',
    planned: 'the plan estimates {{amount}}',
    legLabel: 'Leg',
    chosenShort: 'chosen {{path}}',
    options_one: '{{count}} flight found',
    options_other: '{{count}} flights found',
    empty: 'empty',
    found: 'Options you found',
    textLabel: 'Flights and stopover hotels',
    hint: 'One per line: FROM-TO price, like PEN-KUL 25, or a Google Flights link (its fare is read, or type one after it). Add “paid” to lock a ticket bought; “MNL hotel 35” for a stopover night, “x2” for two.',
    openLink: 'Open the booking page ↗',
    fromLink: 'from a link',
    changes: 'Changes',
    changesUpTo_zero: 'Direct only',
    changesUpTo_one: 'Up to {{count}} change',
    changesUpTo_other: 'Up to {{count}} changes',
    noneRead: 'No flights were read from what is pasted.',
    noneFrom: 'None of these flights leaves {{from}}.',
    noneTo: 'None of these flights reaches {{to}}.',
    intoStart: 'One flies into {{from}}: it may belong to the leg before, the one to {{from}}.',
    noJoin:
      'These flights do not join up from {{from}} to {{to}}. Allow more changes, or add the flight between them. Read: {{flights}}.',
    read: 'Read',
    flights: 'Flights',
    hotels: 'Stopover hotels',
    paid: 'Paid, locked',
    nonePaid: 'none',
    notRead: 'Lines not read',
    unread: 'Line {{line}}: “{{text}}” is not a flight or a hotel.',
    unknownCode: 'Line {{line}}: “{{text}}” has an airport code not in the list.',
    how: 'Every chain of flights from here to {{to}} is priced: flights, stopover hotels, and any paid ticket it leaves unused. A stopover night is a night less in {{to}}.',
    paths_one: '{{count}} path',
    paths_other: '{{count}} paths',
    showSkipping: 'Show paths that skip a paid ticket',
    start: 'Paste the flights you found, then Read.',
    cheapest: 'cheapest',
    fewest: 'fewest flights',
    skipsPaid: 'skips a paid ticket',
    breakdown_one: '{{count}} flight {{flights}}',
    breakdown_other: '{{count}} flights {{flights}}',
    stopover_one: '{{city}} hotel {{cost}}, {{count}} night',
    stopover_other: '{{city}} hotel {{cost}}, {{count}} nights',
    lost: '{{amount}} paid and unused',
    ifPicked: 'If you pick {{path}}',
    thisLeg: 'This leg',
    nightsAt: 'Nights in {{city}}',
    nightsOf: '{{left}} of {{of}}',
    tripExpected: 'Trip expected',
    versusPlan: 'Against the plan',
    inPlan: 'This path is in the plan.',
    use: 'Use this path in the plan',
    used: 'Put into the plan.',
  },
  map: {
    title: 'Map',
    legend: 'Ways on the map',
    kindKm: '{{kind}} · {{km}} km',
    label: 'Map: {{places}}',
    empty: 'Map: no places yet',
    missing: 'Not on the map: {{cities}}. Edit the stop and pick its place.',
    look: 'Map style',
    lookTitle: 'Map style',
    style: { standard: 'Standard', light: 'Light', topo: 'Topo' },
    relief: 'Hill shading',
    terrain: '3D terrain',
    rails: 'Metro lines',
    heightsOf:
      '{{name}} by its height: light near the ground, darker the higher, to {{top}} km',
    heightsLook: 'Height',
    heightsLookHint:
      'Each way drawn by its height: a flight by its altitude from its file, a way on the ground by the ground under it.',
    sky: {
      night: 'Day and night',
      nightHint:
        'Night and its twilights at the time on the timeline, the sun and the moon where each is overhead.',
      vehicle: 'On the way',
      vehicleHint:
        'At the time on the timeline, the plane, train, bus, ship, car or walk then under way, moving along its way.',
      trail: 'Way so far',
      trailHint: 'The part of the way under way already gone, drawn behind it.',
    },
    locate: 'My location',
    north: 'North up',
    here: 'Your location, ±{{metres}} m',
    hereHow:
      'Asked once, when you pressed the button. Your location is not kept or sent to anyone; it is gone when the page closes.',
    notHere: 'Your location could not be found.',
    kind: {
      flight: 'Flight',
      change: 'Flight with a change',
      draft: 'Draft, not added',
      variant: 'Variant shown',
      train: 'Train',
      bus: 'Bus',
      ferry: 'Ferry',
      car: 'Car',
      foot: 'On foot',
      travel: 'Way not planned',
      other: 'Other path',
      walk: 'On foot',
      metro: 'Metro',
      tram: 'Tram',
      taxi: 'Taxi',
      bike: 'Bike',
      cable: 'Cable car',
    },
  },
  search: {
    label: 'Search places or a route',
    town: 'Town without an airport',
    clear: 'Clear the search',
    didYouMean: 'Did you mean',
    places: 'Places',
    airports_one: '{{city}} has {{count}} airport',
    airports_other: '{{city}} has {{count}} airports',
    from: 'From {{city}}, likely direct',
    hintNone: 'One place was not found. Check the spelling or use the airport code.',
    km: '{{km}} km',
    notEuro:
      'The estimates are in euros and this trip is in {{currency}}; adding waits for exchange rates.',
    toPlan: 'Back to the plan',
    where: 'Search a destination: HEL-BKK, a city or a link',
    draft: 'Draft route',
    fromLink: 'Flights in the link',
    fromLinkFare: 'fare from the link',
    inPlan: 'in the plan',
    firstStop: 'where the trip starts',
    withFerry: 'incl. {{km}} km by car ferry',
    notAdded: 'the start, not added',
    way: 'Route {{from}} → {{to}}',
    nightsAt: 'Nights in {{city}}',
    fewer: 'One night less in {{city}}',
    more: 'One night more in {{city}}',
    nights_one: '{{count}} night',
    nights_other: '{{count}} nights',
    travel: 'Travel, estimated',
    typicalNote:
      'Each place starts with the country’s typical prices for a bed, food and getting around; change them on the stop.',
    addToPlan: 'Add to the plan',
  },
  stays: {
    none: 'No bed booked yet.',
    nights_one: '{{count}} night',
    nights_other: '{{count}} nights',
    change: 'Change',
    changeOne: 'Change {{name}}',
    remove: 'Remove',
    removeOne: 'Remove {{name}}',
    add: '+ Add a stay',
    removeQuestion: 'Remove {{name}}?',
    removeBody: 'The booking is removed from the trip; its nights need booking again.',
    open: 'Beds booked in {{city}}',
  },
  local: {
    calendar: 'Calendar',
    system: {
      buddhist: 'Buddhist',
      japanese: 'Japanese eras',
      roc: 'Minguo',
      persian: 'Persian (Solar Hijri)',
      ethiopic: 'Ethiopian',
      indian: 'Indian national (Saka)',
      hebrew: 'Hebrew',
      'islamic-umalqura': 'Islamic (Hijri)',
      chinese: 'Lunar',
      dangi: 'Korean lunar',
      gregory: 'Gregorian',
    },
    religious: 'for its feasts',
    year: 'Year',
    yearIs: '{{local}} = {{ours}}',
    date: 'Date there',
    weekend: 'Weekend',
    day: 'Local calendar',
    lunarDay: 'Day {{day}} of month {{month}}',
    lunarMonth: 'Month {{month}}',
    animalYear: 'Year of the {{animal}}',
    animal: {
      '0': 'Rat',
      '1': 'Ox',
      '2': 'Tiger',
      '3': 'Rabbit',
      '4': 'Dragon',
      '5': 'Snake',
      '6': 'Horse',
      '7': 'Goat',
      '8': 'Monkey',
      '9': 'Rooster',
      '10': 'Dog',
      '11': 'Pig',
    },
    newMoon: 'New moon',
    fullMoon: 'Full moon',
    view: 'Calendar',
  },
  clocks: {
    title: 'Clocks',
    home: 'Working hours at home · {{city}}',
    from: 'Starts',
    to: 'Ends',
    at: 'Time in {{city}}',
    place: 'Place',
    dates: 'Dates',
    clock: 'Clock',
    workThere: 'Working hours',
    everywhere: 'Working hours in each place',
    none: 'No place of the trip has its time zone yet.',
    open: 'Clocks',
    date: 'From',
    timeAt: 'Time in {{place}}',
    working: 'Include working hours',
    order: 'The working day starts before it ends.',
    timeWrong: 'A time such as 9:00 or 17:30',
    zones: 'Time zones of {{country}}',
    part: {
      day: 'Work by day',
      evening: 'Work early or in the evening',
      late: 'Work late in the evening',
      night: 'Work at night',
    },
    key: {
      night: 'Night',
      dawn: 'Early morning',
      morning: 'Morning',
      midday: 'Midday',
      lunch: 'Lunch',
      afternoon: 'Afternoon',
      evening: 'Evening',
      late: 'Late evening',
      work: 'working hours at home',
    },
  },
  leave: {
    title: 'Working days and leave: {{name}}',
    week: 'Working week',
    weekHint: 'Press a day to make it a working day or a day off.',
    perYear: 'Days of leave a year',
    used: 'This trip takes',
    usedOf: '{{used}} of {{perYear}} days',
    marked: 'Days marked',
    none: 'No day marked yet.',
    save: 'Save',
    people: 'People',
    open: 'Working days and leave of {{name}}',
    mark: '{{name}}: {{kind}}',
    workday: 'Working day',
    dayOff: 'Day off',
    kind: { leave: 'On leave', sick: 'Sick leave', work: 'Work day' },
  },
  city: {
    checkInLabel: 'Check-in',
    checkOutLabel: 'Check-out',
    fromTime: 'from {{time}}',
    untilTime: 'until {{time}}',
    perNight: '{{price}} a night',
    bookingRef: 'Booking {{ref}}',
    openBooking: 'Open the booking ↗',
    things: 'Things to do',
    openDays: 'Open the days in {{city}} →',
    towards: 'towards {{place}}',
    backTitle: 'Back to {{room}}',
    open: 'Day plan',
    back: 'Whole trip',
    title: '{{city}} · {{dates}}',
    days: 'Days in {{city}}',
    dayPick: 'Pick a day',
    planned_one: '{{count}} thing',
    planned_other: '{{count}} things',
    plan: 'What the day holds',
    picked: 'Details',
    titleOf: {
      thing: 'To do: {{name}}',
      stay: 'Stay: {{name}}',
      arrive: 'Arrival: {{name}}',
      leave: 'Departure: {{name}}',
    },
    onFrom: 'On to {{name}}',
    add: 'Add something to do',
    nightOf: 'night {{night}} of {{nights}}',
    checkIn: 'check-in',
    checkOut: 'check-out',
    nights_one: '{{count}} night',
    nights_other: '{{count}} nights',
    lands: 'lands {{time}}',
    late: 'not in time',
    lateLong: 'Not made in time: it would leave before the thing before it is done.',
    leaves: 'leaves {{time}}',
    terminal: 'terminal {{terminal}}',
    summary_one: '{{count}} thing to do · {{walk}} on foot · {{money}}',
    summary_other: '{{count}} things to do · {{walk}} on foot · {{money}}',
    costs: 'Shared costs this day',
    costPaid: 'paid by {{name}}',
    costsAll: 'All shared costs',
    poiAdd: 'Add to this day',
    poiPick: 'Add this spot…',
    search: 'Search for a place, an address or GPS',
    poiHere: 'This spot',
    poiNone: 'Nothing named near here.',
    there: 'Getting here',
    fromPlace: 'from {{name}}',
    photo: 'Photo',
    facts: {
      day: 'Day',
      time: 'Time',
      price: 'Price',
      booking: 'Status',
      booked: 'Confirmed',
      notBooked: 'Not booked',
      place: 'Location',
    },
    photoCredit: 'Photo: Wikipedia · {{title}}',
    next: 'Next',
    booked: 'booked',
    change: 'Change',
    by: {
      walk: 'on foot',
      bike: 'bicycle',
      scooter: 'e-scooter',
      metro: 'metro',
      tram: 'tram',
      train: 'train',
      cable: 'cable car',
      bus: 'bus',
      minibus: 'minibus (marshrutka, colectivo)',
      jeepney: 'jeepney',
      songthaew: 'songthaew',
      tuktuk: 'tuk-tuk',
      motortaxi: 'motorbike taxi',
      taxi: 'taxi',
      ridehail: 'ride app (Grab, Uber)',
      car: 'car',
      boat: 'boat',
      ferry: 'ferry',
      cyclo: 'cycle rickshaw',
    },
    missing: 'This stop is not on the trip.',
    thing: {
      newTitle: 'Something to do in {{city}}',
      editTitle: 'Change: {{name}}',
      name: 'Name',
      nameHint: 'e.g. Sensō-ji',
      day: 'Day',
      starts: 'Starts',
      ends: 'Ends',
      place: 'Location',
      price: 'Price',
      booked: 'Ticket bought or table reserved',
      files: 'Photos and files',
      addFile: '+ Photo or PDF',
      removeFile: 'Remove {{name}}',
      fileOnSave: 'kept on saving',
      link: 'Link',
      by: 'Transport',
      byMinutes: 'Travel time',
      add: 'Add',
      save: 'Save',
      remove: 'Remove',
      removeQuestion: 'Remove {{name}}?',
      removeBody: 'It goes from the day.',
      removeConfirm: 'Remove',
      required: 'Enter a name',
      wrongTime: 'Write a time like 09:30',
      wrongMinutes: 'Write whole minutes, at most 1440',
      partEnd: 'Enter where each part but the last ends',
      part: 'Part {{n}}',
      partTo: 'Destination',
      lastTo: 'Destination: {{place}}',
      addChange: '+ A change',
      removePart: 'Remove part {{n}}',
      here: 'this thing',
    },
  },
  file: {
    page: '{{title}}, page {{number}}',
    unreadable: 'This PDF could not be read here: open it in a new tab.',
    tools: 'The PDF’s pages and zoom',
    pageNumber: 'Page, of {{count}}',
    smaller: 'Zoom out',
    larger: 'Zoom in',
    pick: 'Use “{{text}}”',
  },
  ticketCode: {
    title: 'QR code · {{name}}',
    code: 'QR code: {{code}}',
    hint: 'Turn the screen bright and hold the phone still in front of the reader.',
    show: 'Show the QR code of {{name}}',
    showShort: 'QR code',
    open: 'Show',
    field: 'QR code',
  },
  spotMap: {
    open: 'Pick on the map',
    openFor: '{{label}}: pick on the map',
    title: 'Pick the place on the map',
    search: 'Search for a place, or GPS',
    searchHint: 'A station, an address, or 35.55, 139.78',
    found: 'Places found',
    none: 'Nothing found by that name near here.',
    name: 'Name of the place',
    nameHint: 'Click the map first',
    detail: 'picked on the map',
    take: 'Save location',
  },
  calendar: {
    editWay: 'Change the way',
    today: 'Today',
    editTicket: 'Change the ticket',
    editTransport: 'Change the transport',
    arrives: 'Arrives {{time}} · {{place}}',
    title: 'Calendar',
    open: 'Calendar',
    previous: 'Previous month',
    next: 'Next month',
    tripDay: 'Trip day {{day}} of {{days}}',
    night: '{{city}}, {{country}} · night {{night}} of {{nights}}',
    lastDay: 'Last day of the trip',
    leaving: '{{how}} to {{city}}',
    leavingLast: '{{how}} onwards',
    travel: 'Travel',
    booked: 'Booked this day',
    expected: '{{amount}} expected a day',
    summary_one: '{{count}} leg · est. {{amount}} this month',
    summary_other: '{{count}} legs · est. {{amount}} this month',
    legend: 'Legend',
    keyTogether: 'Travelling together',
    keyGap: 'Night without a hotel',
    keyUnresolved: 'Unresolved stop',
    keyUnplanned: 'Unplanned',
    keyEclipse: 'Eclipse',
    eclipse: {
      sun: {
        total: 'Total solar eclipse',
        annular: 'Annular solar eclipse',
        partial: 'Partial solar eclipse',
      },
      moon: {
        total: 'Total lunar eclipse',
        partial: 'Partial lunar eclipse',
        penumbral: 'Penumbral lunar eclipse',
      },
    },
    eclipseLook: {
      total: 'total',
      annular: 'annular',
      partial: 'partial',
      penumbral: 'penumbral',
    },
    eclipseSeen: '{{city}}: {{look}}, {{from}}–{{until}}, deepest at {{peak}}',
    eclipseSunCovered: '{{share}} of the sun covered',
    eclipseMoonCovered: '{{share}} of the moon in shadow',
    eclipsePartSun: 'the sun is below the horizon for part of it',
    eclipsePartMoon: 'the moon is below the horizon for part of it',
    eclipseNone: '{{city}}: not seen from here',
    moonUp: '{{shape}} {{rise}}–{{set}}',
    moonAllDay: '{{shape}}',
    moonStays: '{{shape}} {{rise}}–',
    sunrise: 'Sunrise',
    sunset: 'Sunset',
    solarNoon: 'Solar noon: the sun at its highest',
    daylight: 'Daylight: from sunrise to sunset',
    sunUp: 'Midnight sun: the sun does not set',
    sunDown: 'Polar night: the sun does not rise',
    moonShape: {
      new: 'New moon',
      waxingCrescent: 'Waxing crescent',
      firstQuarter: 'First quarter',
      waxingGibbous: 'Waxing moon',
      full: 'Full moon',
      waningGibbous: 'Waning moon',
      lastQuarter: 'Last quarter',
      waningCrescent: 'Waning crescent',
    },
    keyHoliday: 'Public holiday',
    keyOff: 'Day off',
    offSaid: 'Day off: a weekend or a holiday',
    publicHoliday: 'public holiday',
    observance: 'observance',
    holidayIn: '{{name}} · {{kind}} ({{country}})',
    unplanned: 'unplanned',
    estimate: 'not booked · est. {{range}}',
    expectedThatDay: 'expected that day',
    whole: 'Whole trip',
    show: 'Show {{month}}',
    export: 'Export',
    menu: 'Calendar menu',
    afterTrip: 'After this trip',
    alsoShown: 'Other trips',
    kinTitle: 'Other trips this day',
    kinDay: '{{name}}: {{city}}',
    kinOpen: 'Open the calendar of {{name}} on {{day}}',
    kinOpenShort: 'Open its calendar',
    ics: 'Calendar file',
    icsHint: '.ics · Google, Apple, Outlook',
    subscribe: 'Subscribe link',
    subscribeHint: 'Copied, your calendar keeps it up to date',
    copied: 'Link copied',
    pdf: 'PDF',
    pdfHint: 'Every month, to print or save',
  },
  family: {
    meets: 'joins {{name}} · {{city}}',
    title: 'Trip tree',
    widget: 'Trip tree',
    alone:
      'No branches. Where its people part, each part can be a trip of its own: its own name, link, budget and people.',
    newTitle: 'A branch of {{name}}',
    newHint:
      'A trip of its own that parts from this one: it starts the day the stop ends, with its own budget and people.',
    name: 'Name',
    partsAt: 'Parts at',
    budget: 'Its budget',
    people: 'Travel companions',
    make: 'Make the branch',
    peopleCount_one: '{{count}} person',
    peopleCount_other: '{{count}} people',
    partsFrom: 'parts at {{city}}',
    meetsAgain: 'Meets again at',
    meetsNone: 'Meets no other trip',
    shared: 'The people of the other branches see this trip',
    new: '+ Branch',
    here: 'Branch off here',
    rail: 'The other trips of the tree',
    stopTitle: 'Stop: {{city}}, {{country}} · {{trip}}',
    lastStop: 'Its last stop',
    open: 'Show {{name}} on the map: {{stop}}',
    joinsHere: '{{name}} joins {{day}}',
    partsHere_one: '{{count}} trip from here',
    partsHere_other: '{{count}} trips from here',
    cancelledWay: {
      flight: 'flight cancelled',
      train: 'train cancelled',
      bus: 'bus cancelled',
      ferry: 'ferry cancelled',
      car: 'car cancelled',
      foot: 'walk cancelled',
      ticket: 'ticket cancelled',
      stay: 'stay cancelled',
    },
    peopleShort: '{{count}} ppl',
    joined: '+{{count}} from {{city}}',
    cancelled_one: '{{count}} booking cancelled',
    cancelled_other: '{{count}} bookings cancelled',
    budgetIs: 'budget {{amount}}',
    joins: 'joins',
    at: '{{city}} {{day}}',
    on: 'flight {{way}}',
    meetGap_one: '{{count}} day unplanned before meeting in {{city}}',
    meetGap_other: '{{count}} days unplanned before meeting in {{city}}',
    meetLate: 'It ends after the others have left {{city}}',
  },
  incident: {
    open: 'Report a change: {{name}}',
    title: 'Change or cancellation: {{name}}',
    titleStay: 'Cancelled: {{name}}',
    what: 'Kind of change',
    moved: 'Time changed',
    late: 'Late',
    cancelled: 'Cancelled',
    movedHint: 'The same ticket at a new time: the stops after it move along.',
    lateHint: 'The planned time stays; the real one is said beside it.',
    planned: 'Planned {{when}}',
    leavesDay: 'Leaves on',
    leaves: 'At',
    arrives: 'Arrives at',
    timeWrong: 'A time such as 9:00 or 17:30; when it leaves is needed',
    outcome: 'Outcome',
    replaced: 'Replaced',
    replacedHint:
      'A new ticket in its place, offered or bought: add it on the way. The old one stays struck through.',
    pending: 'Refund pending',
    pendingHint: 'The refund is waiting until it is here; the way is planned again.',
    received: 'Refund received',
    receivedHint: 'The refund is here.',
    voucher: 'Voucher',
    voucherHint: 'The money stays as credit with the seller: its worth and until when.',
    lost: 'No refund',
    lostHint: 'Nothing is refunded: the price stays in the budget as a loss.',
    amount: 'Refund amount',
    worth: 'Worth',
    until: 'Valid until',
    clear: 'Remove the change',
    save: 'Save',
    chipMoved: 'time changed',
    chipLate: 'late · {{times}}',
    chipCancelled: 'cancelled',
    chipReplaced: 'cancelled · replaced',
    chipPending: 'cancelled · {{amount}} to come back',
    chipReceived: 'cancelled · money back',
    chipVoucher: 'cancelled · voucher {{amount}}',
    chipVoucherUntil: 'cancelled · voucher {{amount}} until {{until}}',
    chipLost: 'cancelled · {{amount}} lost',
    replacement: 'replaces a cancelled one',
  },
  lodging: {
    hotel: 'Hotel',
    hostel: 'Hostel',
    apartment: 'Apartment',
    guesthouse: 'Guest house',
    bnb: 'Bed and breakfast',
    resort: 'Resort',
    villa: 'Villa',
    house: 'Holiday home',
    ryokan: 'Ryokan',
    capsule: 'Capsule hotel',
    camping: 'Campsite',
    homestay: 'Homestay',
    boat: 'Boat',
  },
  bookingForm: {
    freeUntil: 'Free cancellation until',
    freeAt: 'Time',
    lodging: 'Kind of place',
    lodgingAny: 'A place to sleep',
    after: 'after {{time}}',
    newTitle: 'New booking',
    checkTitle: 'Check this booking',
    ticketTitle: 'Ticket: {{name}}',
    rentalTitle: 'Car rental: {{name}}',
    flight: 'Flight number',
    towards: 'Destination',
    pnr: 'PNR',
    place: 'Where it is',
    attach: '+ PDF or photo',
    detach: 'Leave out {{name}}',
    source: 'The file read: {{name}}',
    pressInto: 'Press a text in the PDF to put it in',
    chooseField: 'Click a field of the form first, then a text in the PDF',
    wentInto: '“{{text}}” put in',
    noDayIn: '“{{text}}” says no date for {{field}}: press another text',
    editTitle: 'Edit {{name}}',
    stop: 'Stop',
    kind: 'Type',
    stay: 'A stay',
    ticket: 'A ticket',
    checkIn: 'Arrival',
    checkOut: 'Departure',
    from: 'Check-in',
    until: 'Check-out',
    nightsCount_one: '{{count}} night',
    nightsCount_other: '{{count}} nights',
    departs: 'Leaves on',
    name: 'Name',
    nights: 'Nights',
    price: 'Price',
    pay: 'Status',
    paid: 'Paid',
    later: 'Pay on arrival',
    laterTicket: 'Not paid yet',
    reference: 'Booking number',
    add: 'Add booking',
    save: 'Save',
    remove: 'Remove booking',
    removeQuestion: 'Remove {{name}}?',
    removeBody: 'Its price goes back to being an estimate.',
    removeConfirm: 'Remove',
  },
  whatIf: {
    title: 'Suggested changes',
    shift_one: '{{to}} +{{count}} day, {{from}} −{{count}}',
    shift_other: '{{to}} +{{count}} days, {{from}} −{{count}}',
    sameEnd: 'Same end date',
    skip_one: 'Skip {{from}}, +{{count}} day in {{to}}',
    skip_other: 'Skip {{from}}, +{{count}} days in {{to}}',
    oneFewer: 'One stop fewer, same end date',
    none: 'Suggestions appear once two stops have prices to compare.',
    less: '−{{amount}}',
    more: '+{{amount}}',
    same: 'No change',
    apply: 'Apply',
    own: 'Try your own',
    stop: 'Stop',
    days: 'Days at the stop',
    skip: 'Skip this stop',
    expected: 'Expected {{amount}}',
  },
};

/** Every English text in Estonian; a test fails when one is missing (docs/02 §7). */
const et: typeof en = {
  brand: 'TripPath',
  skip: 'Hüppa sisu juurde',
  language: { label: 'Keel' },
  status: {
    checking: 'Kontrollin serverit…',
    ok: 'Server töötab · skeem {{schema}}',
    down: 'Server ei vasta',
  },
  empty: {
    title: 'Reise veel pole',
    tagline: 'Planeeri teekond. Planeeri aeg. Planeeri raha.',
    body: 'Pikkadeks, kolme- kuni kuuekuulisteks reisideks: kas raha jätkub ja mis saab, kui jääd Jaapanisse kauemaks? Siit algab sinu esimene reis.',
    start: 'Planeeri uus reis',
    routeTitle: 'Teekond ja kaart',
    routeBody:
      'Kirjuta HEL-BKK või linn, lisa peatused riikide kaupa ja näe teed kaardil koos tavaliste ümberistumistega.',
    moneyTitle: 'Reis ja eelarve',
    moneyBody:
      'Min, oodatav ja max iga peatuse kohta: makstud, broneeritud ja veel hinnanguline ning mida iga muudatus säästab.',
    roadTitle: 'Reisi ajal',
    roadBody:
      'Tänane jääk kohalikus rahas, arved pildistatud, dokumendid ja sisenemisreeglid käepärast, ka ilma võrguta.',
  },
  pick: {
    placeholder: 'Vali…',
    noOptions: 'Midagi ei leitud',
    count_one: '{{count}} valik',
    count_other: '{{count}} valikut',
    focused: '{{label}}, valimiseks vajuta Enter',
    chosen: '{{label}} valitud',
    guidance: 'Nooleklahvid liiguvad, Enter valib, Escape sulgeb',
    filtered: '{{results}} otsingule „{{text}}”',
  },
  date: {
    placeholder: 'Vali kuupäev',
    open: 'Ava kalender',
    before: 'Eelmine päev',
    after: 'Järgmine päev',
  },
  range: {
    placeholder: 'Vali kuupäevad',
    days_one: '{{count}} päev',
    days_other: '{{count}} päeva',
    since: 'alates {{day}}',
    until: 'kuni {{day}}',
    from: 'Algus',
    to: 'Lõpp',
    typeHint: 'pp.kk.aaaa',
    clear: 'Tühjenda',
  },
  dialog: {
    close: 'Sulge',
    cancel: 'Loobu',
  },
  field: {
    price: 'Hind',
    range: 'Vahemik',
    amount: 'Kirjuta summa, näiteks 12,50',
    currency: '{{field}}: valuuta',
    number: 'Kirjuta täisarv',
    more: '{{label}}: rohkem',
    less: '{{label}}: vähem',
    lowest: 'Vähemalt',
    highest: 'Kõige rohkem',
  },
  problem: {
    check: 'Mõni väli vajab parandamist: need on all märgitud.',
    required: 'Täida see väli',
    too_long: 'Liiga pikk: kõige rohkem 100 märki',
    format: 'Ei ole oodatud kujul',
    range: 'Lubatud piiridest väljas',
    reversed: 'Väikseim on suurimast suurem',
    negative: 'Ei saa olla alla nulli',
    mismatch: 'Peatused muutusid vahepeal; laadi reis uuesti',
    taken: 'Juba kasutusel',
    no_rate: 'Selle valuuta kurss puudub: määra see',
    gone: 'Reisi pole enam või sa ei saa seda muuta',
    booked: 'Selle tee pilet on broneeritud: eemalda pilet, et viisi muuta',
    leaked: 'See parool on teadaolevates lekkinud paroolide nimekirjades: vali teine',
    archived: 'Reis on arhiveeritud ja ainult lugemiseks',
  },
  trips: {
    menu: 'Veel: {{name}}',
    edit: 'Muuda',
    title: 'Reisid',
    new: 'Uus reis',
    days_one: '{{count}} päev',
    days_other: '{{count}} päeva',
    daysShort: '{{count}}p',
    stops_one: '{{count}} peatus',
    stops_other: '{{count}} peatust',
    dates: '{{from}} – {{to}}',
    loading: 'Laadin…',
    failed: 'Serveriga ei saanud ühendust.',
    notFound: 'Sellist reisi ei ole.',
    back: 'Kõik reisid',
    copy: 'Kopeeri…',
    copyHint: 'Sama teekond teistele kuupäevadele',
    archive: 'Arhiveeri',
    archiveHint: 'Peidab nimekirjast, midagi ei kustu',
    restore: 'Võta arhiivist välja',
    restoreHint: 'Tagasi nimekirja, jälle muudetav',
    archived_one: 'Arhiveeritud · {{count}}',
    archived_other: 'Arhiveeritud · {{count}}',
    archivedOn: 'arhiveeritud {{day}}',
    readOnly: 'ainult lugemiseks',
    archivedNote: 'See reis on arhiveeritud ja ainult lugemiseks.',
    readOnlyNote: 'Ainult lugemiseks: seda reisi ei saa muuta.',
    actionFailed: 'See ei õnnestunud. Proovi uuesti.',
  },
  live: {
    here: 'Reisil praegu: {{names}}',
    state: { live: 'Ühendatud', connecting: 'Ühendan…', offline: 'Ühenduseta' },
    see: 'Vaata',
    close: 'Sulge',
    undo: 'Võta tagasi',
    undoFailed: 'Tagasi võtta ei õnnestunud.',
    changedMeanwhile: '{{name}} muutis seda peatust samal ajal sinuga.',
    theirDays_one: '{{name}} pani siia {{count}} päeva.',
    theirDays_other: '{{name}} pani siia {{count}} päeva.',
    keepTheirs: 'Kasuta versiooni: {{name}}',
    keepMine: 'Kasuta minu versiooni',
  },
  dayOrder: {
    ask: 'Järjesta lühima tee järgi',
    title: 'Lühima tee järjekord',
    pace: 'Liikumisviis',
    by: { walk: 'Jalgsi', transit: 'Ühistranspordiga', car: 'Autoga' },
    now: 'Praegu',
    new: 'Uus järjekord',
    km: '{{km}} km',
    time: '{{h}} h {{m}} min',
    was: 'oli {{n}}.',
    fixed: 'kindla kellaajaga',
    already: 'Päev on juba lühimas järjekorras.',
    note: 'Umbes: linnulend kolmandiku võrra pikem. Majutus jääb algusesse ja lõppu; kellaajaga tegevus jääb oma kohale.',
    cancel: 'Loobu',
    apply: 'Rakenda järjekord',
  },
  weather: {
    title: 'Ilm',
    days: 'Päev',
    loading: 'Laadin ilma…',
    failed: 'Ilma ei õnnestunud laadida.',
    partly: 'Mõne koha ilma ei saanud kätte; proovi hiljem uuesti.',
    unknown: 'Selle päeva ilma ei ole teada.',
    forecastTag: 'Prognoos',
    wasTag: 'Tegelik ilm',
    rainFell: 'Sademed',
    usualTag: 'Keskmine {{from}}–{{to}}, mitte prognoos',
    rainChance: 'Vihma tõenäosus',
    rainyDays: 'Vihmaseid päevi',
    highUsually: 'Päeval tavaliselt',
    lowUsually: 'Öösel tavaliselt',
    wind: 'Tuul',
    sun: 'Päikesetõus ja -loojang',
    sunTimes: '{{from}}–{{to}} kohaliku aja järgi',
    mm: '{{mm}} mm',
    kmh: '{{kmh}} km/h',
    band: 'Päeva temperatuur {{years}} aasta jooksul: {{from}} kuni {{to}}',
    bandLabel: 'Päeval tavaliselt {{from}} kuni {{to}}',
    forecastNote: 'Prognoos: Open-Meteo, uueneb iga kolme tunni järel.',
    wasNote: 'Ilm, nagu see sel päeval oli, Open-Meteo arhiivist.',
    usualNote: 'See on keskmine, mitte prognoos. Prognoos tuleb {{day}}, {{days}} päeva enne.',
    lane: 'Ilm',
    partsTitle: 'Päeva jooksul',
    part: {
      night: 'Öö',
      early: 'Varahommik',
      morning: 'Hommik',
      midday: 'Keskpäev',
      afternoon: 'Pärastlõuna',
      evening: 'Õhtu',
      late: 'Hilisõhtu',
    },
    partSaid: '{{part}} {{from}}–{{to}} · {{sky}} · {{temp}}',
    average: '{{from}}–{{to}} · {{sky}} · keskmiselt {{temp}}',
    onWay: 'Teekonna asukohas {{from}}–{{to}} · {{sky}} · {{temp}}',
    clear: 'selge',
    sky: {
      sun: 'päikeseline',
      part: 'vahelduv pilvisus',
      cloud: 'pilves',
      fog: 'udu',
      rain: 'vihm',
      snow: 'lumi',
      storm: 'äike',
    },
  },
  tripCopy: {
    title: 'Kopeeri: {{name}}',
    name: 'Uue reisi nimi',
    nameOf: '{{name}} (koopia)',
    start: 'Algab',
    shift_one: 'Kuupäevad: {{sign}}{{count}} päev · {{from}} – {{to}}',
    shift_other: 'Kuupäevad: {{sign}}{{count}} päeva · {{from}} – {{to}}',
    clocks: 'Kellaajad jäävad samaks kohalikuks ajaks, iga koha oma ajavööndis.',
    holds: 'Koopia sisu',
    route: 'Teekond',
    routeHint: 'Peatused, ööd, sõidud, päevaplaanid ja tegevused',
    prices: 'Hinnad',
    pricesHint: 'Vahemikud ja hinnangud nagu praegu',
    bookings: 'Broneeringud hinnanguna',
    bookingsHint:
      'Iga broneering saab koopias hinnanguks oma hinnaga; kinnituskoode, makseid ega faile ei kopeerita',
    branches_one: 'Harureisid · {{count}}',
    branches_other: 'Harureisid · {{count}}',
    branchesHint: 'Nihkuvad sama palju päevi',
    travellers: 'Kaasreisijad',
    travellersHint:
      'Nende päevad nihkuvad koos reisiga; ühised kulud, võlad ja dokumendid jäävad alati vanale reisile',
    submit: 'Kopeeri',
    cancel: 'Loobu',
  },
  tripForm: {
    newTitle: 'Uus reis',
    editTitle: 'Muuda reisi',
    name: 'Nimi',
    start: 'Alguskuupäev',
    budget: 'Eelarve',
    currency: 'Valuuta',
    past: 'Reis on juba toimunud (varasem alguskuupäev)',
    purpose: 'Reisi liik',
    purposeNone: 'Määramata',
    create: 'Loo reis',
    save: 'Salvesta',
  },
  purpose: {
    holiday: 'Puhkusereis',
    work: 'Tööreis',
    study: 'Õppereis',
    visit: 'Külaskäik',
    culture: 'Kultuurireis',
    adventure: 'Seiklusreis',
    sport: 'Spordireis',
    hint: {
      holiday: 'Puhkamine ja vaatamisväärsused',
      work: 'Tööülesanded ja kohtumised',
      study: 'Koolitus või õppimine',
      visit: 'Pere või sõprade külastamine',
      culture: 'Muuseumid, kontserdid ja kultuurisündmused',
      adventure: 'Matkamine ja aktiivsed tegevused',
      sport: 'Võistlustel osalemine või nende vaatamine',
    },
  },
  today: {
    open: 'Täna',
    back: 'Tagasi reisi juurde',
    before: 'Eelmine päev',
    after: 'Järgmine päev',
    tripDay: 'päev {{day}}/{{of}}',
    here: '{{city}} · öö {{night}}/{{nights}}',
    ended: 'Reis on läbi',
    startsIn_one: 'Reis algab homme',
    startsIn_other: 'Reis algab {{count}} päeva pärast',
    left: 'Tänane jääk',
    spentOf: 'kulutatud {{spent}} / {{allowance}}',
    underPlan: 'seni {{amount}} plaanist vähem',
    overPlan: 'seni {{amount}} üle plaani',
    next: 'Edasi',
    by: {
      flight: 'Lend: {{to}}',
      train: 'Rong: {{to}}',
      bus: 'Buss: {{to}}',
      ferry: 'Praam: {{to}}',
      car: 'Autoga: {{to}}',
      foot: 'Jalgsi: {{to}}',
      none: 'Edasi: {{to}}',
    },
    inDays_zero: 'täna',
    inDays_one: 'homme',
    inDays_other: '{{count}} päeva pärast',
    booked: 'broneeritud · {{amount}}',
    notBooked: 'broneerimata · {{range}}',
    add: 'Lisa kulu',
    amount: 'Summa',
    ownRate: 'sinu kurss',
    ecbRate: 'EKP, {{day}}',
    marketRate: 'turukurss, {{day}}',
    noRate: 'Selle valuuta kurss puudub.',
    setRate: 'Määra kurss',
    category: 'Kategooria',
    categories: {
      food: 'Toit',
      transport: 'Transport',
      stay: 'Majutus',
      fun: 'Meelelahutus',
      other: 'Muu',
    },
    note: 'Märkus',
    photo: 'Tšeki foto',
    photoAgain: 'Uus foto',
    photoAttached: 'Tšeki foto lisatud.',
    save: 'Lisa',
    failed: 'Salvestamine ebaõnnestus. Proovi uuesti.',
    rateTitle: 'Kurss: {{currency}}',
    rateLabel: '{{currency}} ühe {{trip}} kohta',
    rateEcb: 'Kasuta EKP kurssi',
    rateSave: 'Salvesta',
    rateWrong: 'See pole kurss: kirjuta arv, näiteks 30500.',
    spends: 'Selle päeva kulud',
    none: 'Veel pole midagi kulutatud.',
    bill: 'Tšekk',
    remove: 'Eemalda {{what}}',
    offline: 'Võrguühenduseta: kulud ootavad selles telefonis',
    waiting_one: '{{count}} kulu ootab saatmist',
    waiting_other: '{{count}} kulu ootab saatmist',
    stale: 'Võrguühenduseta: näha on viimati nähtu, {{day}}',
    notSent: 'ootab saatmist',
    refused: 'ei salvestatud: {{why}}',
    forget: 'Kustuta {{what}}',
  },
  countries: {
    title: 'Riigid',
    expected: 'Oodatav',
    of: '/ {{budget}}',
    perDay: 'Päeva hind riigiti',
    legendExpected: 'oodatav päevas',
    legendRange: 'madalaimast kõrgeimani',
    legendDear: 'peatus üle {{over}} päevas',
    dearAt: 'Üle {{over}} päevas: {{stops}}',
    nights: 'Päevi',
    aDay: '€ / päev',
    between: 'Lennud ja rongid riikide vahel',
    span: '{{range}} · {{days}} päeva',
    byCountry: 'Riigiti',
    entryBooked: 'Sissesõit · broneeritud',
    open: 'Näita: {{country}}',
    close: 'Peida: {{country}}',
    tiles: {
      countries: 'Riigid',
      countriesSub: '{{days}} päeva · {{cities}} linna',
      entry: 'Sissesõit',
      visas_one: '{{count}} viisa taotleda',
      visas_other: '{{count}} viisat taotleda',
      noVisas: 'Viisat pole vaja',
      visaNext: '{{country}}: {{kind}} · taotle {{day}}',
      forms: 'Saabumisvormid',
      formsToFill: '{{count}} täita',
      noForms: 'Pole midagi täita',
      formNext: 'järgmine: {{form}}, {{range}}',
      noPassport: 'Määra oma pass',
      noPassportSub: 'Dokumentides, et teada, mida iga piir nõuab',
      nights: 'Broneeritud öid',
    },
    rule: {
      free: 'Viisavaba {{days}} p',
      card: 'Saabumiskaart {{days}} p',
      eta: 'Reisiluba {{days}} p',
      evisa: 'E-viisa',
      arrival: 'Viisa piiril',
      visa: 'Viisa',
      by: '{{what}} {{day}}',
    },
    ruleLong: {
      free: 'Viisavaba, {{days}} päeva',
      card: 'Saabumiskaardiga, {{days}} päeva',
      eta: 'Reisiloaga, {{days}} päeva',
      evisa: 'E-viisa, {{days}} päeva',
      arrival: 'Viisa piiril, {{days}} päeva',
      visa: 'Viisa, {{days}} päeva',
    },
    unknownRule: 'Teadmata',
    ownCountry: 'Oma riik',
    inMoney: 'Summad valuutas',
    plugType: 'Tüüp {{letter}}',
    freeMovement: 'Vaba liikumine (EL)',
    bookedOf: '{{booked}} / {{nights}} ööd',
    entry: 'Sissesõit ja viibimine',
    passport: 'Pass · {{country}}',
    daysUsed: 'Päevi kasutatud',
    daysOf: '{{days}} / {{max}}',
    toFill: 'Täita',
    toApply: 'Taotleda',
    filled: 'Tehtud',
    formWindow: '{{form}} · {{range}}',
    applyBy: '{{form}} · hiljemalt {{day}}',
    official: 'Ametlik leht',
    setPassport: 'Määra oma pass dokumentides',
    money: 'Raha',
    currency: 'Valuuta',
    rate: 'Kurss',
    notes: 'Rahatähed',
    coins: 'Mündid',
    seldom: 'Harva kasutusel',
    noRate: 'Kurssi veel pole',
    stays: 'Majutus',
    food: 'Toit',
    local: 'Kohalik transport',
    activities: 'Tegevused',
    paidToPay: 'Makstud · tasumata',
    nightsTitle: 'Majutus',
    booked: 'Broneeritud',
    notBooked: 'Majutus broneerimata',
    partly: '{{booked}} ööd {{nights}}-st broneeritud',
    whileThere: 'Pühad reisi ajal',
    publicHoliday: 'riigipüha',
    observance: 'tähtpäev',
    time: 'Ajavöönd',
    homeAhead_one: '{{count}} tund kodusest ajast ees',
    homeAhead_other: '{{count}} tundi kodusest ajast ees',
    homeBehind_one: '{{count}} tund kodusest ajast taga',
    homeBehind_other: '{{count}} tundi kodusest ajast taga',
    plugs: 'Pistikud ja pinge',
    unknown: 'Teadmata',
    crossings: 'Piiriületused',
    arrive: '→ {{country}}',
    need: {
      free: 'Viisat ega luba pole vaja',
      card: '{{form}}, {{lead}} päeva enne',
      eta: '{{form}}, {{lead}} päeva enne',
      evisa: 'E-viisa, taotle {{lead}} päeva enne',
      arrival: 'Viisa piiril',
      visa: 'Viisa saatkonnast, {{lead}} päeva enne',
    },
    fill: 'Täida {{range}}',
    apply: 'Taotle hiljemalt {{day}}',
    done: 'Tehtud',
    passportValid: 'Sinu pass: {{country}} · kehtib kuni {{day}}',
    passportLong: '(üle 6 kuu pärast naasmist)',
    passportShort: '(alla 6 kuu pärast naasmist — vaata, mida iga piir nõuab)',
    passportNone: 'Sinu pass: {{country}}',
    indicative: 'Ainult suunav — kontrolli enne reisi ametlikest allikatest.',
  },
  theme: { dark: 'Tume režiim' },
  settings: {
    title: 'Reisi seaded',
    trip: 'Reis',
    danger: 'Reisi kustutamine',
    ownerOnly: 'Reisi saab kustutada ainult selle omanik.',
    archive: 'Arhiiv',
    archiveHow:
      'Omanik arhiveerib reisi reiside nimekirjas selle menüüst: siis on see ainult lugemiseks, midagi ei kustutata.',
  },
  widget: {
    move: 'Liiguta: {{name}}',
    close: 'Sulge: {{name}}',
    dock: 'Vidinad',
  },
  ticket: {
    part: {
      ticket: 'Pilet',
      legs: 'Sõidulõigud',
      flights: 'Lennud',
      people: 'Reisijad',
      rules: 'Pagas, check-in',
    },
    departure: 'Väljumine',
    arrival: 'Saabumine',
    airport: 'Lennujaam',
    placeBy: {
      flight: 'Lennujaam',
      train: 'Jaam',
      bus: 'Peatus',
      ferry: 'Sadam',
      car: 'Koht',
      foot: 'Koht',
      none: 'Jaam või peatus',
    },
    setOff: 'Väljasõit',
    arrive: 'Saabumine',
    date: 'Kuupäev',
    terminal: 'Terminal',
    actual: 'Tegelik aeg',
    airline: 'Lennufirma',
    airlineHint: 'Nimi või kood',
    aircraftHint: 'Tüüp või kood',
    clockHint: 'tt:mm',
    cabin: 'Käsipagas',
    pieces: 'Tükke',
    kg: 'Kilogramme',
    size: 'Mõõdud, cm',
    personalItem: 'Lisaks isiklik ese',
    checked: 'Registreeritud pagas',
    bag: '{{number}}. kohver, kg',
    addBag: 'Lisa kohver',
    removeBag: 'Eemalda {{number}}. kohver',
    lookup: 'Täida lennu numbri järgi',
    bags: 'Pagas',
    ticketBags: 'Sama mis piletil',
    ownBags: 'Erinev',
    cabinKg: 'Käsipagas, kg',
    after: 'pärast {{time}}',
    boarding: 'Pardaleminek',
    deplaning: 'Lennukist väljumine',
    apron: { bridge: 'Sild', stairs: 'Trepp', bus: 'Buss' },
    lookingUp: 'Otsin…',
    lookupNeeds: 'Vajab lennu numbrit ja kuupäeva.',
    lookupFound: 'Täidetud teenusest {{service}}: kontrolli üle.',
    lookupNone: 'Ükski teenus ei tea seda lendu sel päeval.',
    lookupNoService: 'Lennuandmete teenust pole seadistatud: vt Andmeallikad.',
    lookupFailed: 'Lendu ei saanud otsida. Proovi uuesti.',
    checkIn: 'Check-in',
    onlineOpens: 'Veebis avaneb, h enne',
    onlineCloses: 'Veebis sulgub, min enne',
    deskOpens: 'Lett avaneb, h enne',
    deskCloses: 'Lett sulgub, min enne',
    notes: 'Märkused',
    leg: '{{number}}. sõidulõik',
    flight: '{{number}}. lend',
    time: 'Kell',
    numberBy: {
      flight: 'Lennu number',
      train: 'Rongi number',
      bus: 'Liini number',
      ferry: 'Reisi number',
      car: 'Broneeringu number',
      foot: 'Broneeringu number',
      none: 'Number',
    },
    operatorBy: {
      flight: 'Vedaja',
      train: 'Vedaja',
      bus: 'Vedaja',
      ferry: 'Laevafirma',
      car: 'Rendifirma',
      foot: 'Giid või firma',
      none: 'Vedaja',
    },
    vehicleBy: {
      flight: 'Lennuk',
      train: 'Rongi tüüp',
      bus: 'Bussi mudel',
      ferry: 'Laev',
      car: 'Mark ja mudel',
      foot: 'Rada',
      none: 'Sõiduk',
    },
    registrationBy: {
      flight: 'Registreerimistunnus',
      train: 'Registreerimistunnus',
      bus: 'Registreerimisnumber',
      ferry: 'IMO number',
      car: 'Registreerimisnumber',
      foot: 'Loa number',
      none: 'Registreerimistunnus',
    },
    gateBy: {
      flight: 'Värav',
      train: 'Perroon',
      bus: 'Platvorm',
      ferry: 'Kai',
      car: 'Värav',
      foot: 'Algus',
      none: 'Värav',
    },
    travelClass: 'Klass',
    classes: {
      train: {
        first: '1. klass',
        second: '2. klass',
        sleeper: 'Magamisvagun',
        couchette: 'Kušett',
      },
      ferry: {
        deck: 'Istekoht',
        inside: 'Sisekajut',
        outside: 'Merevaatega kajut',
        suite: 'Sviit',
      },
    },
    carried: 'Sõiduk pardal',
    carriedNone: 'Puudub',
    carriedKind: {
      car: 'Sõiduauto',
      van: 'Kaubik',
      motorcycle: 'Mootorratas',
      bicycle: 'Jalgratas',
      camper: 'Matkaauto',
    },
    carriedPlate: 'Sõiduki registreerimisnumber',
    removeLeg: 'Eemalda {{name}}',
    addLeg: 'Lisa sõidulõik',
    addFlight: 'Lisa lend',
    legsMenu: 'Lõikude toimingud',
    bagsMenu: 'Pagasi toimingud',
    people: 'Piletil reisijad',
    person: '{{number}}. reisija',
    addPerson: '+ Reisija',
    name: 'Nimi',
    price: 'Selle reisija hind',
    ticketNumber: 'Pileti number',
    share: 'Võrdselt jagatud: {{amount}} igaühele',
    seatBy: {
      flight: 'Istekoht {{leg}}',
      train: 'Koht {{leg}}',
      bus: 'Istekoht {{leg}}',
      ferry: 'Kajut või koht {{leg}}',
      car: 'Istekoht {{leg}}',
      foot: 'Koht {{leg}}',
      none: 'Istekoht {{leg}}',
    },
    coach: 'Vagun {{leg}}',
    removePerson: 'Eemalda {{name}} piletilt',
    nameHint: 'Vali või kirjuta nimi',
    newPerson: '+ {{name}}, uus reisija',
  },
  car: {
    title: 'Auto',
    whose: 'Auto',
    hired: 'Rendiauto',
    own: 'Oma auto',
    kind: 'Kütus',
    petrol: 'Bensiin',
    electric: 'Elekter',
    use: 'Kütusekulu, l/100 km',
    useElectric: 'Kulu, kWh/100 km',
    useWrong: '1 kuni 50 100 km kohta, näiteks 6,5',
    price: 'Liitri hind: {{country}}',
    priceElectric: 'kWh: {{country}}',
    save: 'Salvesta',
  },
  proposal: {
    title: 'Variant',
    close: 'Sulge',
    fold: 'Ahenda ettepanek',
    change: 'Muudatus',
    total: 'Oodatav',
    was: 'praegu {{amount}}',
    length: 'Kestus',
    budget: 'Eelarve',
    within: 'mahub {{amount}} sisse',
  },
  variants: {
    title: 'Variandid',
    widget: 'Variandid',
    none: 'Variante veel pole. Kavanda otsinguväljal marsruut ja hoia see variandina, et seda siin võrrelda.',
    tree: 'Plaan ja selle variandid',
    branches: 'Variandid siit',
    addLabel: 'Google Flightsi link või marsruut, mida hoida variandina',
    addHint: 'Kleebi Google Flightsi link või kirjuta KUL-SIN-SYD',
    add: 'Lisa variant',
    unread: 'Kõiki kohti ei leitud.',
    fix: 'Täienda otsingus',
    precise: 'Lennupiletite täpne võrdlus',
    asIs: 'Plaan praegusel kujul',
    skips: 'Jätab vahele: {{cities}}',
    otherWay: 'Teine tee: {{city}}',
    instead: 'Asendab: {{cities}}',
    goesOn: 'Jätkab kohast {{city}}',
    elsewhere: 'Mujalt, pärast reisi lõppu',
    unfit: 'Ei sobi enam plaaniga',
    days_one: '{{count}} päev · kuni {{end}}',
    days_other: '{{count}} päeva · kuni {{end}}',
    over: '{{amount}} üle eelarve',
    use: 'Kasuta seda',
    forget: 'Eemalda {{name}}',
    forgetShort: 'Eemalda',
    useQuestion: 'Kas võtta {{name}} plaani?',
    useBody: 'Selle kohad saavad peatusteks tavahindadega.',
    useBodyDrops_one:
      'Selle kohad saavad peatusteks tavahindadega. Plaanist kaob: {{cities}}, koos {{count}} broneeringuga.',
    useBodyDrops_other:
      'Selle kohad saavad peatusteks tavahindadega. Plaanist kaob: {{cities}}, koos {{count}} broneeringuga.',
    keep: 'Hoia variandina',
    kept: '{{name}} on hoitud variandina',
  },
  mapPick: {
    inPlan_one: 'Juba sinu plaanis',
    inPlan_other: 'Sinu plaanis {{count}} korda',
    open: 'Ava peatus',
    stopOf_one: 'Peatus {{number}}/{{of}} · {{count}} öö',
    stopOf_other: 'Peatus {{number}}/{{of}} · {{count}} ööd',
    wayHere: 'Kavanda tee siia',
    wayHereFrom: 'Kohast {{from}}: võrdle teid, siis lisa',
    wayHereFirst: 'Alusta marsruuti siit: võrdle teid, siis lisa',
    asStop: 'Lisa peatus siia',
    again: 'Külasta uuesti',
    asStopDetail: 'Uus peatus: määra ööd ja kulud',
    close: 'Sulge',
    perDay: 'Tavaliselt {{range}} päevas',
  },
  track: {
    title: 'Teekonna rada',
    from: 'Failist {{name}} ({{format}})',
    import: 'Impordi rada',
    importHint:
      'GPX-, KML-, KMZ-, GeoJSON-, CSV-, FIT- või TCX-fail: kaardil joonistatakse sõit selle järgi.',
    replace: 'Asenda fail',
    remove: 'Eemalda rada',
    reading: 'Loen faili…',
    failed: 'Seda faili ei õnnestunud rajana lugeda: vaja on vähemalt kahte punkti.',
    saveFailed: 'Salvestamine ebaõnnestus. Proovi uuesti.',
    length: 'Pikkus',
    climb: 'Tõus · langus',
    height: 'Kõrgus',
    time: 'Kestus failis',
    profile: 'Kõrgus teel: {{low}}–{{high}} m',
  },
  tabs: {
    sections: 'Jaotised',
    trips: 'Reisid',
    allTrips: 'Kõik reisid',
    settings: 'Reisi seaded',
    offline: 'Võrguühenduseta kaardid',
    actual: 'Plaan ja tegelikkus',
    stats: 'Statistika',
    lists: 'Ülesanded ja failid',
    plan: 'Plaan',
    bookings: 'Broneeringud',
    calendar: 'Kalender',
    variants: 'Variandid',
    countries: 'Riigid',
    docs: 'Dokumendid',
    journal: 'Reisipäevik',
    group: 'Grupp',
    today: 'Päevakulud',
    search: 'Otsi reisist: linn, broneering…',
    searchOpen: 'Otsi',
    searchMap: 'Otsi kaardilt: „{{text}}“',
    searchStop: 'Peatus · {{from}} – {{to}}',
    searchBooking: 'Broneering · {{city}}',
    searchThing: 'Tegevus · {{city}}',
    budget: 'Eelarve',
    budgetTitle: 'Eelarve ja kulud',
    expected: 'Oodatav kulu',
    range: 'Vähim – suurim',
    paid: 'Makstud',
    documents: 'sh dokumendid ja kindlustused',
    toPay: 'Veel maksta',
    left: 'Eelarvest alles',
    over: 'Eelarvest üle',
    share: 'Jaga',
    history: 'Muudatused',
    export: 'Ekspordi',
    import: 'Impordi kohad',
    menu: 'Menüü',
  },
  rides: {
    ways: 'Transpordivõimalused',
    loading: 'Otsin võimalusi…',
    walk: 'Jalgsi',
    walkHow: 'Mööda tänavaid ja radu · OSRM',
    transit: 'Ühistransport',
    transitLine: '{{mode}} · liin {{line}}',
    transitHow: 'Sõiduplaani järgi · Transitous',
    bike: 'Rendiratas · {{system}}',
    scooter: 'Elektritõukeratas · {{system}}',
    station: 'Jaam {{metres}} kaugusel',
    free_one: '{{count}} ratas vaba',
    free_other: '{{count}} ratast vaba',
    near_one: '{{count}} lähedal',
    near_other: '{{count}} lähedal',
    anywhere: 'Võta ja jäta teeninduspiirkonnas',
    taxi: 'Takso',
    taxiHow: 'Mööda teed, liiklusega arvestatud',
    stations: 'Rattajaamad lähedal · GBFS',
    atEnd: 'sihtkohas',
    bikes_one: '{{count}} ratas',
    bikes_other: '{{count}} ratast',
    docks_one: '{{count}} koht',
    docks_other: '{{count}} kohta',
    stationsNote:
      'Andmed ettevõtte avalikust GBFS-voost, uuendatud iga minut, kui leht on lahti. Teisel päeval näidatakse jaamu ja hinda, mitte rataste arvu.',
    location: 'Asukoht ja aku',
    locationFrom:
      'Kaugused arvutatakse plaani kohast, siin {{place}}, mitte telefoni GPS-ist: planeerimiseks GPS-i vaja pole.',
    locationOnce:
      '„Näita mind kaardil“ küsib asukohta ühe korra ja lõpetab. Pidev jälgimine käib ainult nupust „Juhata“ ja lülitub välja 30 minuti järel või kui leht peidetakse.',
    locationNever: 'Taustal TripPath asukohta ei küsi.',
    showMe: 'Näita mind kaardil',
    guide: 'Juhata',
    stopGuide: 'Lõpeta juhatamine',
    you: 'Sina',
    noLocation: 'Asukohta ei õnnestunud leida.',
    parking: 'Autoga linna · pargi ja sõida',
    parkingBy: {
      train: 'edasi rongiga',
      bus: 'edasi bussiga',
      other: 'edasi ühistranspordiga',
    },
    parkingFree: 'tasuta',
    parkingNone: '15 km raadiuses pole pargi-ja-sõida parklat.',
    parkingNote:
      'Peatusse tullakse autoga: OpenStreetMapi parklad sildiga park_ride, koos hinna ja edasise sõiduga.',
  },
  mail: {
    title: 'Broneeringud e-kirjast',
    lead: 'Saada kinnituskiri reisi aadressile edasi või lohista see siia: TripPath loeb selle ja näitab enne lisamist, mida leidis ja kust.',
    forward: 'Saada edasi siia',
    copy: 'Kopeeri',
    copied: 'Kopeeritud',
    onlyYours:
      'Vastu võetakse ainult nende kontode aadressidelt, kes saavad seda reisi muuta. Teiste kirjad visatakse ära.',
    noAddress:
      'See server kirju vastu ei võta. Lohista kiri või selle fail siia või kleebi kirja tekst.',
    or: 'Või',
    dropHint: '.eml, .pkpass (Apple Wallet), .ics, PDF või pilt',
    drop: 'Lohista kiri või fail siia',
    choose: 'Vali fail',
    paste: 'Kirja tekst',
    pasteHint: 'Kleebi kinnituse tekst',
    read: 'Loe broneering tekstist',
    reading: 'Loen…',
    failed: 'Lugeda ei õnnestunud. Proovi uuesti.',
    received_one: 'Saabunud · {{count}}',
    received_other: 'Saabunud · {{count}}',
    none: 'Midagi pole veel saabunud.',
    today: 'täna {{time}}',
    yesterday: 'eile',
    pasted: 'Kleebitud',
    byHand: 'ava ja lisa käsitsi',
    status: {
      review: 'Ülevaatamiseks',
      added: 'Lisatud',
      left: 'Lisamata',
      none: 'Broneeringut ei leitud',
    },
    readFrom: {
      schema: 'Loetud kirja sees olevast schema.org andmest',
      wallet: 'Loetud Apple Walleti piletist',
      calendar: 'Loetud kalendrisündmusest',
      pdf: 'Loetud manuses olevast PDF-ist',
      picture: 'Loetud manuses olevast pildist',
      text: 'Loetud kirja tekstist',
    },
    pick: 'Vali kiri, et näha, mida sellest loeti.',
    kind: 'Liik',
    kinds: { stay: 'Majutus', ticket: 'Pilet' },
    stop: 'Peatus',
    noStop: 'Neil päevil peatust pole',
    checkIn: 'Sisse',
    checkOut: 'Välja',
    departs: 'Väljub',
    localTime: '{{city}} aeg',
    nights: 'Ööd',
    fits: 'sobib peatusega',
    longer: 'peatusest pikem',
    price: 'Hind',
    pay: 'Makse',
    paid: 'makstud',
    later: 'maksta kohapeal',
    payLater: 'maksta hiljem',
    reference: 'Broneeringu number',
    address: 'Aadress',
    flight: 'Lend',
    seat: 'Istekoht',
    from: 'Saatja: {{sender}}',
    forwardedBy: 'Edasi saatis: {{sender}} · {{when}}',
    original: 'Kiri, nagu see saabus',
    gone: 'Kirja ennast hoitakse 30 päeva; alles jääb ainult see, mis broneeringust sai.',
    notFound: 'Selles kirjas broneeringut ei leitud. Kui see on broneering, lisa see käsitsi.',
    leave: 'Ära lisa',
    change: 'Muuda enne lisamist',
    add: 'Lisa broneering',
    addByHand: 'Lisa käsitsi',
    again: 'Tagasi ülevaatusse',
    seeBookings: 'Vaata broneeringuid',
    note1:
      'Esmalt kirja sisse peidetud schema.org broneering (Booking.com, lennufirmad, Agoda), siis Apple Walleti pilet (.pkpass), siis kalendrisündmus, siis manuses olev PDF või pilt nagu broneeringute leht neid loeb, viimasena kirja tekst.',
    note2:
      'Märgitud read näitavad, kust iga osa loeti; osale vajutades kerib kiri selle kohani.',
    note3: 'Kirja hoitakse 30 päeva, siis jääb alles ainult broneering.',
  },
  flights: {
    title: 'Lennu jälgimine ja teavitused',
    lead: 'Sinu lennud nende päeval: kontrollitakse järjest tihemini ning iga muutus saadetakse telefoni.',
    none: 'Sellel reisil pole veel numbri ja ajaga lendu.',
    toBookings: 'Lisa see broneeringutes',
    pick: 'Lennud',
    late: 'Hilineb {{minutes}} min',
    onTime: 'Plaanipäraselt',
    notYet: 'Pole veel kontrollitud',
    cancelled: 'Tühistatud',
    departed: 'Väljunud',
    landed: 'Maandunud',
    leaves: 'väljub {{time}}',
    atGate: 'värav {{gate}}',
    gateMoved: 'Värav muutus',
    terminalMoved: 'Terminal muutus',
    checkInDesks: 'Check-in lauad',
    beltSaid: 'Pagasilint',
    takes: '{{hours}} h {{minutes}} min',
    terminal: 'Terminal',
    gate: 'Värav',
    gateChanged: 'Värav muutus',
    checkIn: 'Check-in',
    belt: 'Pagasilint: {{city}}',
    changes: 'Muutused',
    nextCheck: 'Järgmine kontroll {{time}}',
    notices: 'Teavitused',
    yours: 'konto seaded · iga reisija enda',
    event: 'Sündmus',
    phone: 'Telefon',
    email: 'E-post',
    calendar: 'Kalender',
    alarm: 'meeldetuletus',
    events: {
      delay: 'Lend hilineb üle 15 min või tühistatakse',
      gate: 'Värav või terminal muutus',
      checkin: 'Check-in avaneb',
      tomorrow: 'Lend, rong või praam homme',
      border: 'Piiriformaalsused (viisa, vorm)',
      free: 'Tasuta tühistamine lõpeb',
      pay: 'Täna kohapeal tasuda',
      companion: 'Kaasreisija muudatus',
    },
    noMail: 'See server e-kirju ei saada.',
    devices_zero: 'Ükski sinu seade veel teavitusi ei saa.',
    devices_one: 'Teavitusi saab {{count}} sinu seade.',
    devices_other: 'Teavitusi saab {{count}} sinu seadet.',
    turnOn: 'Luba teavitused selles seadmes',
    test: 'Saada prooviteade',
    turnedOn: 'See seade saab nüüd teavitusi.',
    denied: 'Brauser on selle saidi teavitused keelanud.',
    noPush: 'See brauser ei saa teavitusi.',
    tested_one: 'Prooviteade saadeti {{count}} seadmesse.',
    tested_other: 'Prooviteade saadeti {{count}} seadmesse.',
    notKept: 'Salvestamine ebaõnnestus. Proovi uuesti.',
    preview: 'Teavitus telefonis',
    now: 'TripPath · nüüd',
    schedule: 'Kontrollimise ajakava',
    when: { day: '−24 h', hours: '−6 h, −3 h', near: '−2 h … väljumine', air: 'lennu ajal' },
    how: {
      day: 'üks kord',
      hours: 'üks kord',
      near: 'iga 20 min',
      air: 'kord tunnis, maandumine',
    },
    quota:
      'Umbes 12 päringut lennu kohta. Tasuta kvoodid kokku (AeroDataBox, AirLabs, Aviationstack) jätkuvad mitmekümne lennu jaoks kuus; teenus vahetub järgmise vastu, kui kuu kvoot saab otsa.',
    offline: 'Võrguühenduseta',
    offline1:
      'Teavitus tuleb Web Pushina ka siis, kui Chrome on kinni (Android) või TripPath on avaekraanil (iPhone).',
    offline2:
      'Võrguta telefon saab teavituse, kui võrk tagasi tuleb. Seepärast lähevad „homme“ ja „check-in“ ka kalendri alarmina, mis heliseb ilma võrguta.',
    link: 'Lennu jälgimine',
  },
  lists: {
    title: 'Ülesanded ja failid',
    lead: 'Mida teha enne minekut, reisi failid, mitme tegevuse muutmine korraga ja teavitused. Kõik on selle reisi kohta; teavituste seaded kehtivad kõigile sinu reisidele.',
    open: 'Ülesanded ja failid',
    tasks: 'Ülesanded enne reisi',
    count: '{{done}} / {{all}} tehtud',
    add: 'Lisa',
    what: 'Ülesanne',
    remindDay: 'Meeldetuletuse kuupäev',
    remindTime: 'Kell',
    anyTime: 'Ükskõik millal',
    save: 'Salvesta',
    remind: 'Tuleta meelde',
    noReminder: 'Meeldetuletuseta',
    late: 'Tähtaeg möödas',
    week: 'Sel nädalal · {{range}}',
    later: 'Enne lahkumist · {{day}}',
    none: 'Kuupäevata',
    done: 'Tehtud',
    doneOn: 'tehtud {{day}}',
    tasksNote:
      'Meeldetuletus tuleb sinu valitud kanalisse (vt Teavitused). Kellaaeg on sinu kodu ajavööndis; kohaga seotud real koha ajas.',
    task: {
      border: '{{form}}: {{country}}',
      borderAbout: 'Riigid · {{country}} · menetlus kuni {{days}} päeva',
      pay: 'Maksa {{name}} ülejäänud summa',
      payAbout: 'Broneeringud · {{city}}',
      free: 'tühistamine tasuta kuni {{day}}',
      checkin: 'Online-registreerimine, {{flight}}',
      checkinAbout: 'Avaneb {{hours}} h enne väljumist',
      maps: 'Laadi telefoni päevade kaardid',
      mapsAbout: 'Võrguühenduseta kaardid · Wi-Fi kaudu',
      mapsOpen: 'Ava',
      noDay: 'Kuupäevata meeldetuletus',
    },
    files: 'Failid',
    filesCount_one: '{{count}} fail · {{size}}',
    filesCount_other: '{{count}} faili · {{size}}',
    zip: 'Laadi kõik alla (ZIP)',
    drop: 'Lohista failid siia',
    dropAbout: 'PDF-id ja pildid · kuni 20 MB faili kohta',
    uploading: 'Laadin üles…',
    tied: 'seotud: {{what}}',
    wholeTrip: 'kogu reisile',
    tieTo: 'Seos: {{name}}',
    remove: 'Kustuta {{name}}',
    removeSure: 'Kustuta jäädavalt',
    filesNote: 'Faile näevad reisi liikmed.',
    things: '{{city}}: tegevused',
    thingsNone: 'Tegevused',
    thingsNoneAbout: 'Ühelgi peatusel pole veel tegevusi. Lisa need peatuse päevadele.',
    thingsHow: 'Märgi read, et muuta neid koos',
    stop: 'Peatus',
    chosen_one: '{{count}} tegevus',
    chosen_other: '{{count}} tegevust',
    move: 'Vii teisele päevale',
    moveTo: 'Vii päevale',
    status: 'Muuda olekut',
    tag: 'Lisa silt',
    tagName: 'Silt',
    copy: 'Kopeeri',
    copyTo: 'Kopeeri päevale',
    delete: 'Kustuta',
    deleteSure_one: 'Kustuta {{count}} jäädavalt',
    deleteSure_other: 'Kustuta {{count}} jäädavalt',
    choose: 'Vali',
    day: 'Päev',
    time: 'Kell',
    thing: 'Tegevus',
    state: 'Olek',
    price: 'Hind',
    booked: 'broneeritud',
    planned: 'plaanis',
    notices: 'Teavitused',
    whatTold: 'Teavituste liigid',
    channel: { email: 'E-kiri', push: 'Brauser', telegram: 'Telegram' },
    event: {
      before: {
        name: 'Meeldetuletus „Tee enne reisi“ nimekirjast',
        about: 'Real määratud kellaajal',
      },
      invite: { name: 'Kutse reisile', about: 'Keegi lisas sind reisile' },
      companion: {
        name: 'Muudatus reisil',
        about: 'Kaasreisija muutis peatust, broneeringut või kulu',
      },
      debt: { name: 'Uus ühiskulu', about: 'Keegi lisas kulu, mis puudutab sind' },
      weekly: {
        name: 'Nädala kokkuvõte',
        about: 'Esmaspäeviti 08:00: mis ees ootab ja mida maksta',
      },
    },
    noMail: 'See server ei saada kirju.',
    inviteMail: 'Kutse tuleb alati e-kirjaga.',
    noBot: 'Selles serveris pole Telegrami robotit.',
    notTied: 'Ühenda kõigepealt Telegram.',
    testMail: 'Saada proovikiri',
    mailSent: 'Proovikiri on saadetud. Vaata postkasti.',
    mailFailed: 'Proovikirja ei saanud saata.',
    tie: 'Ühenda Telegram',
    tieHow: 'Telegram avanes: vajuta Start ja teated tulevad sinna.',
    untie: 'Katkesta Telegrami ühendus',
    mailNote:
      'E-kirjad lähevad serveri SMTP kaudu, sama mis kutsetel. Iga kirja lõpus on link, millega neist kirjadest loobuda.',
  },
  stats: {
    title: 'Statistika',
    lead: 'Kõik reisid kokku, arvutatud plaanist ja sellest, mida lisasid pärast reisi. Arvesse lähevad ainult lõppenud reisid.',
    open: 'Statistika',
    explored: 'Külastatud kohad',
    exploredAs: 'Kuvamisviis',
    heat: 'Soojuskaart',
    heatFew: 'Vähe',
    heatMany: 'Palju',
    heatWhat:
      'Värv näitab, kui palju sinu reisidest seal on: salvestatud rajad, teekonnad, peatused, päeviku kohad ja fotod. Punane seal, kus olid kõige rohkem.',
    fog: 'Avastamata alad',
    exploredSize: '{{cells}} ruutu umbes 2 × 2 km · umbes {{km2}} km²',
    exploredMap: 'Külastatud kohtade kaart',
    noExplored: 'Salvestatud rajad, päeviku kohad ja lõppenud reiside peatused ilmuvad siia.',
    failed: 'Statistikat ei saanud laadida. Proovi uuesti.',
    year: 'Aasta',
    allYears: 'Kõik aastad',
    allTrips: 'Kõik lõppenud reisid',
    tripsPick: 'Reisid',
    leadTrip: 'Ainult see reis, arvutatud selle plaanist: ka enne reisi algust.',
    onTheWay: 'Läbitud',
    byTrain: '{{share}}% rongiga',
    nightsAway: 'Öid kodust eemal',
    trips_one: '{{count}} reis',
    trips_other: '{{count}} reisi',
    countries: 'Riike',
    new_one: '{{count}} uus',
    new_other: '{{count}} uut',
    towns: 'Linnu',
    longestTown: 'kõige kauem: {{town}}',
    flown: 'lennud {{share}}%',
    modes: 'Liikumisviisid',
    noWays: 'Ühtegi lõppenud reisi teedega veel pole.',
    longest: 'Rekordid',
    longestFlight: 'Pikim lend',
    longestTrain: 'Pikim rongisõit',
    longestStay: 'Pikim peatus',
    highest: 'Kõrgeim punkt',
    hours: '{{hours}} h {{minutes}} min',
    nights_one: '{{count}} öö',
    nights_other: '{{count}} ööd',
    byCountry: '{{year}} riikide kaupa',
    badges: 'Saavutused',
    progress: '{{have}} / {{need}}',
    badge: {
      'first-trip': { name: 'Esimene reis', how: 'Lõpeta üks reis' },
      'four-countries': { name: '4 riiki ühe reisiga', how: 'Neli riiki ühel reisil' },
      'fast-train': { name: 'Kiirrong', how: 'Rong üle 200 km/h' },
      'night-train': { name: 'Öörong', how: 'Maga üks öö rongis' },
      ferry: { name: 'Üle vee', how: 'Sõida praamiga' },
      'long-flight': { name: 'Kauglend', how: 'Lend 5000 km või rohkem' },
      climb: { name: 'Mäe otsa', how: '400 m tõusu ühel teel' },
      slow: { name: 'Aeglane reis', how: 'Seitse ööd ühes linnas' },
      equator: { name: 'Mõlemad poolkerad', how: 'Peatused ekvaatorist põhjas ja lõunas' },
      continents: { name: 'Kõik mandrid', how: 'Peatused kõigil kuuel mandril' },
      'countries-10': { name: '10 riiki', how: 'Ööbi 10 riigis' },
      'countries-25': { name: '25 riiki', how: 'Ööbi 25 riigis' },
      'nights-100': { name: '100 ööd ära', how: 'Ole 100 ööd kodust ära' },
      'train-10000': { name: '10 000 km rongiga', how: 'Sõida rongiga 10 000 km' },
      'foot-100': { name: '100 km jalgsi', how: 'Käi jalgsi 100 km teid' },
      'around-the-world': { name: 'Ümber maailma', how: 'Kokku 40 075 km' },
    },
    studio: 'Plakat ja teekonna video',
    noTrips: 'Plakati või video saab teha, kui reis on läbi.',
    noMap: 'Ühtegi selle reisi peatust pole veel kaardil.',
    trip: 'Reis',
    making: 'Vorming',
    poster: 'Plakat',
    video: 'Video',
    sizeLabel: 'Suurus',
    size: { a4: 'A4', a3: 'A3', '50x70': '50×70' },
    ground: 'Taust',
    dark: 'Tume',
    light: 'Hele',
    names: 'Peatuste nimed ja kuupäevad',
    arcs: 'Lennud kaarena',
    arcsOff: 'väljas olles näidatakse ainult maa- ja veeteid',
    posterPreview: 'Plakati eelvaade, {{size}}',
    posterMap: 'Teekond',
    posterLine: '{{nights}} ööd · {{km}}',
    posterNote: 'Plakat on trükivalmis PDF, tehtud reisi teekonnast.',
    videoPreview: 'Video eelvaade',
    videoNote:
      'Video: 30 s, 1080p, teekond joonistub päev päeva järel, nurgas kuupäev ja kilomeetrid. See tehakse selles seadmes ja salvestatakse siia; midagi ei laadita üles.',
    preview: 'Eelvaade',
    makePoster: 'Loo plakat (PDF)',
    makeVideo: 'Loo video',
    recording: 'Teen videot…',
    saveVideo: 'Salvesta video',
  },
  actual: {
    title: 'Plaan ja tegelikkus',
    lead: 'Lisa failid, kus on näha, kus sa tegelikult olid, ja TripPath paneb need plaani kõrvale: ööd, teed ja kilomeetrid. Midagi ei jälgita; arvesse lähevad ainult failid, mille ise lisad.',
    open: 'Plaan ja tegelikkus',
    sources: 'Allikad',
    add: 'Lisa fail',
    working: 'Töötlen…',
    noSources:
      'Faile pole veel. Lisa Google’i ajajoone eksport (JSON) või telefoni või kella rajad: GPX, KML, KMZ, GeoJSON, CSV, FIT või TCX.',
    sourceLine: '{{name}} · {{range}}',
    points_one: '{{n}} punkt',
    points_other: '{{n}} punkti',
    remove: 'Eemalda {{name}}',
    again: '{{name}} on juba olemas nime all {{kept}}, vahele jäetud.',
    unread: '{{name}} ei ole ajajoon ega rada.',
    outside: 'Failis {{name}} pole midagi selle reisi päevadest.',
    failed: 'Serveriga ei saanud ühendust. Proovi uuesti.',
    archived: 'See reis on arhiivis. Muutmiseks too see arhiivist tagasi.',
    once: 'Sama teed ei loeta kaks korda: fail tuntakse ära tema tunnuse (opentracks:trackid) või aja ja koha järgi.',
    nights: 'Ööd',
    nightsSub: 'plaanitud / tegelik',
    stops: 'Peatused',
    stopsAdded: 'lisandusid {{names}}',
    km: 'Maa- ja veeteed',
    kmSub: 'plaanis {{km}} km linnulennult',
    flights: 'Lennud',
    flightsSub: 'plaanitud / lennatud',
    stopsTitle: 'Peatused',
    stop: 'Peatus',
    plan: 'Plaan',
    actual: 'Tegelik',
    nightsCol: 'Ööd',
    difference: 'Erinevus',
    same: 'erinevust pole',
    newStop: 'uus peatus',
    notReached: 'jäi käimata',
    otherDays: 'muudel kuupäevadel',
    more_one: '{{count}} öö rohkem',
    more_other: '{{count}} ööd rohkem',
    fewer_one: '{{count}} öö vähem',
    fewer_other: '{{count}} ööd vähem',
    assumed: 'andmed puuduvad, arvestatud plaani järgi',
    waysTitle: 'Teed',
    noWays: 'Peatuste vahel pole veel teid.',
    way: 'Tee',
    cost: 'Kulu',
    plannedTo: '{{mode}}: {{city}}',
    going: { flight: 'Lend', train: 'Rong', road: 'Maantee', foot: 'Jalgsi' },
    unknown: 'andmed puuduvad',
    keep: 'Jäta plaan muutmata',
    write: 'Uuenda plaan tegelikkuse järgi',
    wroteIn: 'Plaan on uuendatud. Endine plaan jääb siia võrdluseks.',
    writeNote:
      '„Uuenda plaan“ seab iga peatuse ööd nii nagu need olid ning lisab uued peatused ja teed nii nagu mindi. Plaan jääb siia võrdluseks ja selle saab tagasi võtta.',
    written: 'Reisi juurde kirjutatud {{day}}.',
    takeBack: 'Võta tagasi',
    takenBack: 'Plaan on taastatud.',
    notBack: 'Reisi on vahepeal muudetud, seda ei saa enam tagasi võtta.',
    countriesTitle: 'Päevad riikides',
    noPassport: 'Lisa oma pass Dokumentide lehel, et näha, mida iga riik lubab.',
    daysOf: '{{days}} / {{of}} p',
    days_one: '{{count}} p',
    days_other: '{{count}} p',
    rule: {
      free: 'ilma viisata kuni {{days}} päeva',
      card: 'ilma viisata saabumiskaardiga kuni {{days}} päeva',
      eta: 'ETA-ga kuni {{days}} päeva',
      evisa: 'e-viisaga kuni {{days}} päeva',
      arrival: 'piiril antava viisaga kuni {{days}} päeva',
      visa: 'viisaga kuni {{days}} päeva',
    },
    freeToMove: 'sinu passiga vaba liikumine',
    noRule: 'sinu passi kohta reeglit ei tea',
    countriesNote:
      'Päev loetakse riigis olnuks, kui seal oldi mingil kellaajal kohaliku aja järgi; sõidupäev loetakse mõlemas riigis. Reeglid tulevad Dokumentide lehelt sinu passi järgi.',
    schengenTitle: 'Schengeni reegel 90/180',
    schengenWho: '{{name}} ({{country}} pass) · 180 päeva kuni {{day}}',
    schengenSquares: '180 päeva, iga ruut 6 päeva',
    schengenUsed: 'Kasutatud {{used}} päeva, plaanis veel {{planned}}',
    schengenLeft: '{{left}} päeva varu',
    schengenOver: '{{over}} päeva üle piiri',
    schengenFrees_one: '; {{day}} vabaneb {{count}} päev',
    schengenFrees_other: '; {{day}} vabaneb {{count}} päeva',
    schengenNone:
      'Kellelgi sellel reisil pole passi, millele Schengeni 90 päeva 180 sees kehtiks, või pole sinna päevi plaanis.',
    yearTitle: '{{year}} kokku',
    home: 'kodu',
    yearNote: 'Maksuresidentsuse jaoks: üle 183 päeva ühes riigis 12 kuu jooksul.',
  },
  record: {
    title: 'Salvesta tee {{from}} → {{to}}',
    titleAlone: 'Salvesta tee peatusest {{from}}',
    lead: 'Ainult siis, kui ise alustad. Asukoht jääb telefoni ega lähe serverisse enne, kui vaatad salvestuse üle ja vajutad „Lisa reisile“: TripPath asukohta ei jälgi.',
    how: 'Kuidas see töötab',
    how1: 'Telefon kirjutab punkti iga 10 m või 30 s järel.',
    how2: 'Punktid on ainult selles telefonis.',
    how3: 'Lõpus näed kõike ja otsustad, mida reisile lisad.',
    how4: 'Salvestus peatub ise 6 tunni pärast.',
    going: 'Liikumisviis',
    goingAs: {
      detect: 'Automaatne',
      foot: 'Jalgsi',
      bike: 'Rattaga',
      car: 'Autoga',
      train: 'Rongiga',
    },
    start: 'Alusta salvestamist',
    startHow: 'Brauser küsib luba asukohale „rakenduse kasutamise ajal“. Ekraan jääb sisse.',
    elapsed: 'kestus',
    km: 'km',
    up: 'm tõusu',
    now: 'Praegu: {{going}}',
    before: 'enne {{emoji}} {{going}} {{km}}',
    pause: 'Paus',
    resume: 'Jätka',
    finish: 'Lõpeta',
    paused: 'Paus',
    arrived: 'Jõudsid peatusse {{city}}',
    arrivedStay: 'Siin {{nights}} ööd · {{stay}} {{metres}} m · sisseregistreerimine {{time}}',
    arrivedNights: 'Siin {{nights}} ööd',
    guide: 'Juhata mind jalgsi: {{stay}}',
    lost: 'Asukohta ei leitud. Salvestus ootab seda.',
    guiding: 'Juhatamine',
    turn: {
      depart: 'Alusta',
      arrive: 'Oled kohal',
      straight: 'Otse',
      left: 'Pööra vasakule',
      right: 'Pööra paremale',
      'slight left': 'Hoia vasakule',
      'slight right': 'Hoia paremale',
      'sharp left': 'Pööra järsult vasakule',
      'sharp right': 'Pööra järsult paremale',
      uturn: 'Pööra tagasi',
      other: 'Jätka',
    },
    onto: '{{turn}}: {{street}}',
    in: '{{metres}} m pärast',
    then: 'Siis: {{turn}} {{metres}} m',
    left: 'kokku {{metres}} m · {{minutes}} min',
    quiet: 'Hääl välja · kaart telefonis',
    endGuide: 'Lõpeta juhatamine',
    noGuide: 'Jalgsi teed kohta {{stay}} ei leitud.',
    done: 'Salvestus valmis',
    ways: 'Teed',
    stays: 'Peatumised',
    wayLine: '{{going}} {{km}}',
    wayWhen: '{{from}}–{{to}} · tõenäosus {{sure}}%',
    stayLine: '{{name}} · {{minutes}} min',
    stayWhen: '{{from}}–{{to}} · {{planned}}',
    inPlan: 'on päeva plaanis',
    notInPlan: 'plaanis pole',
    stayAt: 'Peatumine kell {{time}}',
    nothing: 'Midagi ei salvestatud.',
    deleteAll: 'Kustuta kõik',
    add: 'Lisa reisile',
    added:
      'Lisatud reisile: teed edasi peatusest {{city}}, plaanimata peatumised tehtud tegevustena.',
    deleted: 'Salvestus on sellest telefonist kustutatud.',
    failed: 'Serveriga ei saanud ühendust; salvestus jääb telefoni.',
    notes: 'Andmed ja arvutus',
    note1:
      'Punktid kogub brauser telefonis (Geolocation watchPosition) ainult avatud lehel; taustal salvestamiseks salvesta OpenTracksi või OsmAndiga ja impordi fail pärast.',
    note2:
      'Teeviis loetakse iga minuti kiirusest; ühe minuti pikkune lõik läheb eelmise teega kokku.',
    note3:
      'Peatumine: vähemalt 10 min 100 m raadiuses. Saabumine: 300 m peatuse punktist või majutusest.',
    note4: 'Juhatamine: OSRM-i sammud (steps=true); päeva kaart on telefonis juba olemas.',
    note5: 'Kõik kellaajad on koha kohalikus ajas; salvestus hoiab aega koos nihkega.',
    link: 'Salvesta see tee',
    mapLabel: 'Salvestatud tee kaart',
  },
  wayDraw: {
    caps: 'Tee · {{day}}',
    title: 'Tee peatusest {{from}}',
    name: 'Tee nimi',
    how: 'Joone tüüp',
    mode: { foot: 'Jalgsi', bike: 'Rattaga', car: 'Autoga', straight: 'Sirgjoon' },
    undo: 'Võta tagasi',
    finish: 'Lõpeta joonistamine',
    draw: 'Joonista kaardil',
    hint: 'Klõps kaardil lisab punkti; joon läheb mööda teid ja radu (OSRM). Punkti saab lohistada.',
    anchors: 'Tee punktid',
    none: 'Punkte veel pole: alustamiseks klõpsa kaardil.',
    point: 'Punkt {{n}}',
    remove: 'Eemalda {{name}}',
    segment: '{{km}} · ↑ {{up}} m ↓ {{down}} m · {{time}}',
    file: 'Või võta joon failist',
    drop: 'Lohista fail siia',
    choose: 'Vali fail',
    kinds: 'GPX, KML, KMZ, GeoJSON, CSV, FIT, TCX',
    read: 'loetud',
    fileInfo: '{{km}} · {{points}} punkti',
    snap: 'Joonda teedele ja radadele',
    snapHow: 'GPS-i hüplemine silutakse; kus rada pole, jääb joon nagu failis.',
    asFile: 'Nagu failis',
    onRoads: 'Teedel',
    snapFailed: 'Ükski tee ega rada ei sobinud: joon jääb nagu failis.',
    fileFailed: 'Seda faili ei saanud teeks lugeda: vaja on vähemalt kahte punkti.',
    photos: 'Punktid fotodest',
    placed_one: '{{count}} foto sai punktiks tee äärde selle asukoha järgi.',
    placed_other: '{{count}} fotot said punktiks tee äärde nende asukoha järgi.',
    unplaced_one: '{{count}} fotol asukohta pole; see jääb tee juurde ilma punktita.',
    unplaced_other: '{{count}} fotol asukohta pole; need jäävad tee juurde ilma punktita.',
    noPhotos: 'Fotosid veel pole.',
    addPhotos: 'Lisa fotosid',
    removePhoto: 'Eemalda see foto',
    photo: 'Foto, tehtud {{when}}',
    photoUnknown: 'Foto, aeg teadmata',
    profile: 'Kõrgusprofiil',
    length: 'Pikkus',
    ascent: 'Tõus',
    descent: 'Langus',
    highest: 'Kõrgeim',
    lowest: 'Madalaim',
    heightsFrom: 'Kõrgus: Terrarium-andmed; failis olevaid kõrgusi eelistatakse.',
    noHeights: 'Kõrgusi veel pole.',
    profileSaid: 'Kõrgus mööda teed: {{low}}–{{high}} m',
    cancel: 'Loobu',
    save: 'Salvesta tee',
    saved: 'Tee on salvestatud: {{name}}.',
    toPlan: 'Vaata plaanil',
    failed: 'Serveriga ei saanud ühendust.',
    mapLabel: 'Tee kaart peatusest {{from}}',
    link: 'Joonista tee kaardil',
  },
  mapAdd: {
    title: 'Lisa koht peatusesse {{city}}',
    how: 'Lisamise viis',
    search: 'Otsing',
    paste: 'Link või koordinaadid',
    searchLabel: 'Koha nimi',
    searching: 'Otsin OpenStreetMapist…',
    noSpots: '{{city}} lähedalt ei leitud midagi.',
    pasteLabel: 'Kleebi Google Mapsi link, koordinaadid või Plus Code',
    reading: 'Loen…',
    readAs: {
      decimal: 'kümnendkraadid',
      seconds: 'kraadid, minutid, sekundid',
      minutes: 'kraadid ja minutid',
      plusCode: 'täielik Plus Code',
      shortCode: 'lühike Plus Code + linn',
      link: 'loetud lingist',
    },
    unread: 'See pole link, koordinaadid ega Plus Code: otsin seda nime järgi.',
    readHow:
      "Nimi ja koordinaadid tulevad lingist endast; Google'i võtit pole vaja. Aadress tuleb OpenStreetMapist.",
    clear: 'Tühjenda',
    addTo: 'Lisa peatusesse {{city}}',
    formats: 'Toetatud vormingud',
    longLink: 'pikk link',
    nameOnly:
      'Kui väljal on ainult number, mis ei saa olla koordinaat, otsitakse seda nime järgi.',
    hint: 'Vihje',
    hintText:
      'Vajuta kaardil kohta (või hoia seal, või paremklõps): näed, mis seal on, ja saad selle kohe lisada.',
    here: 'Siin',
    near: 'Lähedal',
    add: 'Lisa',
    addPoint: 'Lisa see punkt peatusesse {{city}}',
    moveStop: 'Pane peatus siia',
    close: 'Sulge',
    point: 'Punkt kaardil',
    added: 'Lisatud peatusesse {{city}}: {{name}}. Määra see peatuse mõnele päevale.',
    moved: '{{city}} on nüüd aadressil {{address}}.',
    toDays: 'Vaata peatuse päevi',
    link: 'Lisa koht kaardilt',
    failed: 'Serveriga ei saanud ühendust.',
    mapLabel: '{{city}} kaart',
  },
  myPlaces: {
    menu: 'Minu kohad',
    title: 'Minu kohad',
    count_one: '{{count}} koht',
    count_other: '{{count}} kohta',
    countries_one: '{{count}} riik',
    countries_other: '{{count}} riiki',
    lead: 'Kohad, mida tahad kunagi näha. Need on reisidest eraldi: lisa koht reisile, kui see sobib teele.',
    search: 'Otsi nime, linna või sildi järgi',
    tags: 'Sildid',
    all: 'Kõik',
    newTag: 'Uus silt',
    favouritesOnly: 'Ainult lemmikud',
    showVisited: 'Näita külastatud kohti',
    visitedOn: 'külastatud {{day}}',
    favourite: 'Lemmik',
    none: 'Kohti pole veel. Lisa need Google Mapsi või Naveri nimekirjast, failist või reisi kaardilt.',
    noneFound: 'Ükski koht ei sobi.',
    mapLabel: 'Minu kohtade kaart',
    mapNote:
      'Lähestikku kohad on üks ring arvuga. Suumi lähemale või vajuta ringile, et näha igat kohta eraldi. Käidud kohad on tuhmid.',
    place: 'Koht',
    photoOf: '{{name}}: foto {{n}}/{{count}}',
    morePhotos: '+{{count}}',
    edit: 'Muuda',
    addTag: 'Silt',
    markFavourite: 'Lemmik',
    markVisited: 'Märgi külastatuks',
    visited: 'Külastatud',
    addTo: 'Lisa reisile: {{trip}} · {{city}}, {{days}}',
    onTrip: 'Reisil {{trip}} · {{city}}',
    noStop: 'Ühegi reisi peatus pole 30 km raadiuses.',
    similar: 'Sarnane koht on juba olemas',
    similarSays:
      '„{{name}}“ {{metres}} m kaugusel, lisatud {{day}} {{source}}. Kas see on sama koht?',
    merge: 'Ühenda üheks',
    keepApart: 'Jäta eraldi',
    mergeHow:
      'Ühendamisel jäävad alles mõlema fotod, sildid ja märkmed; reisid, kus koht on, viitavad edasi ühele kohale.',
    note: 'Märkmed',
    hours: 'Lahtiolekuajad',
    hoursFrom: '{{hours}} (OpenStreetMap)',
    noHours: 'OpenStreetMapis märkimata',
    asking: 'Küsin OpenStreetMapilt…',
    trips: 'Reisid',
    noTrips: 'Pole ühelgi reisil',
    added: 'Lisatud',
    addedHow: '{{day}}, {{source}}',
    source: {
      google: 'Google Mapsi nimekirjast',
      naver: 'Naver Mapi nimekirjast',
      file: 'failist',
      search: 'otsingust',
      map: 'kaardilt',
    },
    addPhotos: 'Lisa fotosid',
    removePhoto: 'Eemalda see foto',
    photoFailed: 'See fail pole foto.',
    remove: 'Eemalda koht',
    removeAsk:
      'Kas eemaldada „{{name}}“ sinu kohtade hulgast? Ka selle fotod eemaldatakse; tegevused, milleks see sai, jäävad reisidele.',
    editTitle: 'Muuda kohta',
    name: 'Nimi',
    save: 'Salvesta',
    cancel: 'Loobu',
    tagTitle: 'Uus silt',
    tagEditTitle: 'Muuda silti',
    tagName: 'Nimi',
    emoji: 'Emoji',
    colour: 'Värv',
    taken: 'Sul on juba sama nimega silt.',
    removeTag: 'Eemalda silt',
    tagsOf: 'Koha {{name}} sildid',
    failed: 'Serveriga ei saanud ühendust.',
    noneChosen: 'Vali koht nimekirjast või kaardilt.',
  },
  offline: {
    title: 'Võrguühenduseta kaardid',
    lead: 'Iga peatuse ümbrus laaditakse telefoni, nii et kaart, kohad ja teed töötavad lennurežiimis ja ilma SIM-ita.',
    areas: '{{trip}} · alad',
    total: 'kokku {{size}}',
    noPlaces: 'Ükski reisi peatus pole veel kaardil.',
    route: 'Teekonna ülevaade',
    routeHow_one: '{{count}} peatus ja selle teed · suurendus kuni linnani',
    routeHow_other: 'kõik {{count}} peatust ja nendevahelised teed · suurendus kuni linnani',
    stopHow: '{{days}} · {{km}} km raadius · tänavateni',
    loading: 'laadin · {{done}} / {{all}}',
    waiting: 'Ootel',
    old_one: 'Aegunud · {{count}} päev',
    old_other: 'Aegunud · {{count}} päeva',
    kept: 'Alla laaditud',
    none: 'Pole laaditud',
    progress: 'Laadin: {{name}}',
    radiusOf: 'Raadius: {{name}}',
    km: '{{km}} km',
    remove: 'Eemalda {{name}}',
    loadAll: 'Laadi kõik alla · {{size}} veel',
    renew: 'Uuenda aegunud',
    changeRadius: 'Muuda raadiust',
    waitingWifi: 'Mobiilne võrk: kaardid ootavad Wi-Fi-t.',
    settings: 'Seaded',
    wifiOnly: 'Ainult Wi-Fi kaudu',
    wifiOnlyHow: 'Mobiilse andmesidega ei laadita; ootel alad jätkavad Wi-Fi tulles.',
    autoNew: 'Uued peatused automaatselt',
    autoNewHow: 'Peatus, mis reisile lisandub, laaditakse järgmise Wi-Fi ajal.',
    renewing: 'Uuendamine',
    renewingHow:
      'Kaart aegub {{count}} päeva pärast; vana kaart töötab edasi, kuni uus on laaditud.',
    storage: 'Salvestusruum selles telefonis',
    noStorage: 'Brauser ei avalda salvestusruumi mahtu.',
    maps: 'Kaardid',
    documents: 'Dokumendid ja tšekid',
    free: 'Vaba umbes {{size}} GB',
    persisted: 'püsiv salvestus lubatud',
    notPersisted: 'ruumi puudusel võidakse kustutada',
    persist: 'Luba püsiv salvestus',
    persistHow: 'Püsiva salvestusega ei kustuta brauser kaarte ruumi puudusel ise ära.',
    radiusUsual: 'Peatust hoitakse {{km}} km raadiuses, kui raadiust ei muudeta.',
    today: 'Ühenduseta · kaart telefonist, {{day}}',
    next: 'Järgmine: {{name}}',
    walk: 'jalgsi {{km}} km · {{minutes}} min · asukoht GPS-ist',
    whereAmI: 'Minu asukoht',
    noGps: 'Asukohta ei õnnestunud leida.',
    gpsHow: 'GPS töötab ilma võrguta; teekond jalgsi on päeva plaanist, kaart telefonist.',
  },
  emissions: {
    title: 'Süsinikujälg',
    lead: 'Iga sõidu CO₂ ühe reisija kohta: reisi kokku, liikide kaupa ja mida üks vahetus muudaks.',
    open: 'Süsinikujälg',
    oneTraveller: '{{name}} · üks reisija',
    unit: 'kg CO₂e',
    perDay: '{{kg}} kg päevas · sõidud kokku {{km}} km',
    split: 'Jaotus liikide kaupa',
    flights_one: 'Lennud · {{count}}',
    flights_other: 'Lennud · {{count}}',
    trains_one: 'Rongid · {{count}}',
    trains_other: 'Rongid · {{count}}',
    ground: 'Bussid, praamid, autod ja linnas sõit',
    stays_one: 'Majutus · {{count}} öö',
    stays_other: 'Majutus · {{count}} ööd',
    basis:
      'Arvutatud sõidu pikkusest ja liigist. Lend: kaugus lennujaamast lennujaama, ümberistumised ja istmeklass. Majutus: allpool.',
    rf: 'Arvesta kõrgel lendamise mõju (RF)',
    ways: 'Käigud ja majutus',
    largest: 'suurimad eespool',
    way: 'Käik',
    kind: 'Liik',
    km: 'km',
    kg: 'kg CO₂e',
    via: 'ümberistumine: {{cities}}',
    cabin: {
      economy: 'turistiklass',
      premium: 'premium-turistiklass',
      business: 'äriklass',
      first: 'esimene klass',
    },
    stay_one: 'Majutus · {{country}}, {{count}} öö',
    stay_other: 'Majutus · {{country}}, {{count}} ööd',
    stayHow: '{{nights}} × {{per}} kg toa-öö ÷ {{sharing}} toas',
    estimated: 'hinnang',
    hotel: 'Hotell',
    towns_one: 'Linnades · {{count}} sõit',
    towns_other: 'Linnades · {{count}} sõitu',
    townsKind: 'Metroo, buss, takso',
    swaps: 'Võimalikud vahetused',
    swap: '{{from}} → {{to}}: {{mode}}',
    saves: '−{{kg}} kg',
    longer: '+{{time}}',
    dearer: '+{{amount}}',
    cheaper: '−{{amount}}',
    openRoutes: 'Ava teede võrdluses',
    noSwaps: 'Ühelgi lennul pole mõistlikku asendust maad või merd mööda.',
    routesNote: 'Marsruutide lehel on hinna kõrval ka CO₂ ja järjestus selle järgi.',
    data: 'Andmed ja arvutus',
    note1:
      'Tegurid on repositooriumi tabelis data/emissions.tsv: kg CO₂e reisija-km kohta liigi ja istmeklassi järgi, iga rida oma allikaga (DESNZ/Defra 2025 heitetegurid). Võtit ega teenust pole vaja.',
    note2:
      'Lend: kaugus lennujaamast lennujaama koos piletil olevate ümberistumistega, 8 % pikem, nagu DESNZ soovitab; ühe riigi sees siselennu teguriga, muidu rahvusvahelise teguriga istmeklassi järgi. Kõrgel lendamise mõju on ülal sees või väljas.',
    note3:
      'Majutus: ööd × riigi tegur toa ja öö kohta ÷ toas olijad (kuni kaks reisijat). Riigil, mida tabelis pole, on tabeli keskmine, märgitud „hinnang“.',
    note4:
      'Rong, buss, praam ja linnasõidud Defra keskmiste teguritega; auto jagatakse reisijate vahel.',
    note5: 'Arvud on hinnang ja ümardatud kilodeni; need ei mõjuta eelarvet.',
    loading: 'Arvutan…',
  },
  importPlaces: {
    title: 'Impordi kohti',
    kinds: 'Allikas',
    list: 'Google Mapsi või Naveri nimekiri',
    file: 'Fail: GPX, KML, KMZ',
    link: 'Listi jagamislink',
    read: 'Loe nimekiri',
    reading: 'Loen…',
    summary_one: '„{{name}}“ · {{count}} koht',
    summary_other: '„{{name}}“ · {{count}} kohta',
    toStops_one: '{{count}} läheb peatusesse',
    toStops_other: '{{count}} lähevad peatustesse',
    farOff_one: '{{count}} on teekonnast kaugel',
    farOff_other: '{{count}} on teekonnast kaugel',
    all: 'Vali kõik',
    take: 'Lisa',
    place: 'Koht',
    kind: 'Liik nimekirjas',
    stop: 'Peatus',
    day: 'Päev',
    noDay: 'Päev määramata',
    newStop: 'Uus peatus: {{city}}',
    newStopAfter: 'Uus peatus: {{city}} · pärast {{after}}',
    away: '{{city}} · {{km}} km, lähim peatus {{from}}',
    none: 'Jäta peatuseta',
    noneDay: '—',
    note: 'Kohad seotakse lähima peatusega, mille kaugus on alla 30 km. Kaugemale jääva saab lisada uue peatusena teekonna õigesse kohta või jätta peatuseta. Nimi, aadress ja koordinaadid tulevad nimekirjast; iga koht jääb ka sinu kohtade hulka.',
    drop: 'Lohista fail siia või vali see',
    choose: 'Vali fail',
    dropHint: 'GPX, KML või KMZ · kuni 20 MB · Google My Maps, Komoot, AllTrails, Garmin',
    fileRead: '{{file}} · {{trails}}, {{points}}',
    trails_one: '{{count}} rada',
    trails_other: '{{count}} rada',
    points_one: '{{count}} punkt',
    points_other: '{{count}} punkti',
    trail: 'Rada „{{name}}“',
    trailFacts: '{{km}} km · {{days}} jalgsi. Lisatakse matkana, mitte lennuna.',
    trailClimb:
      '{{km}} km · tõus {{ascent}} m · {{days}} jalgsi. Lisatakse matkana, mitte lennuna.',
    walk: 'Jalgsi',
    from: 'Alguspeatus',
    endTown: 'Peatus raja lõpus',
    pointInFile: 'Punkt failis',
    nothingYet: 'Midagi ei lisata enne, kui vajutad „Lisa“.',
    cancel: 'Loobu',
    add_one: 'Lisa {{count}} koht',
    add_other: 'Lisa {{count}} kohta',
    addTrail: 'Lisa rada',
    addBoth_one: 'Lisa rada ja {{count}} koht',
    addBoth_other: 'Lisa rada ja {{count}} kohta',
    badLink: 'See pole Google Mapsi ega Naveri nimekirja link.',
    notList: 'Selle lingi taga nimekirja ei leitud. Kas nimekiri on lingiga jagatud?',
    badFile: 'Selles failis pole rada ega nimega kohti.',
    failed: 'Serveriga ei saanud ühendust.',
    archived:
      'See reis on arhiveeritud. Kohtade lisamiseks võta see reisi seadetes arhiivist välja.',
    waiting: 'Päevale määramata kohad',
    waitingHint:
      'Imporditud või sinu enda kohad 15 km sees: vali päev, siis on see sel päeval tegevus.',
    onDay: 'Lisa päevale',
    pickDay: 'Päev kohale {{name}}',
    takeOff: 'Eemalda {{name}}',
  },
  exportTrip: {
    title: 'Ekspordi reis „{{name}}“',
    kinds: 'Failitüüp',
    pdf: 'PDF reisiraamat',
    gpx: 'GPX kaardirakendusele',
    geo: 'KML, GeoJSON',
    table: 'Tabel (CSV, Excel)',
    ics: 'Kalender (.ics)',
    contents: 'Raamatu sisu',
    cover: 'Kaas ja teekonna kaart',
    coverHint: 'Reisi nimi, kuupäevad, reisijad, kõik peatused kaardil',
    days: 'Päevad',
    daysHint:
      'Iga päev: koht, öö, edasine sõit kohaliku ajaga, tegevused, ilm kui see on teada',
    bookings: 'Broneeringud ja piletid',
    bookingsHint: 'Broneeringu number, aadress, sisseregistreerimine, istekoht, pagas',
    entry: 'Sisenemine ja dokumendid',
    entryHint: 'Iga riigi reegel sinu passile, viisapäevad, mida enne piiri teha',
    money: 'Raha',
    moneyHint:
      'Eelarve, makstud, maksta jäänud, riikide kaupa. Välja jäetuna pole raamatus ühtki summat',
    format: 'Vorming',
    language: 'Keel',
    paper: 'Paber',
    dayAPage: 'Iga päev uuelt lehelt',
    branches: 'Koos haru-reisidega',
    branchesHint: '{{names}} lisaks oma peatükkidena',
    preview: 'Eelvaade',
    coverPage: '{{page}} · Kaas',
    dayPage: '{{page}} · Päev',
    bookingPage: '{{page}} · Broneering',
    entryPage: '{{page}} · Riik',
    entryOf: '{{country}} · sisenemine',
    pages_one: 'Umbes {{count}} leht · {{size}}.',
    pages_other: 'Umbes {{count}} lehte · {{size}}.',
    serverMade:
      'PDF-i teeb server; brauser ainult laadib selle alla, nii et fail on telefonis ja arvutis sama.',
    counting: 'Arvutan lehtede arvu…',
    stops: 'Peatused punktidena',
    stopsHint: 'Nimi, ööd, kuupäevad; Organic Maps, OsmAnd, Google My Maps näitavad neid',
    ways: 'Teed maad ja vett mööda radadena',
    waysHint: 'Rong, buss, praam, auto: teekonna joon nagu kaardil, iga sõit oma rada',
    flights: 'Lennud sirgjoonena',
    flightsHint: 'Paljud rakendused joonistavad need üle kogu kaardi; vaikimisi välja',
    sights: 'Päevade tegevused punktidena',
    sightsHint_one: '{{count}} koht, päeva ja peatuse nimega',
    sightsHint_other: '{{count}} kohta, igaüks päeva ja peatuse nimega',
    gpxFile: 'Fail: {{file}} · GPX 1.1 · kellaajad UTC-s, nagu GPX nõuab.',
    kml: 'KML (Google Earth, My Maps)',
    kmz: 'KMZ koos fotodega',
    geojson: 'GeoJSON',
    places: 'Peatused ja päevade kohad',
    placesHint: 'Iga koht oma kaustas päeva järgi; nimi, kuupäevad, märkmed',
    lines: 'Teed joontena, värv liikumisviisi järgi',
    linesHint: 'Rong roheline, buss oranž, praam türkiis, nagu TripPathi kaardil',
    amounts: 'Summad',
    amountsHint: 'GeoJSON-is omadused cost ja currency; KML-is kirjelduses',
    geoNote:
      'GeoJSON: üks FeatureCollection, punktid ja jooned, iga objekti juures kind, arrivalAt ja departureAt koos ajavööndi nihkega. Sobib QGIS-i, uMapi ja oma kaardi jaoks.',
    whatGoes: 'Tabeli sisu',
    costs: 'Kulud',
    costsHint:
      'Kuupäev, riik, kategooria, summa kohalikus valuutas ja reisi valuutas, kurss, kes maksis, jagamine',
    tableBookings: 'Broneeringud',
    tableBookingsHint: 'Mis, millal kohaliku ajaga, kinnituskood, hind, makstud või maksta',
    tableDays: 'Päevad ja tegevused',
    tableDaysHint: 'Üks rida tegevuse kohta',
    xlsx: 'Excel (.xlsx), iga osa oma lehel',
    csv: 'CSV, iga osa oma failina (ZIP)',
    tableNote:
      'CSV: UTF-8, eraldaja koma, kümnendmurd punktiga. Excelis on summad arvud, kuupäevad kuupäevad ja kokkuvõtte leht valemitega.',
    noRows: 'Veel pole midagi näidata.',
    icsLead:
      'See on olemas ka Kalendri lehel. Siin on sama: ühekordne fail või tellimus, mida kalender ise uuendab.',
    feed: 'Tellimuse link',
    feedNone: 'Linki veel pole.',
    feedNote:
      'Lingi teadja näeb kuupäevi ja kohti, mitte summasid ega kinnituskoode. Uue lingi tegemine tühistab vana.',
    newFeed: 'Loo uus link',
    icsOnce: 'Laadi alla .ics fail',
    cancel: 'Loobu',
    download: 'Laadi alla {{what}}',
    copy: 'Kopeeri link',
    copied: 'Link kopeeritud',
    makeFeed: 'Loo link',
    failed: 'Serveriga ei saanud ühendust.',
  },
  sharing: {
    title: 'Jagamine ja ligipääs',
    members: 'Liikmed',
  },
  history: {
    title: 'Muudatused',
  },
  docs: {
    open: 'Dokumendid',
    title: 'Dokumendid',
    back: 'Tagasi reisi juurde',
    rules: 'Selle marsruudi sisenemisreeglid',
    rulesLead: 'Päevad arvestatud plaanist · meeldetuletus enne iga piiri, kus midagi vaja',
    passport: 'Pass',
    noPassport: 'Vali pass, millega reisid, et näha, mida iga piir nõuab.',
    country: 'Riik',
    border: 'Sisenemine',
    stay: 'Päevi riigis',
    todo: 'Nõuded enne sisenemist',
    reminder: 'Tehtud',
    pickPassport: 'vali pass',
    noCountries: 'Lisa marsruudile peatusi, et näha selle piire.',
    undecided: 'otsustamata',
    days: '{{days}} / {{max}} lubatud',
    over_one: '{{count}} päev üle piiri',
    over_other: '{{count}} päeva üle piiri',
    unknown: 'Teadmata: vaata ametlikke allikaid või määra ise',
    kind: {
      free: 'Viisat ega luba pole vaja',
      card: 'Saabumiskaart: {{form}}',
      eta: 'Reisiluba: {{form}}',
      evisa: 'E-viisa: {{form}}',
      arrival: 'Viisa piiril',
      visa: 'Viisa saatkonnast: {{form}}',
    },
    official: 'ametlik leht',
    officialFor: 'Ametlik leht: {{country}}',
    wikipedia: 'Wikipedia',
    wikipediaFor: 'Wikipedia: {{country}}',
    yours: 'sinu määratud',
    ownRule: 'Sinu reegel: {{country}}',
    whose: 'Reisija',
    mine: 'Minu',
    actions: 'Toimingud',
    needs: 'Nõue',
    stayDays: 'Lubatud päevi',
    forgetOwn: 'Kustuta minu reegel',
    kindName: {
      free: 'Pole vaja',
      card: 'Saabumiskaart',
      eta: 'Reisiluba',
      evisa: 'E-viisa',
      arrival: 'Viisa piiril',
      visa: 'Viisa saatkonnast',
    },
    due: '{{day}}',
    doneLabel: 'tehtud',
    tick: '{{todo}}, {{country}}: tehtud',
    indicative: 'Ainult suunav. Kontrolli enne reisi ametlikke allikaid.',
    documents: 'Dokumendid',
    scan: 'Pildista kaameraga',
    upload: 'Laadi üles',
    addPlain: 'Lisa ilma failita',
    identity: 'Isikudokumendid ja kindlustused',
    linked: 'Kõik dokumendid',
    noDocuments: 'Dokumente veel pole.',
    passportOk: 'kehtib kuni {{day}} · 6 kuu reegel korras',
    passportShort: 'aegub {{day}}: alla 6 kuu pärast reisi',
    passportNoDate: 'lisa aegumise kuupäev',
    notAdded: 'lisamata',
    optional: 'valikuline',
    insuranceMissing: 'Reisikindlustus puudub',
    addPolicy: 'Lisa poliis',
    privateToYou: 'ainult sulle',
    sharedBy: 'lisas {{name}}',
    openFile: 'Ava {{name}}',
    edit: 'Muuda: {{name}}',
    remove: 'Eemalda {{name}}',
    removeTitle: 'Kas eemaldada see dokument?',
    removeBody: '{{name}} ja selle fail eemaldatakse kõigilt, kes seda näevad.',
    offline: 'Hoia failid selles telefonis',
    offlineDone: 'Salvestatud selles telefonis kasutamiseks ilma võrguta',
    kinds: {
      passport: 'Pass',
      id_card: 'ID-kaart',
      visa: 'Viisa',
      driving_licence: 'Juhiluba',
      permit: 'Rahvusvaheline juhiluba',
      vehicle: 'Sõiduki registreerimistunnistus',
      insurance: 'Reisikindlustus',
      motor_insurance: 'Liikluskindlustus (roheline kaart)',
      casco_insurance: 'Kaskokindlustus',
      accident_insurance: 'Õnnetusjuhtumikindlustus',
      bike_insurance: 'Jalgrattakindlustus',
      electronics_insurance: 'Elektroonikakindlustus',
      liability_insurance: 'Vastutuskindlustus',
      vaccination: 'Vaktsineerimiskaart',
      ticket: 'Pilet',
      booking: 'Broneering',
      other: 'Muu',
    },
    add: 'Lisa dokument',
    change: 'Muuda dokumenti',
    what: 'Liik',
    name: 'Nimi',
    note: 'Märkus',
    valid: 'Kehtivusaeg',
    validFrom: 'Kehtib alates',
    validTo: 'Kehtib kuni',
    validSince: 'kehtib alates {{day}}',
    validUntil: 'kehtib kuni {{day}}',
    validReversed: 'Viimane kehtivuspäev on enne esimest',
    price: 'Hind',
    stop: 'Peatus',
    noStop: 'Peatuseta',
    private: 'Ainult mina näen seda',
    file: 'Fail',
    attach: 'Lisa fail',
    replace: 'Asenda fail',
    removeFile: 'Eemalda fail',
    attached: '{{name}} lisatud',
    kept: 'Fail on olemas',
    save: 'Salvesta',
    failed: 'Salvestamine ebaõnnestus. Proovi uuesti.',
    before: 'Enne lahkumist',
    beforeIn_zero: 'Enne lahkumist · täna',
    beforeIn_one: 'Enne lahkumist · 1 päev',
    beforeIn_other: 'Enne lahkumist · {{count}} päeva',
    newItem: 'Uus ülesanne',
    addItem: 'Lisa',
    usual: 'Lisa levinumad',
    usualItems: {
      bank: 'Teata pangale reisist',
      card: 'Telli tasuta välismaksetega kaart',
      maps: 'Laadi alla võrguühenduseta kaardid',
      esim: 'Hangi eSIM või kohalik SIM',
    },
    removeItem: 'Eemalda {{text}}',
    reminders: 'Enne piiri',
    remindersLink: 'Ava dokumendid',
    reminderLine: '{{todo}}, {{country}} · {{due}}',
  },
  activity: {
    title: 'Muudatused',
    online: 'Vaatab praegu seda reisi: {{names}}',
    none: 'Muudatusi veel pole.',
    someone: 'Keegi',
    changed: '{{name}} tegi muudatuse',
    code: {
      trip_change: '{{name}} muutis reisi',
      route_add: '{{name}} lisas teekonnale',
      route_change: '{{name}} muutis teekonda',
      areas_arrive: '{{name}} jõudis: {{about}}',
      areas_leave: '{{name}} lahkus: {{about}}',
      areas_change: '{{name}} muutis alasid',
      route_compare: '{{name}} võrdles teid',
      bookings_add: '{{name}} lisas broneeringu',
      bookings_change: '{{name}} muutis broneeringuid',
      travellers_change: '{{name}} muutis kaasreisijaid',
      people_change: '{{name}} muutis reisi liikmeid',
      costs_add: '{{name}} lisas ühise kulu',
      costs_settle: '{{name}} märkis võla tasutuks',
      costs_bill: '{{name}} lisas tšeki foto',
      costs_change: '{{name}} muutis ühiseid kulusid',
      share_open: '{{name}} lülitas vaatamislingi sisse',
      documents_add: '{{name}} lisas dokumendi',
      documents_change: '{{name}} muutis dokumente',
      before_change: '{{name}} muutis lahkumiseelset nimekirja',
      journal_change: '{{name}} kirjutas päevikusse',
      trip_archive: '{{name}} arhiveeris reisi',
      trip_restore: '{{name}} taastas reisi',
      day_change: '{{name}} muutis päeva plaani',
      car_change: '{{name}} muutis autot',
      leave_change: '{{name}} muutis puhkepäevi',
      branches_change: '{{name}} muutis reisi harusid',
      mail_add: '{{name}} saatis broneeringu e-kirjaga',
      flights_change: 'Lennu olek muutus',
    },
  },
  costs: {
    title: 'Ühised kulud',
    lead: 'Mida reisijad üksteise eest maksavad: kes maksis, kuidas jagada ja kes kellele võlgneb.',
    add: 'Lisa kulu',
    none: 'Ühiseid kulusid veel pole.',
    day: 'Kuupäev',
    item: 'Kirjeldus',
    itemHint: 'nt õhtusöök Shinjukus',
    paidBy: 'Maksja',
    split: 'Jaotus',
    equally: 'Võrdselt: {{names}}',
    amount: 'Summa',
    currency: 'Valuuta',
    charged: 'Tegelik summa',
    chargedHint:
      'Kui palju see tegelikult maksis – kaardilt maha läinud või sularahas. Tühjaks jättes arvutatakse selle päeva kursiga.',
    noRate: 'Sellel päeval pole selle raha kurssi: kirjuta tegelik summa.',
    bill: 'Tšekk',
    total: 'Kokku',
    remove: 'Eemalda',
    removeOne: 'Eemalda {{item}}',
    to: '→ {{name}}',
    settled: 'Tagasi makstud',
    showBill: 'Vaata tšekki: {{item}}',
    addBill: 'Lisa tšeki foto: {{item}}',
    viewBill: 'Vaata',
    addPhoto: 'Lisa foto',
    balances: 'Saldod',
    person: 'Reisija',
    paidSum: 'Makstud',
    share: 'Osa',
    net: 'Saldo',
    settleTitle: 'Tasaarveldus',
    pays: '{{from}} → {{to}}: {{amount}}',
    square: 'Keegi ei võlgne kellelegi.',
    markSettled: 'Märgi makstuks',
    allPhotos: 'Igal kulul on tšeki foto.',
    missing_one: '{{count}} kulul puudub tšeki foto.',
    missing_other: '{{count}} kulul puudub tšeki foto.',
    newTitle: 'Uus ühine kulu',
    sharedWith: 'Jagatakse võrdselt',
    save: 'Lisa',
    billOf: 'Tšekk: {{item}}',
    billAlt: 'Tšeki foto: {{item}}',
    billKept: 'Maksja: {{name}} · foto on sinu serveris, kuni keegi selle kustutab.',
    deleteBill: 'Kustuta foto',
    notPhoto: 'Seda faili ei saa fotona lugeda: vali JPEG, PNG või WebP.',
    removeQuestion: 'Eemaldada {{item}}?',
    removeBody: 'Kulu ei lähe enam saldosse arvesse. Kustutatakse ka tšeki foto.',
    removeConfirm: 'Eemalda',
  },
  group: {
    rolesTitle: 'Rollid ja õigused',
    manage: 'Halda ligipääsu',
    access: 'Ligipääs',
    can: {
      route: 'Muuta teekonda',
      money: 'Näha raha',
      documents: 'Avada jagatud dokumente',
      people: 'Kutsuda ja jagada',
    },
    yes: 'jah',
    no: 'ei',
    open: 'Grupp',
    back: 'Tagasi reisi juurde',
    title: 'Grupp',
    lead: 'Kes sellel reisil reisib ja kes seda näeb.',
    people: 'Reisijad',
    roleOf: 'Roll: {{name}}',
    remove: 'Eemalda {{name}} reisilt',
    removeShort: 'Eemalda reisilt',
    removeQuestion: 'Eemaldada {{name}} reisilt?',
    removeBody: 'Ta ei näe seda enam. Tema konto jääb alles.',
    removeConfirm: 'Eemalda',
    lastOwner: 'Reisil peab jääma vähemalt üks omanik.',
    invited: 'Kutsutud',
    invitedAs: 'roll: {{role}} · kuni {{until}}',
    withdraw: 'Võta kutse tagasi: {{email}}',
    withdrawShort: 'Võta tagasi',
    invite: 'Kutsu reisile',
    as: 'Roll',
    byMail: 'Ta saab e-kirja liitumislingiga.',
    noMail: 'Kutse tehakse lingina: saadad selle ise e-posti või sõnumiga.',
    send: 'Saada kutse',
    makeInvite: 'Loo kutselink',
    sent: 'Kutse saadeti aadressile {{email}}.',
    sendYourself: 'Saada see link ise aadressile {{email}}. See töötab üks kord, 14 päeva.',
    inviteLink: 'Kutse link',
    copy: 'Kopeeri',
    copied: 'Kopeeritud',
    readOnly: 'Vaatamislink',
    readOnlyLead:
      'Lingiga näeb igaüks teekonda ja kuupäevi; summasid, faile ja nimesid ainult siis, kui lubad. Broneeringute numbrid on alati peidus.',
    sharedSince: 'Vaatamislink on aktiivne alates {{day}}.',
    readOnlyLink: 'Jagatav link',
    makeLink: 'Loo vaatamislink',
    newLink: 'Loo uus link',
    turnOff: 'Lülita link välja',
  },
  invitation: {
    title: '{{who}} kutsub sind reisile {{trip}}',
    someone: 'Keegi',
    as: 'Liitud rollis: {{role}}.',
    join: 'Liitu kasutajana {{name}}',
    signInFirst: 'Aadressil {{email}} on juba konto: kutse vastuvõtmiseks logi sisse.',
    newAccount: 'Loo konto aadressile {{email}} ja liitu.',
    create: 'Loo konto ja liitu',
    gone: 'See kutse ei kehti enam: seda on kasutatud, see võeti tagasi või aegus.',
  },
  seatMap: {
    needsAircraft: 'Istmeplaan · {{leg}}: lisa lennuk vahekaardil Lennud',
    open: 'Istmeplaan',
    openFor: 'Istmeplaan · {{leg}}',
    whole: 'Lennuk ülalt, nina üleval, mõõtkavas',
    close: 'Read {{row}}. rea ümber, lähemalt',
    exitRow: 'väljapääsurida',
    place: '{{seat}}: {{kind}}, {{side}}',
    placeShort: '{{kind}}, {{side}}',
    kind: { window: 'aken', middle: 'keskel', aisle: 'vahekäik' },
    side: { left: 'vasak pool', right: 'parem pool', centre: 'keskel' },
    placeCentre: '{{seat}}: keskel',
    windowBeside: 'Aken on täpselt sinu kõrval',
    kindOf: 'Koht {{seat}}',
    kindByMap: '{{kind}}, istmeplaani järgi',
    row: 'Rida {{row}}',
    wing: { ahead: 'tiivast eespool', over: 'tiiva kohal', behind: 'tiivast tagapool' },
    byDoor: 'ukse lähedal: väljud esimeste seas',
    noWindow: 'Selle rea kõrval akent täpselt pole: aknad järgivad raame, read istmete sammu',
    notOnMap: '{{seat}} pole selles paigutuses: kontrolli allpool paigutust.',
    unknown: 'Lennukile {{model}} istmekaarti veel pole.',
    type: 'Lennuk',
    size: 'Mõõtmed',
    sizeSaid: 'pikkus {{length}} m · tiivaulatus {{span}} m',
    body: 'Kere',
    bodySaid: '{{width}} m lai · raamid iga {{frame}} cm järel, aken igas vahes',
    doors: 'Uksed, väljapääsud',
    doorsSaid: '{{doors}} paari uksi · {{exits}} paari tiiva kohal',
    maker: 'tootja',
    layout: 'Paigutus',
    rows_one: '{{count}} rida',
    rows_other: '{{count}} rida',
    source: { own: 'sinu', wikipedia: 'Wikipedia', typical: 'tüübi tavapaigutus' },
    own: 'sinu koht',
    mate: 'kaaslane',
    window: 'aken',
    exit: 'avariiväljapääs',
    note: 'Joonis tuleb lennukitüübist ja lennufirma paigutusest; iga lennufirma paigutab salongi omamoodi. Täpse kaardi näitab check-in.',
    correct: 'Paranda {{airline}} paigutust',
    layoutLead:
      '{{airline}} · {{model}}. Kehtib kõigile {{airline}} lendudele selle lennukiga.',
    classes: 'Kohad klasside kaupa',
    pitch: 'Istmete samm, cm',
    skipRows: 'Vahele jäetud read',
    exitRows: 'Väljapääsuread',
    layoutWrong: 'Kirjuta kohad kujul C12 Y168 ja read kujul 11, 12.',
    failed: 'Istmeplaani ei saanud lugeda ega salvestada. Proovi uuesti.',
    save: 'Salvesta paigutus',
    restore: 'Taasta Wikipediast',
  },
  flightApis: {
    title: 'Teenused',
    lead: 'Teenused, mida TripPath küsib. Lendu otsitakse numbri ja kuupäeva järgi neist järjest: kui üks annab vea, võti puudub või kuu piir on täis, küsitakse järgmiselt; iga leitud lend salvestatakse. Järjekorda muudad lohistades.',
    failed: 'Teenuseid ei saanud lugeda ega muuta. Proovi uuesti.',
    state: { on: 'Kasutusel', off: 'Väljas', noKey: 'Võti puudub' },
    move: 'Liiguta {{name}}: lohista või vajuta Alt ja nooleklahvi',
    key: 'API-võti',
    saveKey: 'Salvesta võti',
    removeKey: 'Eemalda võti',
    signup: 'Tasuta võti: {{name}}',
    limit: 'Päringuid kuus',
    enabled: 'Kasuta seda teenust',
    used: 'Täna {{day}} · aastas {{year}}',
    aMonth: 'kuus',
    flights: 'Lennud',
    settings: 'Seaded: {{name}}',
    showKey: 'Näita võtit',
    unlimited: 'Kuu piiranguta (∞)',
    save: 'Salvesta',
    cancel: 'Loobu',
    add: 'Lisa teenus',
    addOne: 'Lisa {{name}}',
    addThis: 'Lisa',
    remove: 'Eemalda teenus',
    none: 'Ühtegi teenust pole lisatud: lende numbri järgi ei otsita. Lisa teenus, et alustada.',
    what: {
      aerodatabox: 'Lennud numbri ja kuupäeva järgi · RapidAPI',
      airlabs: 'Lähim sama numbriga lend',
      aviationstack: 'Lennud numbri ja kuupäeva järgi',
    },
    lastError: 'Viimane viga: {{error}}',
    requests: 'Viimased päringud',
    noRequests: 'Ühtki lendu pole veel otsitud.',
    at: 'Aeg',
    flight: 'Lend',
    service: 'Teenus',
    outcome: 'Tulemus',
    cache: 'Vahemälus',
    outcomes: { found: 'Leitud', none: 'Ei leitud', failed: 'Viga', cached: 'Vahemälust' },
  },
  data: {
    title: 'Andmeallikad',
    lead: 'Selle serveri teatmeandmed. Iga loendit saab uuendada allikast või failist; logist näed, kuidas läks.',
    sets: 'Loendid',
    set: 'Loend',
    records: 'Kirjeid',
    source: 'Allikas',
    updated: 'Uuendatud',
    actions: 'Toimingud',
    builtIn: 'Väljalaskega kaasas',
    notLoaded: 'Pole veel laaditud',
    refresh: 'Uuenda allikast',
    actionsOf: 'Uuenda: {{name}}',
    uploadHint: 'CSV, gzip või Excel',
    upload: 'Laadi fail üles',
    uploadOf: 'Laadi üles {{name}} fail',
    failed: 'Uuendust ei saanud alustada. Proovi uuesti.',
    ownerOnly: 'Andmeallikaid näeb ainult see konto, mis serverit haldab.',
    log: 'Logi',
    tabs: { lists: 'Loendid', services: 'Teenused', log: 'Logi' },
    noRuns: 'Uuendusi pole veel tehtud.',
    again: 'Proovi uuesti',
    notTheFile: 'failist ei leitud ühtki selle loendi kirjet — see pole õige fail',
    name: {
      airlines: 'Lennufirmad',
      aircraft: 'Lennukitüübid',
      registrations: 'Lennukite registreeringud',
      dimensions: 'Lennukite mõõtmed',
      ships: 'Laevad',
      shipping_lines: 'Laevafirmad',
      rail_operators: 'Raudtee-ettevõtted',
      bus_operators: 'Bussifirmad',
      train_types: 'Rongitüübid',
      stations: 'Raudteejaamad',
      transit: 'Ühistranspordi vedajad',
      price_levels: 'Hinnatasemed',
      europe_price_levels: 'Euroopa hinnatasemed',
      car_models: 'Automudelid',
      car_rentals: 'Autorendifirmad',
      bike_shares: 'Rendirattad ja -tõukerattad',
      banknotes: 'Rahatähed ja mündid',
    },
    what: {
      airlines: 'OpenFlights · airlines.dat',
      aircraft: 'OpenFlights · planes.dat',
      registrations: 'tar1090-db · aircraft.csv.gz — registreeringu järgi täitub lennukitüüp',
      dimensions:
        'FAA Aircraft Characteristics Database · pikkus, tiivaulatus, mootorid, kaal ICAO koodi järgi — istmekaart mõõtkavas',
      ships: 'Wikidata · reisilaevad ja parvlaevad IMO numbri järgi ning nende käitajad',
      shipping_lines: 'Wikidata · laevafirmad ja parvlaevade käitajad',
      rail_operators: 'Wikidata · raudtee-ettevõtted ja rongivedajad',
      bus_operators: 'Wikidata · linna- ja kaugliinibussifirmad',
      train_types: 'Wikidata · rongitüübid ja nende tootjad',
      stations: 'trainline-eu/stations · Euroopa raudteejaamad asukoha ja ajavööndiga',
      transit: 'MobilityData · ühistranspordi sõiduplaanide (GTFS) vedajad',
      price_levels:
        'Maailmapank ICP 2021 · riikide hinnatase — majutus, toit, transport, vaba aeg — hinnangu andmiseks riigile, mille hindu TripPathil pole',
      europe_price_levels:
        'Eurostat · Euroopa hinnatasemed, Maailmapanga omadest värskemad, eelistatud Euroopas',
      car_models: 'Wikidata · automudelid ja nende margid',
      car_rentals: 'Wikidata · autorendifirmad',
      bike_shares:
        'MobilityData · iga rendiratta- ja tõukerattasüsteemi avalik GBFS-voog — jaamad, rattad ja hinnad',
      banknotes:
        'Wikipedia · iga valuuta käibel olevad rahatähed ja mündid artikli infokastist (CC BY-SA)',
    },
    way: { source: 'allikast', file: 'failist' },
    status: { running: 'Käib', ok: 'Korras', failed: 'Viga' },
    step: {
      fetch: 'Laaditakse {{source}}',
      fetched: 'Saadud {{size}}',
      file: 'Fail {{name}}, {{size}}',
      read_one: 'Loetud {{n}} kirje',
      read_other: 'Loetud {{n}} kirjet',
      kept: 'Salvestatud {{seconds}} s-ga',
      failed: 'Viga: {{error}}',
      stopped: 'Server peatus enne lõppu',
    },
  },
  reset: {
    forgot: 'Unustasid parooli?',
    title: 'Parooli taastamine',
    lead: 'Saadame lingi, millega saad uue parooli panna.',
    email: 'E-post',
    send: 'Saada link',
    sent: 'Kui sellel aadressil on konto, tuleb kiri mõne minuti jooksul. Vaata ka rämpsposti.',
    noMail: 'See server ei saada kirju. Palu serveri haldajal sulle uus parool panna.',
    back: 'Tagasi sisselogimisse',
    newTitle: 'Uus parool',
    password: 'Uus parool',
    hint: 'Vähemalt 10 märki. Parool, mis on teadaolevates lekkinud paroolide nimekirjades, ei sobi.',
    strength: 'Parooli tugevus: {{said}}',
    strong: { 0: 'liiga lühike', 1: 'nõrk', 2: 'keskmine', 3: 'hea', 4: 'tugev' },
    others: 'Logi välja kõigist teistest seadmetest',
    save: 'Salvesta parool',
    done: 'Uus parool on pandud.',
    signIn: 'Logi sisse',
    gone: 'See link on lõppenud või juba kasutatud.',
    again: 'Küsi uus link',
  },
  account: {
    title: 'Logi sisse',
    firstTitle: 'Loo esimene konto',
    firstLead:
      'Selles serveris pole veel ühtki kontot. Esimesele kuuluvad kõik juba olemasolevad reisid.',
    name: 'Sinu nimi',
    email: 'E-post',
    password: 'Parool',
    passwordHint: 'Vähemalt 10 märki.',
    create: 'Loo konto',
    signIn: 'Logi sisse',
    signOut: 'Logi välja',
    dataSources: 'Andmeallikad',
    menuOf: 'Konto: {{name}}',
    wrong: 'See e-post ja parool ei klapi ühegi kontoga.',
    turnedOff: 'See konto on välja lülitatud. Küsi selle TripPathi haldajalt.',
    shared: 'jagatud sinuga · {{role}}',
    role: { owner: 'omanik', editor: 'muutja', viewer: 'vaataja' },
  },
  areas: {
    title: 'Alad',
    lead: 'Ring ümber peatuse või majutuse. Kui reisija telefon salvestab teekonda ja ületab selle piiri, saavad kõik reisil olijad teada. Kedagi ei jälgita: öeldakse ainult piiri ületamine.',
    none: 'Alasid veel pole.',
    around: 'Ümber',
    aroundPick: 'Vali peatus või majutus',
    name: 'Ala nimi',
    radius: 'Raadius',
    tell: 'Teata, kui keegi',
    tells: { arrive: 'jõuab', leave: 'lahkub', both: 'jõuab või lahkub' },
    told: 'teatab, kui keegi {{what}}',
    add: 'Lisa ala',
    remove: 'Eemalda {{name}}',
    removeShort: 'Eemalda',
    invalid: 'Kontrolli nime ja raadiust.',
    failed: 'Alasid ei õnnestunud salvestada. Proovi uuesti.',
  },
  people: {
    menu: 'Kasutajad',
    title: 'Kasutajad',
    lead: 'Kõik, kellel on selles TripPathis konto. Välja lülitatud konto ei saa sisse logida; selle reisid ja kulud jäävad alles.',
    count: 'Kontod · {{count}}',
    you: 'sina',
    first: 'haldab serverit',
    admin: 'admin',
    off: 'välja lülitatud',
    since: 'alates {{day}}',
    lastSeen: 'viimati {{day}}',
    neverSeen: 'praegu sisse logimata',
    trips_one: '{{count}} reis',
    trips_other: '{{count}} reisi',
    twoStep: 'kaheastmeline kinnitus',
    passkeys_one: '{{count}} pääsuvõti',
    passkeys_other: '{{count}} pääsuvõtit',
    assistants_one: '{{count}} abiline või võti',
    assistants_other: '{{count}} abilist või võtit',
    actions: '{{name}}: tegevused',
    makeAdmin: 'Tee adminiks',
    unmakeAdmin: 'Võta admini õigus',
    turnOff: 'Lülita välja',
    turnOn: 'Lülita sisse',
    signOut: 'Logi kõikjalt välja',
    reset: 'Saada parooli link',
    resetSent: 'Uue parooli link saadeti aadressile {{email}}.',
    resetLink:
      'See server e-kirju ei saada. Anna see link kasutajale {{email}}; see kehtib 30 minutit ja ühe korra:',
    delete: 'Kustuta',
    deleteTitle: 'Kustutada {{name}}?',
    deleteBody: 'Konto ja reisid, kus on ainult tema, kustutatakse. Seda ei saa tagasi võtta.',
    shared:
      'Sel kontol on teistega jagatud reise; nende kulud ja broneeringud on ka teiste omad. Lülita konto hoopis välja.',
    failed: 'See ei õnnestunud. Proovi uuesti.',
    loadFailed: 'Kasutajaid ei õnnestunud laadida.',
    invite: 'Kutsu kedagi',
    inviteHow: 'Ta saab lingi, millega konto teha; see kehtib 7 päeva ja ühe korra.',
    email: 'E-post',
    language: 'Kirja keel',
    send: 'Saada kutse',
    sentMail: 'Kutse saadeti aadressile {{email}}.',
    sentLink: 'See server e-kirju ei saada. Anna see link kasutajale {{email}}:',
    waiting: 'Ootel kutsed · {{count}}',
    until: 'kuni {{day}}',
    takeBack: 'Võta tagasi',
  },
  join: {
    title: 'Loo oma TripPathi konto',
    lead: 'Sind kutsuti aadressiga {{email}}.',
    gone: 'See kutse on kasutatud või aegunud. Küsi uut.',
    create: 'Loo konto',
  },
  assistant: {
    menu: 'AI-abiline',
    title: 'AI-abiline',
    lead: 'Oma AI-abiline (Claude, ChatGPT või muu MCP-d toetav) saab reisi lugeda ja muuta sinu loa piires. TripPathi server ise AI-d ei kutsu ega maksa selle eest.',
    connect: 'Ühendamine',
    connectHow:
      'Lisa see aadress oma AI-abilise ühenduste alla. Sisselogimine ja load küsitakse TripPathis.',
    copy: 'Kopeeri',
    copied: 'Kopeeritud',
    connected: 'Ühendatud abilised · {{count}}',
    none: 'Ühtegi abilist pole veel ühendatud.',
    since: 'ühendatud {{day}}',
    lastUsed: 'viimati {{when}}',
    today: 'täna {{time}}',
    uses_one: '{{count}} tegevus',
    uses_other: '{{count}} tegevust',
    revoke: 'Katkesta',
    revokeKey: 'Kustuta',
    revokeFailed: 'Tagasi võtta ei õnnestunud. Proovi uuesti.',
    failed: 'Abilisi ei õnnestunud laadida. Proovi uuesti.',
    allTrips: 'Kõik reisid',
    onlyTrip: 'Ainult „{{name}}“ · {{days}}',
    tripsOf: 'reisid: {{trips}}',
    allElse: 'kõik muu: ei',
    scope: {
      trips: 'reisid: lugeda',
      plan: 'plaan: muuta',
      bookings: 'broneeringud: lugeda',
      money: 'raha: lugeda ja lisada',
      documents: 'dokumendid: lugeda',
    },
    scopeNo: {
      trips: 'reisid: ei',
      plan: 'plaan: ei',
      bookings: 'broneeringud: ei',
      money: 'raha: ei',
      documents: 'dokumendid: ei',
    },
    keys: 'Isiklikud API-võtmed · {{count}}',
    keysHow: 'Oma skriptidele: saada võti päisena „Authorization: Bearer …“ samale aadressile.',
    noKeys: 'Võtmeid veel pole.',
    keyName: 'Võtme nimi',
    keyNamePlaceholder: 'Eelarve tabel',
    makeKey: 'Loo võti',
    keyMade: 'Sinu uus võti. Seda näidatakse ainult praegu — kopeeri see turvalisse kohta.',
    keyFailed: 'Anna võtmele nimi ja vali vähemalt üks luba.',
    trips: 'Reisid',
    ask: {
      title: '{{name}} soovib ligipääsu sinu TripPathile',
      lead: 'Loa saad igal ajal tagasi võtta: Menüü → AI-abiline.',
      unknown:
        'Seda abilist ei tunta või soovis ta naasta aadressile, mida ta ei öelnud. Alusta ühendamist abilises uuesti.',
      broken:
        'Lingist puudub see, mida abiline peab saatma. Alusta ühendamist abilises uuesti.',
      deny: 'Keeldu',
      allow: 'Luba',
      failed: 'Luba anda ei õnnestunud. Proovi uuesti.',
    },
    give: {
      trips: 'Reiside lugemine',
      tripsMore: 'Peatused, ööd, sõidud, eelarve kokku',
      plan: 'Plaani muutmine',
      planMore: 'Peatuse ööd; iga muudatus on muudatuste logis ja tagasivõetav',
      bookings: 'Broneeringute lugemine',
      bookingsMore: 'Majutused ja piletid, hinnad ja kas makstud',
      money: 'Kulud ja võlad',
      moneyMore: 'Lugemine ja lisamine',
      documents: 'Dokumendid ja passiandmed',
      documentsMore: 'Nimed, liigid ja kehtivus; faile ei saadeta',
    },
    example: {
      title: 'Näide vestlusest abilises',
      ask: 'Lisa Kyotosse 2 ööd ja ütle, mis juhtub eelarvega. Kas Soul jääb siis viisavabaks?',
      read: 'get_trip · Aasia 2027',
      preview: 'preview_change · Kyoto 3 → 5 ööd',
      says: 'Kyoto 5 ööd nihutab Souli kaks päeva edasi: 19.–24. märts. Lend Osaka → Soul on makstud ja tuleb ümber broneerida.',
      shown: 'Eelvaade · ei ole veel salvestatud',
      more: '+230 € oodatav',
      budget: '2672 € / eelarve 3420 €',
      visa: 'Lõuna-Korea 5 päeva / 90 viisavaba',
      unused: 'Makstud pilet KE 2118 jääb kasutamata, kui kuupäeva ei muudeta.',
      yes: 'Tee ära.',
      apply: 'apply_change · Kyoto 5 ööd · ajalukku „Claude (John)“',
      done: 'Tehtud. Kyotos on nüüd 5 ööd; TripPathi muudatuste logis saad selle ühe vajutusega tagasi võtta.',
      twoSteps:
        'Muutused käivad kahes sammus: abiline näitab eelvaadet, salvestab alles su „jah“ peale.',
      history:
        'Iga tegevus on muudatuste logis abilise ja sinu nimega ning tuleb teistele kohe ekraanile.',
      limits: 'Abiline näeb ainult lubatud reise ja osi; luba saab igal ajal ära võtta.',
    },
  },
  security: {
    menu: 'Konto ja turvalisus',
    title: 'Konto ja turvalisus',
    lead: 'Parool, pääsuvõtmed, kaheastmeline kinnitus ja seadmed.',
    signingIn: 'Sisselogimine',
    password: 'Parool',
    changed: 'Muudetud {{date}}',
    changePassword: 'Muuda parooli',
    current: 'Praegune parool',
    newPassword: 'Uus parool',
    newHint: 'Vähemalt 10 märki. Teised seadmed logitakse välja.',
    wrongCurrent: 'See pole konto praegune parool.',
    saved: 'Parool muudetud. Teised seadmed on välja logitud.',
    save: 'Salvesta',
    cancel: 'Loobu',
    passkeys_one: 'Pääsuvõtmed · {{count}}',
    passkeys_other: 'Pääsuvõtmed · {{count}}',
    passkeysHint:
      'Sõrmejälg, nägu, PIN või turvavõti; parooli pole siis vaja ja see läheb ka teiseks sammuks',
    passkeysNeedName:
      'Pääsuvõtmed vajavad nimega aadressi — localhost või oma domeen https-iga —, mitte IP-aadressi.',
    passkeysNoBrowser: 'See brauser pääsuvõtmeid ei tee.',
    addPasskey: 'Lisa pääsuvõti',
    passkey: 'Pääsuvõti',
    passkeyFailed: 'Pääsuvõtit ei lisatud.',
    passkeyName: 'Nimi',
    added: 'Lisatud',
    lastUsed: 'Viimati',
    neverUsed: 'pole kasutatud',
    rename: 'Nimeta ümber',
    remove: 'Eemalda',
    renameTitle: 'Pääsuvõtme nimi',
    removeTitle: 'Eemalda pääsuvõti „{{name}}“',
    removeLead: 'Sellega enam sisse logida ei saa.',
    twoStep: 'Kaheastmeline kinnitus',
    on: 'Sees',
    off: 'Väljas',
    app: 'Autentimisrakendus',
    appHint: 'Google Authenticator, Aegis, 1Password, Bitwarden…',
    appSince: 'lisatud {{date}}',
    turnOn: 'Lülita sisse',
    newPhone: 'Seo uus telefon',
    backup: 'Varukoodid · {{left}} alles {{total}}-st',
    backupHint: 'Kadunud telefoni puhuks. Iga kood kehtib ühe korra.',
    newCodes: 'Loo uued koodid',
    offHint: 'Väljalülitamiseks küsitakse parooli ja koodi.',
    turnOff: 'Lülita välja',
    devices: 'Seadmed, kus oled sisse logitud',
    device: 'Seade',
    lastSeen: 'Viimati',
    how: 'Sisselogimisviis',
    thisDevice: 'see seade',
    now: 'praegu',
    byPassword: 'parool',
    byCode: 'parool + kood',
    byPasskey: 'pääsuvõti',
    unknownDevice: 'Tundmatu brauser',
    signOut: 'Logi välja',
    signOutOthers: 'Logi välja kõigist teistest',
    setupTitle: 'Kaheastmeline kinnitus',
    phoneTitle: 'Uus telefon',
    scan: 'Skaneeri kood rakendusega',
    manual: 'Võti käsitsi sisestamiseks:',
    enterCode: 'Sisesta rakenduse 6 numbrit',
    code: 'Kood',
    wrongCode: 'See kood ei klapi. Võta rakendusest uusim.',
    confirm: 'Kinnita',
    keepCodes: 'Hoia varukoodid alles',
    codesLead: 'Neid näidatakse ainult nüüd.',
    download: 'Laadi alla .txt',
    copy: 'Kopeeri',
    copied: 'Kopeeritud',
    done: 'Valmis',
    codesTitle: 'Uued varukoodid',
    offTitle: 'Lülita kaheastmeline kinnitus välja',
    codeOrBackup: 'Kood rakendusest või varukood',
    wrongPassword: 'See pole konto parool.',
    failed: 'See ei õnnestunud. Proovi uuesti.',
    secondTitle: 'Sisesta kood rakendusest',
    trust: 'Usalda seda seadet 30 päeva',
    or: 'või',
    usePasskey: 'Kasuta pääsuvõtit',
    useBackup: 'Kasuta varukoodi',
    useApp: 'Kasuta rakenduse koodi',
    backupCode: 'Varukood',
    backupTitle: 'Sisesta varukood',
    ticketGone: 'Sisselogimine aegus. Sisesta parool uuesti.',
    passkeyWrong: 'Selle pääsuvõtmega sisse ei saanud.',
    passkeyCancelled: 'Pääsuvõtit ei kasutatud.',
  },
  view: {
    lead: 'Sa näed selle reisi teekonda ja kuupäevi. Raha, broneeringud ja dokumendid jäävad omanikule ja muutjatele.',
    noStops: 'Peatusi veel pole.',
    gone: 'See link on välja lülitatud või pole kunagi olnud.',
    undecided: 'veel otsustamata',
    then: 'edasi: {{how}}',
    joins: '{{name}} liitub {{dates}}',
    traveller: 'Reisija {{n}}',
    leadAmounts:
      'Sa näed selle reisi teekonda, kuupäevi ja summasid. Broneeringud jäävad omanikule ja muutjatele.',
    amounts: 'Oodatav {{expected}}, eelarve {{budget}}',
    files: 'Failid',
    journal: 'Päevik',
    ended: 'See link on lõppenud.',
    locked: 'Liiga palju valesid fraase. Proovi 15 minuti pärast uuesti.',
    phraseTitle: 'See reis küsib salafraasi',
    phraseLead: 'Lingi jagaja ütles fraasi eraldi: suusõnal või teises sõnumis.',
    phrase: 'Salafraas',
    phraseWrong: 'See pole õige fraas.',
    open: 'Ava reis',
  },
  sights: {
    title: 'Tee ääres',
    back: 'Tagasi plaani',
    way: 'Käik: {{from}} → {{to}} · {{how}}',
    by: {
      car: 'autoga',
      bus: 'bussiga',
      train: 'rongiga',
      foot: 'jalgsi',
      flight: 'lennukiga',
      ferry: 'praamiga',
    },
    km: '{{km}} km',
    hours: '{{hours}} h {{minutes}} min',
    minutes: '{{minutes}} min',
    detour: 'Kõrvalekalle',
    upTo10: 'kuni 10 min',
    upTo30: 'kuni 30 min',
    upTo60: 'kuni 1 h',
    kind: 'Liik',
    kinds: {
      view: 'Vaated',
      nature: 'Loodus',
      town: 'Linnad',
      food: 'Söök',
      fuel: 'Kütus ja laadimine',
    },
    source: { wikivoyage: 'Wikivoyage', wikipedia: 'Wikipedia', osm: 'OSM' },
    score: 'Tähtsus {{n}} / 5',
    onWay: 'marsruudil',
    plus: '+{{minutes}} min',
    pick: 'Vali {{name}}',
    map: 'Kaart: tee ja vaatamisväärsused',
    none: 'Tee ääres pole neist midagi.',
    failed: 'Vaatamisväärsusi ei saanud; proovi hiljem.',
    noRoad: 'Vaatamisväärsused leitakse teekonnale autoga kahe kaardil oleva peatuse vahel.',
    howTitle: 'Tähtsus ja allikad',
    howLead: 'Hinnangut ei kopeerita arvustustest. Tähtsus arvutatakse avatud andmetest:',
    how1: 'mitmes keeles on kohal Wikipedia artikkel (Wikidata sitelinks);',
    how2: 'kui palju artiklit loetakse (Wikimedia lehevaatamised, avalik API);',
    how3: 'kas Wikivoyage teab seda;',
    how4: 'OSM silt: tourism=viewpoint, heritage, natural=peak.',
    detourNote: 'Kõrvalekalle: OSRM sõiduaeg teelt kohani ja tagasi.',
    add: 'Lisa valitud käigu peatusteks',
    addFailed: 'Peatusi ei õnnestunud lisada. Proovi uuesti.',
    link: 'Vaatamisväärsused tee ääres',
  },
  journal: {
    title: 'Reisipäevik',
    lead: 'Päevad tulevad plaanist, kohad fotode asukohast, sõnad sinult. Pidevat GPS-i pole: aku jääb alles.',
    notYet: 'Päevik avaneb reisi esimesel päeval, {{day}}.',
    stop: '{{city}} · {{dates}}',
    addPhotos: 'Lisa fotod',
    unwritten: 'Kirjutamata',
    fromPlan: 'Plaanist: {{things}}.',
    photos_one: '{{count}} foto päevast.',
    photos_other: '{{count}} fotot päevast.',
    write: 'Kirjuta',
    edit: 'Muuda',
    titleField: 'Pealkiri',
    bodyField: 'Mis juhtus',
    save: 'Salvesta',
    cancel: 'Loobu',
    here: 'Olen siin',
    hereFailed: 'Asukohta ei saanud.',
    places_one: '{{count}} koht fotodest',
    places_other: '{{count}} kohta fotodest',
    spent: 'kulud {{amount}}',
    way: '{{how}} {{minutes}} min',
    photoAt: 'Foto, {{at}} · {{place}}',
    photo: 'Foto',
    remove: 'Eemalda {{name}}',
    mapTitle: 'Teekond fotodest',
    howTitle: 'Asukoht ilma GPS-jälgimiseta',
    how1: 'Foto enda asukoht ja kellaaeg (EXIF), kui telefon selle salvestas.',
    how2: 'Selle puudumisel päeva plaani koht, mille kellaaeg fotoga sobib.',
    how3: '„Olen siin“ nupp: üks asukoht, kui seda tahad, mitte pidev jälgimine.',
    howNote: 'Fotod vähendatakse 1600 px WebP-ks nagu tšekid; originaalid jäävad telefoni.',
    shareTitle: 'Jagamine ja raamat',
    share: 'Näita päevikut lugemislingil, ilma summadeta',
    noLink: 'Tee kõigepealt lugemislink jagamise lehel.',
    bookLead:
      'PDF-raamat: kaas, kaart, iga päev oma lehel fotode ja tekstiga. Trükikoja jaoks A5 või A4, lõikevaruga.',
    paper: 'Paber',
    bleed: 'Lõikevaruga (3 mm)',
    book: 'Tee raamat',
    failed: 'Päevikut ei õnnestunud salvestada. Proovi uuesti.',
  },
  share: {
    unknown:
      'See link tehti enne, kui linke kopeerimiseks hoiti. Tee uus link, et seda kopeerida.',
    views: 'Vaadatud',
    times_one: '{{count}} kord',
    times_other: '{{count}} korda',
    last: 'Viimati',
    until: 'Kehtib kuni',
    noEnd: 'lõputa',
    choice: {
      expires: 'Lõpeb ise',
      phrase: 'Küsi salafraasi',
      amounts: 'Näita summasid',
      files: 'Näita faile',
      names: 'Näita kaasreisijate nimesid',
      journal: 'Näita päevikut',
    },
    about: {
      expires: 'Nädal pärast reisi lõppu, {{when}} (sinu kodu aeg)',
      phrase: 'Ütle fraas suusõnal või teises kanalis; lingiga koos see ei liigu',
      amounts: 'Välja lülitatuna ei ole lehel ühtki hinda ega võlga',
      files: 'Piletid ja kinnitused; kinnituskoodid on alati peidetud',
      names: 'Välja lülitatuna: „Reisija 2“, „Reisija 3“',
      journal: 'Kirjutatud päevad ja nende fotod, ilma kuludeta',
    },
    phraseLabel: 'Salafraas',
    stop: 'Lõpeta jagamine',
    renew: 'Tee uus link',
    renewNote:
      '„Tee uus link“ annab uue aadressi ja salafraasi; vana link näitab siis „See link on välja lülitatud“.',
    log: 'Vaatamised',
    none: 'Keegi pole linki veel avanud.',
    device: { phone: 'telefon', tablet: 'tahvel', computer: 'arvuti' },
    outcome: { open: 'avatud', ok: 'fraas õige' },
    wrong_one: 'fraas vale 1 kord',
    wrong_other: 'fraas vale {{count}} korda',
    logNote:
      'Linn tuleb IP-aadressist ligikaudselt; IP-aadressi ennast ei hoita. Pärast 5 valet fraasi on link 15 minutit lukus.',
  },
  plan: {
    route: 'Teekond',
    undecidedHint: 'Otsustamata: ava see, et valida koht, või lohista õigesse kohta.',
    openStop: 'Ava {{city}}',
    removeStop: 'Eemalda {{city}}',
    showVariant: 'Näita varianti {{name}}',
    showPlan: 'Näita plaani praegusel kujul',
    addStop: 'Lisa peatus',
    noStops: 'Peatusi veel pole. Lisa esimene.',
    budget: 'Reis ja eelarve',
    reckoned: 'Arvestus',
    wholeTrip: 'Kogu reis',
    perPerson_one: 'Inimese kohta',
    perPerson_other: 'Inimese kohta ({{count}})',
    min: 'Min',
    expected: 'Oodatav',
    max: 'Max',
    paid: 'Makstud',
    toPay: 'Broneeritud, maksmata',
    estimated: 'Hinnanguline',
    budgetIs: 'eelarve {{amount}}',
    over: 'Max ületab eelarvet {{amount}} võrra.',
    share: '{{country}} = {{percent}} kogusummast.',
    countries: 'Riigid',
    country: 'Riik',
    days: 'Päevi',
    perDay: 'Päevas',
    aDay: '{{amount}} päevas',
    total: 'Kokku',
    edit: 'Muuda reisi',
    delete: 'Kustuta reis',
    deleteQuestion: 'Kustutada „{{name}}”?',
    deleteBody: 'Peatused kaovad koos sellega. Seda ei saa tagasi võtta.',
    deleteConfirm: 'Kustuta',
    failed: 'Salvestamine ebaõnnestus. Proovi uuesti.',
    moveUp: 'Liiguta {{city}} üles',
    moveDown: 'Liiguta {{city}} alla',
    openNights_one: '{{count}} öö ilma voodita',
    openNights_other: '{{count}} ööd ilma voodita',
    cashNeeded: 'veel tasuda {{amount}}',
    lost: 'kahjum {{amount}}',
    refundsPending: 'oodatav tagasimakse {{amount}}',
    stats: 'Reis numbrites',
    statDays: 'päeva · {{from}} – {{to}}',
    statAverage: 'keskmiselt päevas',
    statPlaces: 'riiki · linna',
    statBooked: 'broneeritud · {{paid}} makstud',
    visaTitle: 'Viisavabad päevad',
    visaPassport: 'Sinu pass',
    visaNone: 'Ükski selle marsruudi riik ei piira su päevi.',
    visaNote: 'Ainult suunav — kontrolli enne reisi ametlikke allikaid.',
    validTo: 'kehtib kuni {{day}}',
    daysShort_one: '{{count}} p',
    daysShort_other: '{{count}} p',
    perDayShortAmount: '{{amount}}/p',
    stopOf: '{{when}} · {{days}}',
    placeOf: 'Variandis {{name}} · {{when}} · {{days}}',
    way: '{{mode}} {{from}} → {{to}}',
    leaves: 'Väljumine',
    arrives: 'Saabumine',
    times: '{{leaves}} → {{arrives}}',
    wayHere: 'Saabumine',
    hotel: 'Hotell',
    ticket: 'Pilet',
    link: 'Link',
    hotelName: 'Hotelli nimi',
    stayWhole_one: 'Hinnad kogu peatuse eest, {{count}} öö',
    stayWhole_other: 'Hinnad kogu peatuse eest, {{count}} ööd',
    stayPrice: 'Hind kogu peatuse eest',
    found: 'Leitud: {{name}}',
    addHotel: '+ Hotell',
    pasteHotel: 'Kleebi hotelli link…',
    readingLink: 'Loen linki…',
    removeHotel: 'Eemalda {{name}}',
    chooseHotel: 'Peatun siin: {{name}}',
    moreHotels_one: '(+{{count}} veel kaalumisel)',
    moreHotels_other: '(+{{count}} veel kaalumisel)',
    activitiesShow: '+ Tegevused',
    changeBed: 'Muuda öö hinda: {{city}}',
    changeDaily: 'Muuda toitu, kohalikku transporti ja tegevusi: {{city}}',
    changeWay: 'Muuda hinda ja kellaaegu: {{way}}',
    changeDays: 'Muuda päevi: {{city}}',
    fuel: '{{km}} km · {{litres}} l × {{price}}/l',
    charge: '{{km}} km · {{litres}} kWh × {{price}}/kWh',
    fuelEdit: 'Auto…',
    addTicket: 'Lisa pilet',
    ticketBooked: 'pilet broneeritud',
    ticketLink: 'pilet',
    transferShort: 'ümberistumine',
    ends: 'Algus ja sihtkoht',
    endFrom: 'Väljub',
    endTo: 'Saabub',
    endPlace: 'Jaam, aadress',
    howOn: 'Vali transport',
    wayOpen: 'otsustamata',
    changeWayMode: 'Muu sõiduviis',
    variantUsed: 'Variant on plaanis.',
    undo: 'Võta tagasi',
    addRental: 'Lisa autorent',
    showTotals: 'Näita kogusummat',
    showPerDay: 'Näita päeva hinda',
    hotels: 'Kaalutavad hotellid',
    file: 'PDF',
    openFile: 'Ava uues vahelehes',
    change: 'Muuda',
    changeBooking: 'Muuda: {{name}}',
    held: 'Pilet {{from}} → {{city}} on broneeritud: see peatus püsib paigal',
    hours: 'sisse {{checkIn}}, välja {{checkOut}}',
    editDays: 'Päevi kohas {{city}}',
    daysUnit: 'p',
    daysHint: 'Topeltklõps muudab päevi',
    gap_one: 'Planeerimata · {{count}} päev',
    gap_other: 'Planeerimata · {{count}} päeva',
    gapBetween: '{{from}} → {{to}}',
    gapAdd: '+ Lisa peatus',
    gapLonger: 'Pikenda: {{city}}',
    gapLongerShort: 'Pikenda',
    transferHint: 'Ümberistumine teel: oma päevi pole, siit sõidetakse läbi',
    editStop: 'Muuda peatust',
    addStay: 'Lisa majutus',
    stopMenu: 'Peatuse toimingud',
    stopCosts: '{{city}}: broneeringud ja hinnangud',
    date: 'Kuupäev',
    item: 'Kulu',
    price: 'Hind',
    perDayShort: '/ päev',
    status: 'Olek',
    nightsShort_one: '{{count}} öö',
    nightsShort_other: '{{count}} ööd',
    payPaid: 'makstud',
    payLater: 'maksta kohapeal',
    payLaterTicket: 'veel maksmata',
    openRow_one: '{{count}} öö · {{range}}/öö',
    openRow_other: '{{count}} ööd · {{range}}/öö',
    find: 'Otsi',
    open: 'Broneerimata',
    plannedRow_one: '1 tegevus kavas',
    plannedRow_other: '{{count}} tegevust kavas',
    estimate: {
      food: 'Toit päevas',
      local: 'Kohalik transport päevas',
      activities: 'Tegevused',
    },
    perDaySuffix: '/p',
    est: 'hinnang',
    changeVia: '{{via}} → {{to}}',
    inJourney: 'osa teekonnast {{from}} → {{to}}',
    pricedAt: 'sisaldub {{city}} hinnas',
    stopSums: 'Makstud {{paid}} · maksta {{toPay}} · hinnang {{estimate}}',
    perDayLabel: 'päevas',
    stopWidget: 'Peatus või teekond',
    nothingPicked: 'Vali peatus või teekond kaardil, teekonnas või ajajoonel.',
    related: 'Enne ja pärast',
    stopTitle: 'Peatus: {{city}}, {{country}}',
    placeTitle: 'Variandi koht: {{city}}, {{country}}',
    runWas: 'Plaanis: {{days}} · {{amount}}',
    moreWidget: 'Dokumendid, kaasreisijad',
    fromLink: 'lingist',
    openLink: 'Ava ↗',
    added: 'Lisatud: {{cities}}',
    byHand: 'Lisa peatus käsitsi',
  },
  stopStatus: {
    booked: 'Broneeritud',
    expensive: 'Kallis',
    estimate: 'Hinnang',
    unresolved: 'Otsustamata',
  },
  stopForm: {
    spot: 'Täpne koht',
    spotHint: 'Aadress, bussijaam, lennujaam või GPS: 35.55, 139.78',
    spotNote: 'Asukoht linnas: kaart näitab peatust seal ja teekond algab sealt.',
    spotFirst: 'Vali enne linn.',
    spotClear: 'Kasuta linna keskpunkti',
    spots: 'Leitud kohad',
    noSpots: 'Linna lähedalt selle nimega midagi ei leitud.',
    spotFailed: 'OpenStreetMapiga ei saadud ühendust; proovi hiljem uuesti.',
    spotOff: 'Kohtade otsing on selles serveris välja lülitatud.',
    newTitle: 'Uus peatus',
    editTitle: 'Muuda: {{city}}',
    city: 'Linn või koht',
    towns: 'Leitud kohad',
    airport: 'on lennujaam',
    onMap: 'Kaardil valitud kohas.',
    byAirport: 'Kaardil lennujaama järgi. Vali koht, et see täpselt paigutada.',
    offMap: 'Veel kaardil pole: kirjuta nimi ja vali koht.',
    noTowns: 'Sellise nimega kohta pole. Peatuse saab ikka salvestada.',
    osmFailed: 'OpenStreetMapiga ei saanud ühendust; pakutakse ainult lennujaamaga linnu.',
    country: 'Riik',
    days: 'Päevi',
    accommodation: 'Majutus öö kohta',
    accommodationBooked: 'broneeritud · keskmiselt {{price}} öö',
    shownIn: 'Hinnad valuutas',
    food: 'Toit päevas',
    local: 'Kohalik transport päevas',
    activities: 'Tegevused kokku',
    costs: 'Hinnanguline eelarve',
    out: 'Järgmine sõit',
    outNone: 'Määramata',
    estimate: 'Transpordi hind',
    booked: 'Selle tee pilet on broneeritud: viisi muutmiseks eemalda see broneeringutest.',
    estimatedBy: {
      eurostat:
        'Hinnad on hinnang Eurostati hinnatasemete järgi: muuda need, kui tead paremini.',
      icp: 'Hinnad on hinnang Maailmapanga hinnatasemete (ICP 2021) järgi: muuda need, kui tead paremini.',
    },
    daysHeld_one: '{{count}} päev: piletid siia ja edasi on ostetud',
    daysHeld_other: '{{count}} päeva: piletid siia ja edasi on ostetud',
    unresolved: 'Veel otsustamata',
    transfer: 'Ümberistumine: ainult läbisõit, ööd pole',
    add: 'Lisa peatus',
    save: 'Salvesta',
    remove: 'Eemalda peatus',
    removeQuestion: 'Eemaldada {{city}}?',
    removeBody: 'Selle päevad ja kulud kaovad plaanist.',
    removeConfirm: 'Eemalda',
  },
  mode: {
    flight: 'Lend',
    train: 'Rong',
    bus: 'Buss',
    ferry: 'Praam',
    car: 'Auto',
    foot: 'Jalgsi',
  },
  way: {
    widget: 'Teekond: {{from}} → {{to}}',
    took: 'Kestus',
    distance: 'Vahemaa',
    checkIn: 'Check-in avaneb',
    home: 'Kodune aeg: {{at}} ({{zone}})',
    waitHere: '{{city}} · ooteaeg {{wait}}',
    changeAt: 'Ümberistumine: {{city}}',
    notChosen: 'Edasine transport on valimata.',
  },
  journeys: {
    title: 'Teekonnad',
    none: 'Pileteid veel pole. Pilet tuleb siia oma lõikudega: millal ja kuhu, kohad ja väravad ning mida see lubab.',
    ticket: 'Kuupäev',
    route: 'Teekond',
    times: 'Väljumine – saabumine',
    leaves: 'Väljumine',
    arrives: 'Saabumine',
    clock: 'Kellaaeg',
    went: 'Ajad',
    plan: 'Plaan',
    real: 'Tegelik',
    local: 'Kohalik aeg',
    gates: 'Väravad',
    openLeg: 'Ava pileti lõik {{leg}}',
    terminal: 'Terminal {{at}}',
    flight: 'Reisi nr',
    aircraft: 'Sõiduk',
    seats: 'Kohad',
    rules: 'Pagas, check-in',
    numbers: '{{first}} ja {{last}}',
    open: 'Ava pilet {{name}}',
    pnr: 'PNR {{ref}}',
    reference: 'Nr {{ref}}',
    km: '{{km}} km',
    home: 'Kodune aeg',
    homeHint: 'Kodune aeg, {{zone}}',
    gateBy: {
      flight: 'Värav {{gate}}',
      train: 'Perroon {{gate}}',
      bus: 'Platvorm {{gate}}',
      ferry: 'Kai {{gate}}',
      car: 'Värav {{gate}}',
      foot: 'Algus {{gate}}',
      none: 'Värav {{gate}}',
    },
    coachSeat: 'vagun {{coach}}, {{seat}}',
    coach: 'vagun {{coach}}',
    carried: '{{kind}} pardal',
    delay: '{{by}} min plaanist',
    zones: '{{from}} · {{to}} · kodu {{home}}',
    legsOf: 'Sõidud {{numbers}}',
    totals: {
      legs: 'Sõite',
      km: 'Kilomeetreid',
      onTheWay: 'Sõiduaeg kokku',
      late: 'Hilinenud',
      lateBy: 'keskmiselt {{minutes}} min',
      carriers: 'Vedajad',
    },
    boardBy: { bridge: 'Pardale sillalt', stairs: 'Pardale trepist', bus: 'Pardale bussiga' },
    offBy: {
      bridge: 'Väljumine sillalt',
      stairs: 'Väljumine trepist',
      bus: 'Väljumine bussiga',
    },
    terminalFrom: 'Väljub {{at}}',
    terminalTo: 'Saabub {{at}}',
    terminals: '{{from}} → {{to}}',
    cabin: 'Käsipagas {{what}}',
    pieces_one: '{{count}} tk',
    pieces_other: '{{count}} tk',
    kg: '{{kg}} kg',
    personal: '+ isiklik ese',
    checked: 'Pagas {{kg}} kg',
    online: 'Online check-in {{span}}',
    desk: 'Lett {{span}}',
    opens: 'alates {{at}}',
    closes: 'kuni {{at}}',
    hoursBefore_one: '{{count}} h enne',
    hoursBefore_other: '{{count}} h enne',
    minutesBefore_one: '{{count}} min enne',
    minutesBefore_other: '{{count}} min enne',
  },
  bookings: {
    fromMail: 'Lisa meilist',
    journey: 'Sõit',
    estimateCol: 'Hinnang',
    view: { all: 'Kõik', stays: 'Majutus', transport: 'Transport' },
    views: 'Näita',
    title: 'Broneeringud',
    open: 'Broneeringud',
    add: 'Lisa broneering',
    none: 'Broneeringuid veel pole.',
    paid: 'Makstud',
    later: 'Maksta kohapeal',
    estimated: 'Veel hinnanguline',
    day: 'Päev',
    what: 'Broneering',
    stop: 'Peatus',
    nights: 'Öid',
    price: 'Hind',
    payment: 'Makse',
    noDay: '—',
    edit: 'Muuda: {{name}}',
    gaps: 'Majutuseta ööd',
    gap_one: '{{city}}: {{count}} öö',
    gap_other: '{{city}}: {{count}} ööd',
    noGaps: 'Kõik ööd on majutusega.',
    ticket: 'Pilet',
    stay: 'Majutus',
    type: 'Kirjuta broneering või kleebi link',
    typeHint:
      'Näiteks: 25.10 BKK-SYD 160 · Lub d Siam 20.10 5n 100 later · Booking.com-i, Agoda, Airbnb, Google Flightsi või 12Go link',
    read: 'Loe',
    readFailed: 'Seda rida ei õnnestunud lugeda. Proovi uuesti.',
    drop: 'Lohista kinnituse PDF või foto siia',
    choose: 'Vali fail',
    dropHint:
      'Hotelli, lennu, rongi või bussi kinnitused. Midagi ei lisata enne, kui oled üle vaadanud.',
    pdfFailed: 'Seda faili ei õnnestunud lugeda: see pole PDF ega foto.',
    noOcr: 'Fotosid ja skaneeringuid see server ei loe: OCR-i mudelid pole paigaldatud.',
    reading: 'Loen faili…',
    lead: 'Kleebi link, TripPath loeb hinna ja kuupäevad',
    bookedSoFar: 'Broneeritud seni',
    pasteTitle: 'Kleebi link või rida',
    openLink: 'Ava leht: {{name}}',
    typePlaceholder: 'https://www.booking.com/… või 25.10 BKK-SYD 160',
    count_one: 'Broneeringud · {{count}}',
    count_other: 'Broneeringud · {{count}}',
    groupBy: 'Rühmita',
    by: { date: 'Kuupäev', city: 'Linn', country: 'Riik' },
    perDay: 'Päevas',
    groupBooked: '{{amount}} broneeritud',
    bookStay: 'Broneeri majutus: {{city}}',
    nightsCount_one: '{{count}} öö',
    nightsCount_other: '{{count}} ööd',
    gapNights_one: '{{count}} öö broneerimata',
    gapNights_other: '{{count}} ööd broneerimata',
    wayOpen: 'broneerimata',
    waysOpen_one: '{{count}} sõit broneerimata',
    waysOpen_other: '{{count}} sõitu broneerimata',
    fromBookings: 'sinu broneeringutest',
    tickets_one: '{{count}} pilet',
    tickets_other: '{{count}} piletit',
    soFar: 'Seni · {{from}} – {{to}}',
    booked: 'Broneeritud',
    perDayDays_one: 'Päevas, {{count}} päev',
    perDayDays_other: 'Päevas, {{count}} päeva',
  },
  timeline: {
    sunriseOnWay: 'Päikesetõus lennukist {{time}} · {{where}}',
    sunsetOnWay: 'Päikeseloojang lennukist {{time}} · {{where}}',
    play: {
      group: 'Esita reis',
      play: 'Esita',
      pause: 'Peata',
      speed: 'Kiirus',
      x60: '1 min/s',
      x600: '10 min/s',
      x3600: '1 h/s',
      moment: 'Kaardil näidatav kellaaeg',
    },
    title: 'Ajajoon',
    zoom: 'Suum',
    zoomIn: 'Suurenda',
    zoomOut: 'Vähenda',
    zooms: { month: 'Kuu', week: 'Nädal', day: 'Päev', hour: 'Tund', minute: 'Minut' },
    fit: 'Kogu reis',
    fitPicked: 'Valitud vahemik',
    clock: 'Aeg',
    local: 'Kohalik',
    gaps_one: '{{count}} tühimik',
    gaps_other: '{{count}} tühimikku',
    gapShort: '{{count}} p',
    nextGap: 'Järgmise tühimiku juurde',
    clockAt: 'Aeg: {{city}} kell',
    inAir: 'sõidus',
    night: 'öö',
    block: '{{city}}, {{from}}–{{to}}, {{days}}',
    keys: 'Lohista mööda teed: järjekord või kuupäev. Lohista otsast: päevad. ← → päev, Shift ← → kuupäev, Alt ← → järjekord.',
    moved: '{{city}} teisaldatud',
    shifted: '{{city}} alates {{date}}',
    heldByTicket: '{{from}} → {{to}}: pilet on ostetud, aeg ei muutu.',
    today: 'täna',
  },
  travellers: {
    title: 'Kaasreisijad',
    add: 'Lisa reisikaaslane',
    none: 'Lisa kontota kaasreisija nimi, et valida ta piletil reisijaks.',
    onTickets_one: '{{count}} piletil',
    onTickets_other: '{{count}} piletil',
    noTicket: 'pole veel ühelgi piletil',

    edit: 'Muuda: {{name}}',
    editShort: 'Muuda',
    show: '{{name}} – tema teekonnad',
    together: '{{name}} on sinuga',
    planner: 'Puhkuse plaan · {{name}}',
    stay: 'Liitub kohas {{city}} {{dates}}',
    stayElsewhere: 'Sinuga {{dates}}',
    leaveDays_one: '{{count}} puhkusepäev',
    leaveDays_other: '{{count}} puhkusepäeva',
    holidays_one:
      'Peatuse sisse jääb {{count}} riigipüha, nii et {{days}} kalendripäeva maksavad {{leave}} puhkusepäeva.',
    holidays_other:
      'Peatuse sisse jääb {{count}} riigipüha, nii et {{days}} kalendripäeva maksavad {{leave}} puhkusepäeva.',
    noHolidays:
      '{{days}} kalendripäeva ilma nädalavahetusteta maksavad {{leave}} puhkusepäeva.',
    own: 'Tema enda teekond',
  },
  holiday: {
    new_year: 'Uusaasta',
    independence: 'Iseseisvuspäev',
    good_friday: 'Suur reede',
    easter: 'Ülestõusmispühade 1. püha',
    spring: 'Kevadpüha',
    pentecost: 'Nelipühade 1. püha',
    victory: 'Võidupüha',
    midsummer: 'Jaanipäev',
    restoration: 'Taasiseseisvumispäev',
    christmas_eve: 'Jõululaupäev',
    christmas: 'Esimene jõulupüha',
    boxing_day: 'Teine jõulupüha',
  },
  travellerForm: {
    newTitle: 'Uus reisikaaslane',
    editTitle: 'Muuda: {{name}}',
    name: 'Nimi',
    add: 'Lisa reisikaaslane',
    save: 'Salvesta',
    remove: 'Eemalda reisikaaslane',
    removeQuestion: 'Eemaldada {{name}}?',
    removeBody: 'Sinu plaan ei muutu.',
    removeConfirm: 'Eemalda',
  },
  routes: {
    order: 'Teede järjestus',
    byPrice: 'Hinna järgi',
    byCo2: 'CO₂ järgi',
    co2: '{{kg}} kg CO₂e',
    title: 'Teede võrdlus',
    open: 'Võrdle teid',
    to: 'kuni',
    readTitle: 'Loetud',
    pathsFound: 'Teid leitud',
    total: 'Kokku',
    ifPickedTitle: 'Valiku mõju',
    howTitle: 'Hindamise põhimõtted',
    saved: 'Salvestatud võrdlused',
    noLegs: 'Võrdlemiseks on vaja reisil kaht peatust.',
    leg: 'Teekond {{number}}/{{count}} · lahkumine {{day}}',
    planned: 'plaanis hinnanguliselt {{amount}}',
    legLabel: 'Teekond',
    chosenShort: 'valitud {{path}}',
    options_one: '{{count}} lend leitud',
    options_other: '{{count}} lendu leitud',
    empty: 'tühi',
    found: 'Leitud võimalused',
    textLabel: 'Lennud ja vahepeatuse hotellid',
    hint: 'Üks rea kohta: KUST-KUHU hind, näiteks PEN-KUL 25, või Google Flightsi link (hind loetakse lingist või kirjuta see järele). Lisa „paid“, kui pilet on ostetud; „MNL hotel 35“ vahepeatuse ööks, „x2“ kaheks ööks.',
    openLink: 'Ava broneerimise leht ↗',
    fromLink: 'lingist',
    changes: 'Ümberistumised',
    changesUpTo_zero: 'Ainult otse',
    changesUpTo_one: 'Kuni {{count}} ümberistumine',
    changesUpTo_other: 'Kuni {{count}} ümberistumist',
    noneRead: 'Kleebitud tekstist ei loetud ühtki lendu.',
    noneFrom: 'Ükski neist lendudest ei välju kohast {{from}}.',
    noneTo: 'Ükski neist lendudest ei jõua kohta {{to}}.',
    intoStart:
      'Üks lendab kohta {{from}}: see võib kuuluda eelmisele lõigule, mis viib kohta {{from}}.',
    noJoin:
      'Need lennud ei ühendu kohast {{from}} kohta {{to}}. Luba rohkem ümberistumisi või lisa vahepealne lend. Loetud: {{flights}}.',
    read: 'Loe',
    flights: 'Lennud',
    hotels: 'Vahepeatuse hotellid',
    paid: 'Makstud, lukus',
    nonePaid: 'pole',
    notRead: 'Lugemata read',
    unread: 'Rida {{line}}: „{{text}}“ pole lend ega hotell.',
    unknownCode: 'Rida {{line}}: „{{text}}“ lennujaama koodi pole loendis.',
    how: 'Iga lendude ahel siit kohta {{to}} saab hinna: lennud, vahepeatuse hotellid ja kasutamata jäävad makstud piletid. Öö vahepeatuses on öö vähem kohas {{to}}.',
    paths_one: '{{count}} tee',
    paths_other: '{{count}} teed',
    showSkipping: 'Näita teid, mis jätavad makstud pileti kasutamata',
    start: 'Kleebi leitud lennud ja vajuta Loe.',
    cheapest: 'odavaim',
    fewest: 'kõige vähem lende',
    skipsPaid: 'makstud pilet jääb kasutamata',
    breakdown_one: '{{count}} lend {{flights}}',
    breakdown_other: '{{count}} lendu {{flights}}',
    stopover_one: '{{city}} hotell {{cost}}, {{count}} öö',
    stopover_other: '{{city}} hotell {{cost}}, {{count}} ööd',
    lost: '{{amount}} makstud ja kasutamata',
    ifPicked: 'Kui valid {{path}}',
    thisLeg: 'See teekond',
    nightsAt: 'Ööd kohas {{city}}',
    nightsOf: '{{left}}/{{of}}',
    tripExpected: 'Reis kokku oodatavalt',
    versusPlan: 'Võrreldes plaaniga',
    inPlan: 'See tee on plaanis.',
    use: 'Vali see tee',
    used: 'Lisatud plaani.',
  },
  map: {
    title: 'Kaart',
    legend: 'Teed kaardil',
    kindKm: '{{kind}} · {{km}} km',
    label: 'Kaart: {{places}}',
    empty: 'Kaart: kohti veel pole',
    missing: 'Kaardil pole: {{cities}}. Muuda peatust ja vali selle koht.',
    look: 'Kaardi stiil',
    lookTitle: 'Kaardi stiil',
    style: { standard: 'Tavaline', light: 'Hele', topo: 'Topo' },
    relief: 'Reljeefi varjutus',
    terrain: '3D maastik',
    rails: 'Metrooliinid',
    heightsOf:
      '{{name}} kõrguse järgi: maa lähedal hele, mida kõrgemal, seda tumedam, kuni {{top}} km',
    heightsLook: 'Kõrgus',
    heightsLookHint:
      'Iga teekond värvitakse kõrguse järgi: lend faili lennukõrguse järgi, maismaa tee maapinna kõrguse järgi.',
    sky: {
      night: 'Päev ja öö',
      nightHint: 'Öö ja hämarus ajajoone kellaajal, päike ja kuu seal, kus nad on seniidis.',
      vehicle: 'Liikuv sõiduk',
      vehicleHint:
        'Ajajoone kellaajal teel olev lennuk, rong, buss, laev, auto või jalgsi käik liigub kaardil oma teel.',
      trail: 'Läbitud tee',
      trailHint: 'Teel oleva sõiduku seni läbitud osa, joonistatud tema taha.',
    },
    locate: 'Minu asukoht',
    north: 'Põhi üles',
    here: 'Sinu asukoht, ±{{metres}} m',
    hereHow:
      'Küsiti üks kord, kui vajutasid nuppu. Asukohta ei salvestata ega saadeta kellelegi; lehe sulgemisel on see kadunud.',
    notHere: 'Sinu asukohta ei leitud.',
    kind: {
      flight: 'Lend',
      change: 'Lend ümberistumisega',
      draft: 'Mustand, lisamata',
      variant: 'Näidatud variant',
      train: 'Rong',
      bus: 'Buss',
      ferry: 'Praam',
      car: 'Auto',
      foot: 'Jalgsi',
      travel: 'Planeerimata sõit',
      other: 'Muu tee',
      walk: 'Jalgsi',
      metro: 'Metroo',
      tram: 'Tramm',
      taxi: 'Takso',
      bike: 'Ratas',
      cable: 'Köisraudtee',
    },
  },
  search: {
    label: 'Otsi kohti või marsruuti',
    town: 'Lennujaamata linn',
    clear: 'Tühjenda otsing',
    didYouMean: 'Kas mõtlesid',
    places: 'Kohad',
    airports_one: '{{city}}: {{count}} lennujaam',
    airports_other: '{{city}}: {{count}} lennujaama',
    from: 'Kohast {{city}} tõenäoliselt otse',
    hintNone: 'Üht kohta ei leitud. Kontrolli kirjapilti või kasuta lennujaama koodi.',
    km: '{{km}} km',
    notEuro: 'Hinnangud on eurodes, see reis aga {{currency}}; lisamine ootab vahetuskursse.',
    toPlan: 'Tagasi plaani juurde',
    where: 'Otsi sihtkohta: HEL-BKK, linn või link',
    draft: 'Marsruudi mustand',
    fromLink: 'Lingis olevad lennud',
    fromLinkFare: 'hind lingist',
    inPlan: 'plaanis',
    firstStop: 'reisi algus',
    withFerry: 'sh {{km}} km autopraamiga',
    notAdded: 'algus, ei lisata',
    way: 'Teekond {{from}} → {{to}}',
    nightsAt: 'Ööd kohas {{city}}',
    fewer: 'Üks öö vähem kohas {{city}}',
    more: 'Üks öö rohkem kohas {{city}}',
    nights_one: '{{count}} öö',
    nights_other: '{{count}} ööd',
    travel: 'Sõit, hinnanguliselt',
    typicalNote:
      'Iga koht saab riigi tavahinnad majutusele, toidule ja liikumisele; muuda neid peatuses.',
    addToPlan: 'Lisa plaani',
  },
  stays: {
    none: 'Majutust pole veel broneeritud.',
    nights_one: '{{count}} öö',
    nights_other: '{{count}} ööd',
    change: 'Muuda',
    changeOne: 'Muuda: {{name}}',
    remove: 'Eemalda',
    removeOne: 'Eemalda: {{name}}',
    add: '+ Lisa majutus',
    removeQuestion: 'Eemaldada {{name}}?',
    removeBody: 'Broneering eemaldatakse reisilt; selle ööd tuleb uuesti broneerida.',
    open: 'Majutus: {{city}}',
  },
  local: {
    calendar: 'Kalender',
    system: {
      buddhist: 'Budistlik',
      japanese: 'Jaapani ajastud',
      roc: 'Minguo',
      persian: 'Pärsia (päikese-Hidžra)',
      ethiopic: 'Etioopia',
      indian: 'India rahvuskalender (saka)',
      hebrew: 'Heebrea',
      'islamic-umalqura': 'Islami (Hidžra)',
      chinese: 'Kuukalender',
      dangi: 'Korea kuukalender',
      gregory: 'Gregoriuse',
    },
    religious: 'pühade jaoks',
    year: 'Aasta',
    yearIs: '{{local}} = {{ours}}',
    date: 'Kohalik kuupäev',
    weekend: 'Nädalavahetus',
    day: 'Kohalik kalender',
    lunarDay: '{{month}}. kuu {{day}}. päev',
    lunarMonth: '{{month}}. kuu',
    animalYear: '{{animal}} aasta',
    animal: {
      '0': 'Roti',
      '1': 'Härja',
      '2': 'Tiigri',
      '3': 'Jänese',
      '4': 'Draakoni',
      '5': 'Mao',
      '6': 'Hobuse',
      '7': 'Kitse',
      '8': 'Ahvi',
      '9': 'Kuke',
      '10': 'Koera',
      '11': 'Sea',
    },
    newMoon: 'Noorkuu',
    fullMoon: 'Täiskuu',
    view: 'Kalender',
  },
  clocks: {
    title: 'Kellad',
    home: 'Kodune tööaeg · {{city}}',
    from: 'Algab',
    to: 'Lõpeb',
    at: 'Kell kohas {{city}}',
    place: 'Koht',
    dates: 'Kuupäevad',
    clock: 'Kell',
    workThere: 'Tööaeg',
    everywhere: 'Tööaeg igas kohas',
    none: 'Ühelgi reisi kohal pole veel ajavööndit.',
    open: 'Kellad',
    date: 'Alates',
    timeAt: 'Kell: {{place}}',
    working: 'Arvesta tööaega',
    order: 'Tööpäev peab algama enne, kui see lõpeb.',
    timeWrong: 'Kellaaeg, näiteks 9:00 või 17:30',
    zones: '{{country}} ajavööndid',
    part: {
      day: 'Töö päeval',
      evening: 'Töö vara või õhtul',
      late: 'Töö hilisõhtul',
      night: 'Töö öösel',
    },
    key: {
      night: 'Öö',
      dawn: 'Varahommik',
      morning: 'Hommik',
      midday: 'Keskpäev',
      lunch: 'Lõuna',
      afternoon: 'Pärastlõuna',
      evening: 'Õhtu',
      late: 'Hilisõhtu',
      work: 'kodune tööaeg',
    },
  },
  leave: {
    title: 'Tööaeg ja puhkus: {{name}}',
    week: 'Töönädal',
    weekHint: 'Vajuta päevale, et see tööpäevaks märkida või sellest vabastada.',
    perYear: 'Puhkusepäevi aastas',
    used: 'Selle reisi peale kulub',
    usedOf: '{{used}} / {{perYear}} päeva',
    marked: 'Märgitud päevad',
    none: 'Ühtki päeva pole veel märgitud.',
    save: 'Salvesta',
    people: 'Inimesed',
    open: '{{name}} — tööaeg ja puhkus',
    mark: '{{name}}: {{kind}}',
    workday: 'Tööpäev',
    dayOff: 'Vaba päev',
    kind: { leave: 'Puhkus', sick: 'Haigus', work: 'Tööpäev' },
  },
  city: {
    checkInLabel: 'Sisseregistreerimine',
    checkOutLabel: 'Väljaregistreerimine',
    fromTime: 'alates {{time}}',
    untilTime: 'kuni {{time}}',
    perNight: '{{price}} öö',
    bookingRef: 'Broneering {{ref}}',
    openBooking: 'Ava broneering ↗',
    things: 'Tegevused',
    openDays: 'Ava {{city}} päevad →',
    towards: 'suunas {{place}}',
    backTitle: 'Tagasi: {{room}}',
    open: 'Päevakava',
    back: 'Kogu reis',
    title: '{{city}} · {{dates}}',
    days: '{{city}} päevad',
    dayPick: 'Vali päev',
    planned_one: '{{count}} tegevus',
    planned_other: '{{count}} tegevust',
    plan: 'Päevakava',
    picked: 'Üksikasjad',
    titleOf: {
      thing: 'Tegevus: {{name}}',
      stay: 'Ööbimine: {{name}}',
      arrive: 'Saabumine: {{name}}',
      leave: 'Lahkumine: {{name}}',
    },
    onFrom: 'Edasi: {{name}}',
    add: 'Lisa tegevus',
    nightOf: 'öö {{night}}/{{nights}}',
    checkIn: 'check-in',
    checkOut: 'check-out',
    nights_one: '{{count}} öö',
    nights_other: '{{count}} ööd',
    lands: 'saabub {{time}}',
    late: 'ei jõua',
    lateLong: 'Ei jõua: peaks lahkuma enne, kui eelmine tegevus lõpeb.',
    leaves: 'väljub {{time}}',
    terminal: 'terminal {{terminal}}',
    summary_one: '{{count}} tegevus · jalgsi {{walk}} · {{money}}',
    summary_other: '{{count}} tegevust · jalgsi {{walk}} · {{money}}',
    costs: 'Päeva ühised kulud',
    costPaid: 'maksis {{name}}',
    costsAll: 'Kõik ühised kulud',
    poiAdd: 'Lisa selle päeva tegevuseks',
    poiPick: 'Lisa see koht…',
    search: 'Otsi kohta, aadressi või GPS',
    poiHere: 'See koht',
    poiNone: 'Siin lähedal pole nimega kohti.',
    there: 'Siia',
    fromPlace: 'lähtekoht: {{name}}',
    photo: 'Foto',
    facts: {
      day: 'Päev',
      time: 'Aeg',
      price: 'Hind',
      booking: 'Olek',
      booked: 'Broneeritud',
      notBooked: 'Broneerimata',
      place: 'Asukoht',
    },
    photoCredit: 'Foto: Wikipedia · {{title}}',
    next: 'Edasi',
    booked: 'broneeritud',
    change: 'Muuda',
    by: {
      walk: 'jalgsi',
      bike: 'jalgratas',
      scooter: 'tõukeratas',
      metro: 'metroo',
      tram: 'tramm',
      train: 'rong',
      cable: 'köisraudtee',
      bus: 'buss',
      minibus: 'marsruuttakso (marshrutka, colectivo)',
      jeepney: 'džiipni',
      songthaew: 'songthaew',
      tuktuk: 'tuk-tuk',
      motortaxi: 'mototakso',
      taxi: 'takso',
      ridehail: 'sõiduteenus (Grab, Uber)',
      car: 'auto',
      boat: 'paat',
      ferry: 'praam',
      cyclo: 'velorikša',
    },
    missing: 'Seda peatust reisil pole.',
    thing: {
      newTitle: 'Tegevus: {{city}}',
      editTitle: 'Muuda: {{name}}',
      name: 'Nimi',
      nameHint: 'nt Sensō-ji',
      day: 'Kuupäev',
      starts: 'Algus',
      ends: 'Lõpp',
      place: 'Asukoht',
      price: 'Hind',
      booked: 'Pilet ostetud või laud broneeritud',
      files: 'Pildid ja failid',
      addFile: '+ Pilt või PDF',
      removeFile: 'Eemalda {{name}}',
      fileOnSave: 'salvestatakse koos tegevusega',
      link: 'Link',
      by: 'Transport',
      byMinutes: 'Sõiduaeg',
      add: 'Lisa',
      save: 'Salvesta',
      remove: 'Eemalda',
      removeQuestion: 'Eemaldada „{{name}}“?',
      removeBody: 'Tegevus eemaldatakse päeva plaanist.',
      removeConfirm: 'Eemalda',
      required: 'Sisesta nimi',
      wrongTime: 'Kirjuta kellaaeg, nt 09:30',
      wrongMinutes: 'Kirjuta täisminutid, kuni 1440',
      partEnd: 'Sisesta iga osa sihtkoht (v.a viimane)',
      part: 'Osa {{n}}',
      partTo: 'Sihtkoht',
      lastTo: 'Sihtkoht: {{place}}',
      addChange: '+ Ümberistumine',
      removePart: 'Eemalda osa {{n}}',
      here: 'see tegevus',
    },
  },
  file: {
    page: '{{title}}, lehekülg {{number}}',
    unreadable: 'Seda PDF-i ei saanud siin avada: ava see uues vahelehes.',
    tools: 'PDF-i leheküljed ja suurendus',
    pageNumber: 'Lehekülg, kokku {{count}}',
    smaller: 'Vähenda',
    larger: 'Suurenda',
    pick: 'Kasuta „{{text}}”',
  },
  ticketCode: {
    title: 'QR-kood · {{name}}',
    code: 'QR-kood: {{code}}',
    hint: 'Keera ekraan heledaks ja hoia telefoni lugeja ees paigal.',
    show: 'Näita broneeringu {{name}} QR-koodi',
    showShort: 'QR-kood',
    open: 'Näita',
    field: 'QR-kood',
  },
  spotMap: {
    open: 'Vali kaardilt',
    openFor: '{{label}}: vali kaardilt',
    title: 'Vali koht kaardilt',
    search: 'Otsi kohta või GPS',
    searchHint: 'Jaam, aadress või 35.55, 139.78',
    found: 'Leitud kohad',
    none: 'Selle nimega siit lähedalt ei leitud midagi.',
    name: 'Koha nimi',
    nameHint: 'Klõpsa enne kaardil',
    detail: 'valitud kaardilt',
    take: 'Salvesta asukoht',
  },
  calendar: {
    editWay: 'Muuda teekonda',
    today: 'Täna',
    editTicket: 'Muuda piletit',
    editTransport: 'Muuda transporti',
    arrives: 'Saabub {{time}} · {{place}}',
    title: 'Kalender',
    open: 'Kalender',
    previous: 'Eelmine kuu',
    next: 'Järgmine kuu',
    tripDay: 'Reisi {{day}}. päev {{days}}-st',
    night: '{{city}}, {{country}} · öö {{night}}/{{nights}}',
    lastDay: 'Reisi viimane päev',
    leaving: '{{how}}: {{city}}',
    leavingLast: '{{how}} edasi',
    travel: 'Sõit',
    booked: 'Sel päeval broneeritud',
    expected: 'oodatav {{amount}} päevas',
    summary_one: '{{count}} sõit · hinnanguliselt {{amount}} sel kuul',
    summary_other: '{{count}} sõitu · hinnanguliselt {{amount}} sel kuul',
    legend: 'Legend',
    keyTogether: 'Koos reisides',
    keyGap: 'Majutuseta öö',
    keyUnresolved: 'Otsustamata peatus',
    keyUnplanned: 'Planeerimata',
    keyEclipse: 'Varjutus',
    eclipse: {
      sun: {
        total: 'Täielik päikesevarjutus',
        annular: 'Rõngakujuline päikesevarjutus',
        partial: 'Osaline päikesevarjutus',
      },
      moon: {
        total: 'Täielik kuuvarjutus',
        partial: 'Osaline kuuvarjutus',
        penumbral: 'Poolvarjuline kuuvarjutus',
      },
    },
    eclipseLook: {
      total: 'täielik',
      annular: 'rõngakujuline',
      partial: 'osaline',
      penumbral: 'poolvarjuline',
    },
    eclipseSeen: '{{city}}: {{look}}, {{from}}–{{until}}, suurim {{peak}}',
    eclipseSunCovered: '{{share}} päikesest kaetud',
    eclipseMoonCovered: '{{share}} kuust varjus',
    eclipsePartSun: 'päike on osa ajast silmapiiri all',
    eclipsePartMoon: 'kuu on osa ajast silmapiiri all',
    eclipseNone: '{{city}}: siit ei paista',
    moonUp: '{{shape}} {{rise}}–{{set}}',
    moonAllDay: '{{shape}}',
    moonStays: '{{shape}} {{rise}}–',
    sunrise: 'Päikesetõus',
    sunset: 'Päikeseloojang',
    solarNoon: 'Keskpäev: päike kõige kõrgemal',
    daylight: 'Päeva pikkus: päikesetõusust loojanguni',
    sunUp: 'Kesköine päike: päike ei looju',
    sunDown: 'Polaaröö: päike ei tõuse',
    moonShape: {
      new: 'Noorkuu',
      waxingCrescent: 'Kasvav sirp',
      firstQuarter: 'Esimene veerand',
      waxingGibbous: 'Kasvav kuu',
      full: 'Täiskuu',
      waningGibbous: 'Kahanev kuu',
      lastQuarter: 'Viimane veerand',
      waningCrescent: 'Kahanev sirp',
    },
    keyHoliday: 'Riigipüha',
    keyOff: 'Puhkepäev',
    offSaid: 'Puhkepäev: nädalavahetus või püha',
    publicHoliday: 'riigipüha',
    observance: 'tähtpäev',
    holidayIn: '{{name}} · {{kind}} ({{country}})',
    unplanned: 'planeerimata',
    estimate: 'broneerimata · hinnanguliselt {{range}}',
    expectedThatDay: 'oodatav sel päeval',
    whole: 'Kogu reis',
    show: 'Näita: {{month}}',
    export: 'Ekspordi',
    menu: 'Kalendri menüü',
    afterTrip: 'Pärast selle reisi lõppu',
    alsoShown: 'Teised reisid',
    kinTitle: 'Teised reisid sel päeval',
    kinDay: '{{name}}: {{city}}',
    kinOpen: 'Ava reisi „{{name}}“ kalender, {{day}}',
    kinOpenShort: 'Ava kalender',
    ics: 'Kalendrifail',
    icsHint: '.ics · Google, Apple, Outlook',
    subscribe: 'Tellimislink',
    subscribeHint: 'Kopeeritakse, kalender värskendab ise',
    copied: 'Link kopeeritud',
    pdf: 'PDF',
    pdfHint: 'Kõik kuud, printimiseks või salvestamiseks',
  },
  family: {
    meets: 'liitub reisiga {{name}} · {{city}}',
    title: 'Reisipuu',
    widget: 'Reisipuu',
    alone:
      'Harusid pole. Kui reisijad lähevad lahku, võib iga osa olla eraldi reis: oma nimi, link, eelarve ja inimesed.',
    newTitle: 'Reisi {{name}} haru',
    newHint:
      'Eraldi reis, mis sellest hargneb: algab päeval, mil peatus lõpeb, oma eelarve ja inimestega.',
    name: 'Nimi',
    partsAt: 'Hargneb peatusest',
    budget: 'Eelarve',
    people: 'Kaasreisijad',
    make: 'Loo haru',
    peopleCount_one: '{{count}} inimene',
    peopleCount_other: '{{count}} inimest',
    partsFrom: 'hargneb: {{city}}',
    meetsAgain: 'Liitub uuesti',
    meetsNone: 'Ei liitu teise reisiga',
    shared: 'Teiste harude inimesed näevad seda reisi',
    new: '+ Haru',
    here: 'Loo haru siit',
    rail: 'Puu teised reisid',
    stopTitle: 'Peatus: {{city}}, {{country}} · {{trip}}',
    lastStop: 'Reisi viimane peatus',
    open: 'Näita kaardil {{name}}: {{stop}}',
    joinsHere: '{{name}} liitub {{day}}',
    partsHere_one: 'siit {{count}} reis',
    partsHere_other: 'siit {{count}} reisi',
    cancelledWay: {
      flight: 'lend tühistatud',
      train: 'rong tühistatud',
      bus: 'buss tühistatud',
      ferry: 'praam tühistatud',
      car: 'auto tühistatud',
      foot: 'matk tühistatud',
      ticket: 'pilet tühistatud',
      stay: 'ööbimine tühistatud',
    },
    peopleShort: '{{count}} in.',
    joined: '+{{count}} · {{city}}',
    cancelled_one: '{{count}} broneering tühistatud',
    cancelled_other: '{{count}} broneeringut tühistatud',
    budgetIs: 'eelarve {{amount}}',
    joins: 'liitub reisiga',
    at: '{{city}} {{day}}',
    on: 'lend {{way}}',
    meetGap_one: '{{count}} päev planeerimata enne kokkusaamist: {{city}}',
    meetGap_other: '{{count}} päeva planeerimata enne kokkusaamist: {{city}}',
    meetLate: 'Lõpeb alles siis, kui teised on juba lahkunud: {{city}}',
  },
  incident: {
    open: 'Teata muudatusest: {{name}}',
    title: 'Muudatus või tühistamine: {{name}}',
    titleStay: 'Tühistatud: {{name}}',
    what: 'Muudatuse liik',
    moved: 'Aeg muutus',
    late: 'Hilineb',
    cancelled: 'Tühistatud',
    movedHint: 'Sama pilet uue ajaga: järgmised peatused nihkuvad kaasa.',
    lateHint: 'Plaanitud aeg jääb alles, tegelik aeg on selle kõrval.',
    planned: 'Plaanitud {{when}}',
    leavesDay: 'Väljub',
    leaves: 'Kell',
    arrives: 'Saabub kell',
    timeWrong: 'Kellaaeg, näiteks 9:00 või 17:30; väljumise aeg on vajalik',
    outcome: 'Lahendus',
    replaced: 'Asendus',
    replacedHint:
      'Uus pilet selle asemel, pakutud või ise ostetud: lisa see teekonnale. Vana jääb kriipsutatult alles.',
    pending: 'Tagasimakse ootel',
    pendingHint: 'Tagasimakse jääb ootele, kuni see on käes; teekond on jälle planeerimata.',
    received: 'Tagasimakse laekunud',
    receivedHint: 'Tagasimakse on käes.',
    voucher: 'Vautšer',
    voucherHint: 'Raha jääb müüja juurde krediidiks: summa ja kehtivus.',
    lost: 'Raha ei tagastata',
    lostHint: 'Midagi ei tagastata: hind jääb eelarvesse kahjumina.',
    amount: 'Tagasimakse summa',
    worth: 'Väärtus',
    until: 'Kehtib kuni',
    clear: 'Eemalda muudatus',
    save: 'Salvesta',
    chipMoved: 'aeg muutus',
    chipLate: 'hilineb · {{times}}',
    chipCancelled: 'tühistatud',
    chipReplaced: 'tühistatud · asendatud',
    chipPending: 'tühistatud · {{amount}} tuleb tagasi',
    chipReceived: 'tühistatud · raha käes',
    chipVoucher: 'tühistatud · vautšer {{amount}}',
    chipVoucherUntil: 'tühistatud · vautšer {{amount}} kuni {{until}}',
    chipLost: 'tühistatud · kahjum {{amount}}',
    replacement: 'asendab tühistatut',
  },
  lodging: {
    hotel: 'Hotell',
    hostel: 'Hostel',
    apartment: 'Korter',
    guesthouse: 'Külalistemaja',
    bnb: 'B&B',
    resort: 'Kuurort',
    villa: 'Villa',
    house: 'Puhkemaja',
    ryokan: 'Ryokan',
    capsule: 'Kapselhotell',
    camping: 'Kämping',
    homestay: 'Kodumajutus',
    boat: 'Paat',
  },
  bookingForm: {
    freeUntil: 'Tasuta tühistamine kuni',
    freeAt: 'Kellaaeg',
    lodging: 'Majutuse liik',
    lodgingAny: 'Ööbimiskoht',
    after: 'pärast {{time}}',
    newTitle: 'Uus broneering',
    checkTitle: 'Kontrolli broneeringut',
    ticketTitle: 'Pilet: {{name}}',
    rentalTitle: 'Autorent: {{name}}',
    flight: 'Lennu number',
    towards: 'Sihtkoht',
    pnr: 'PNR',
    place: 'Asukoht',
    attach: '+ PDF või foto',
    detach: 'Eemalda: {{name}}',
    source: 'Loetud fail: {{name}}',
    pressInto: 'Vajuta PDF-is tekstile, see läheb väljale',
    chooseField: 'Klõpsa kõigepealt vormi väljal, siis PDF-is tekstil',
    wentInto: '„{{text}}” pandi väljale',
    noDayIn: '„{{text}}” ei ütle kuupäeva väljale {{field}}: vajuta mõnele muule tekstile',
    editTitle: 'Muuda: {{name}}',
    stop: 'Peatus',
    kind: 'Tüüp',
    stay: 'Majutus',
    ticket: 'Pilet',
    checkIn: 'Saabumine',
    checkOut: 'Lahkumine',
    from: 'Check-in',
    until: 'Check-out',
    nightsCount_one: '{{count}} öö',
    nightsCount_other: '{{count}} ööd',
    departs: 'Väljumise päev',
    name: 'Nimi',
    nights: 'Öid',
    price: 'Hind',
    pay: 'Olek',
    paid: 'Makstud',
    later: 'Maksta kohapeal',
    laterTicket: 'Maksmata',
    reference: 'Broneeringu number',
    add: 'Lisa broneering',
    save: 'Salvesta',
    remove: 'Eemalda broneering',
    removeQuestion: 'Eemaldada {{name}}?',
    removeBody: 'Selle hind muutub taas hinnanguks.',
    removeConfirm: 'Eemalda',
  },
  whatIf: {
    title: 'Muudatuste ettepanekud',
    shift_one: '{{to}} +{{count}} päev, {{from}} −{{count}}',
    shift_other: '{{to}} +{{count}} päeva, {{from}} −{{count}}',
    sameEnd: 'Sama lõpukuupäev',
    skip_one: 'Jäta {{from}} vahele, +{{count}} päev: {{to}}',
    skip_other: 'Jäta {{from}} vahele, +{{count}} päeva: {{to}}',
    oneFewer: 'Üks peatus vähem, sama lõpukuupäev',
    none: 'Soovitused tulevad, kui kahel peatusel on hinnad, mida võrrelda.',
    less: '−{{amount}}',
    more: '+{{amount}}',
    same: 'Muutust pole',
    apply: 'Rakenda',
    own: 'Proovi ise',
    stop: 'Peatus',
    days: 'Päevi peatuses',
    skip: 'Jäta see peatus vahele',
    expected: 'Oodatav {{amount}}',
  },
};

export const resources = { en: { translation: en }, et: { translation: et } };

/** A ready, synchronous i18next instance in `language`. */
export function createI18n(language: Language = defaultLanguage): i18n {
  const instance = i18next.createInstance();
  void instance.use(initReactI18next).init({
    resources,
    lng: language,
    fallbackLng: defaultLanguage,
    interpolation: { escapeValue: false },
    initAsync: false,
  });
  return instance;
}
